/* eslint-disable */
import { useMutation, useQuery } from '@tanstack/react-query';
import type { QueryKey, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

import { courseFetcher } from '../Fetcher';
import type { RequestOptions } from '../Fetcher';
const fetcher = courseFetcher;

export type Maybe<T> = T | null;

export type OneOf<T> = T extends unknown[]
  ? T[number]
  : T extends readonly unknown[]
    ? T[number]
    : T;

export type Exact<T extends { [key: string]: unknown }> = {
  [K in keyof T]-?: Exclude<T[K], undefined>;
};

export type Values<T extends Record<string, unknown>> = T extends Record<string, infer U>
  ? U
  : T extends { [key: string]: infer U }
    ? U
    : never;

export const enum ResponseStatus {
  OK = 'OK',
  SERVER_ERROR = 'SERVER_ERROR',
  DUPLICATE = 'DUPLICATE',
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  BAD_PARAMETER = 'BAD_PARAMETER',
  CONFLICT = 'CONFLICT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

export type Success<T = unknown> = {
  statusCode: ResponseStatus.OK;
  message: '';
  data: T;
  errorCode: string;
};

export type Error = {
  statusCode: Omit<keyof typeof ResponseStatus, 'OK'>;
  message: string;
  data: Maybe<''>;
  errorCode: string;
};

/**
 * @typeParam T - Response data type
 */
export type ApiResponse<T = unknown> = Success<T>;

export const API_ENDPOINT = {
  CLIENT_V1_VOUCHERS_ENROLLMENT_VERIFICATIONS_CONFIRM:
    '/client/api/v1/vouchers/enrollment-verifications/confirm',
  CLIENT_V1_VOUCHER_ARCHIVE: '/client/api/v1/voucher/archive',
  CLIENT_V1_MY_LISTS: '/client/api/v1/my-lists',
  CLIENT_V1_COURSE_CERTIFICATES_CONFIRM: '/client/api/v1/course-certificates/confirm',
  CLIENT_V1_ROULETTE_DRAW: '/client/api/v1/roulette/draw',
  CLIENT_V1_MY_LISTS_SEQUENCE: '/client/api/v1/my-lists/sequence',
  CLIENT_V1_MY_LISTS_COURSE_SEQUENCE: '/client/api/v1/my-lists/course/sequence',
  CLIENT_V1_MY_LISTS_COURSE: '/client/api/v1/my-lists/course',
  CLIENT_V1_MARKETING_PARTNERS: '/client/api/v1/marketing-partners',
  CLIENT_V1_MARKETING_PARTNER_LINKS: '/client/api/v1/marketing-partner-links',
  CLIENT_V1_COURSE_COURSEID_LIKE: (courseId: string) => `/client/api/v1/course/${courseId}/like`,
  CLIENT_V1_CART: '/client/api/v1/cart',
  CLIENT_V2_VOUCHERS_MY: '/client/api/v2/vouchers/my',
  CLIENT_V2_VOUCHERS_ENROLLMENT_VERIFICATIONS: '/client/api/v2/vouchers/enrollment-verifications',
  CLIENT_V2_MY_LISTS_ID: (id: string) => `/client/api/v2/my-lists/${id}`,
  CLIENT_V1_VOUCHERS_MY_UPCOMING_EVENTS: '/client/api/v1/vouchers/my/upcoming-events',
  CLIENT_V1_VOUCHER_MY_CHALLENGES: '/client/api/v1/voucher/my/challenges',
  CLIENT_V1_USER_STATISTICS: '/client/api/v1/user-statistics',
  CLIENT_V1_ROULETTE_RESULTS: '/client/api/v1/roulette/results',
  CLIENT_V1_ROADMAP_AUTHOR_USERID: (userId: string) => `/client/api/v1/roadmap/author/${userId}`,
  CLIENT_V1_ORDER_SUMMARIES: '/client/api/v1/order/summaries',
  CLIENT_V1_MY_LISTS_ID: (id: string) => `/client/api/v1/my-lists/${id}`,
  CLIENT_V1_MARKETING_PARTNERS_COURSES_SEARCH: '/client/api/v1/marketing-partners/courses/search',
  CLIENT_V1_MARKETING_PARTNER_LINKS_CREATE_PREVIEW:
    '/client/api/v1/marketing-partner-links/create/preview',
  CLIENT_V1_COURSES_ID_SIMPLE: (id: string) => `/client/api/v1/courses/${id}/simple`,
  CLIENT_V1_COURSE_USER_USERID: (userId: string) => `/client/api/v1/course/user/${userId}`,
  CLIENT_V1_COURSE_LIKES: '/client/api/v1/course/likes',
  CLIENT_V1_COURSE_GIFTS_SENT: '/client/api/v1/course-gifts/sent',
  CLIENT_V1_COURSE_GIFTS_RECEIVED: '/client/api/v1/course-gifts/received',
  CLIENT_V1_COURSE_CERTIFICATES: '/client/api/v1/course-certificates',
  CLIENT_V1_COURSE_CERTIFICATES_UNCONFIRMED: '/client/api/v1/course-certificates/unconfirmed',
  CLIENT_V1_COURSE_CERTIFICATES_DETAIL: '/client/api/v1/course-certificates/detail',
  CLIENT_V1_COURSE_CERTIFICATES_CHALLENGE_DETAIL:
    '/client/api/v1/course-certificates/challenge/detail',
  CLIENT_V1_CATEGORY_MAIN: '/client/api/v1/category/main',
  CLIENT_V1_VOUCHERS_ID_FREE: (id: string) => `/client/api/v1/vouchers/${id}/free`,
};

export function useClientApiV1VouchersEnrollmentVerificationsConfirmUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateResponse,
    unknown,
    ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateResponse,
    unknown,
    ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateVariables =
  ConfirmEnrollmentVerificationRequest;

export type ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateResponse = EmptyOkResponse;

export function useClientApiV1VoucherArchiveUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1VoucherArchiveUpdateResponse,
    unknown,
    ClientApiV1VoucherArchiveUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1VoucherArchiveUpdateResponse,
    unknown,
    ClientApiV1VoucherArchiveUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1VoucherArchiveUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1VoucherArchiveUpdateVariables = ArchiveVoucherV2Request;

export type ClientApiV1VoucherArchiveUpdateResponse = EmptyOkResponse;

export const fetchClientApiV1MyLists = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1MyListsResponse>(url, requestOptions);

export interface ClientApiV1MyListsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1MyListsResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MyListsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MyListsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MyLists(url, requestOptions),
  ...options,
});

export function useClientApiV1MyListsQuery(params: ClientApiV1MyListsQueryParams) {
  const result = useQuery<ClientApiV1MyListsResponse, unknown>(
    getClientApiV1MyListsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsResponse = ApiResponseListFindMyListResponse;

export function useClientApiV1MyListsUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsUpdateResponse,
    unknown,
    ClientApiV1MyListsUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsUpdateResponse,
    unknown,
    ClientApiV1MyListsUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsUpdateVariables = UpdateMyListRequest;

export type ClientApiV1MyListsUpdateResponse = EmptyOkResponse;

export function useClientApiV1MyListsCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsCreateResponse,
    unknown,
    ClientApiV1MyListsCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsCreateResponse,
    unknown,
    ClientApiV1MyListsCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsCreateVariables = CreateMyListRequest;

export type ClientApiV1MyListsCreateResponse = EmptyOkResponse;

export function useClientApiV1CourseCertificatesConfirmUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CourseCertificatesConfirmUpdateResponse,
    unknown,
    ClientApiV1CourseCertificatesConfirmUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CourseCertificatesConfirmUpdateResponse,
    unknown,
    ClientApiV1CourseCertificatesConfirmUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1CourseCertificatesConfirmUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CourseCertificatesConfirmUpdateVariables = ConfirmCourseCertificateRequestV2;

export type ClientApiV1CourseCertificatesConfirmUpdateResponse =
  ApiResponseConfirmCourseCertificateResponse;

export function useClientApiV1RouletteDrawCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1RouletteDrawCreateResponse,
    unknown,
    ClientApiV1RouletteDrawCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1RouletteDrawCreateResponse,
    unknown,
    ClientApiV1RouletteDrawCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1RouletteDrawCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1RouletteDrawCreateResponse = ApiResponseRouletteDrawResponse;

export type ClientApiV1RouletteDrawCreateVariables = unknown;

export function useClientApiV1MyListsSequenceCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsSequenceCreateResponse,
    unknown,
    ClientApiV1MyListsSequenceCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsSequenceCreateResponse,
    unknown,
    ClientApiV1MyListsSequenceCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsSequenceCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsSequenceCreateVariables = UpdateMyListsSequenceRequest;

export type ClientApiV1MyListsSequenceCreateResponse = EmptyOkResponse;

export function useClientApiV1MyListsCourseSequenceCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsCourseSequenceCreateResponse,
    unknown,
    ClientApiV1MyListsCourseSequenceCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsCourseSequenceCreateResponse,
    unknown,
    ClientApiV1MyListsCourseSequenceCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsCourseSequenceCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsCourseSequenceCreateVariables = UpdateMyListCourseSequenceRequest;

export type ClientApiV1MyListsCourseSequenceCreateResponse = EmptyOkResponse;

export const fetchClientApiV1MyListsCourse = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1MyListsCourseResponse>(url, requestOptions);

export interface ClientApiV1MyListsCourseQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MyListsCourseResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MyListsCourseQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MyListsCourseQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MyListsCourse(url, requestOptions),
  ...options,
});

export function useClientApiV1MyListsCourseQuery(params: ClientApiV1MyListsCourseQueryParams) {
  const result = useQuery<ClientApiV1MyListsCourseResponse, unknown>(
    getClientApiV1MyListsCourseQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 강의 기반 강의폴더 조회 */
export type ClientApiV1MyListsCourseQueryString = Partial<{
  courseId: number;
}>;

export type ClientApiV1MyListsCourseResponse = ApiResponseFindMyListByCourseResponse;

export function useClientApiV1MyListsCourseCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsCourseCreateResponse,
    unknown,
    ClientApiV1MyListsCourseCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsCourseCreateResponse,
    unknown,
    ClientApiV1MyListsCourseCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsCourseCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsCourseCreateVariables = AddCourseRequest;

export type ClientApiV1MyListsCourseCreateResponse = EmptyOkResponse;

export function useClientApiV1MyListsCourseDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsCourseDeleteResponse,
    unknown,
    ClientApiV1MyListsCourseDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsCourseDeleteResponse,
    unknown,
    ClientApiV1MyListsCourseDeleteVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsCourseDeleteVariables) =>
      fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsCourseDeleteVariables = RemoveCourseRequest;

export type ClientApiV1MyListsCourseDeleteResponse = EmptyOkResponse;

export function useClientApiV1MarketingPartnersCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MarketingPartnersCreateResponse,
    unknown,
    ClientApiV1MarketingPartnersCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MarketingPartnersCreateResponse,
    unknown,
    ClientApiV1MarketingPartnersCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MarketingPartnersCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MarketingPartnersCreateVariables = SignupMarketingPartnerRequest;

export type ClientApiV1MarketingPartnersCreateResponse = ApiResponseSignupMarketingPartnerResponse;

export function useClientApiV1MarketingPartnerLinksCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MarketingPartnerLinksCreateResponse,
    unknown,
    ClientApiV1MarketingPartnerLinksCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MarketingPartnerLinksCreateResponse,
    unknown,
    ClientApiV1MarketingPartnerLinksCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MarketingPartnerLinksCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MarketingPartnerLinksCreateVariables = CreateMarketingPartnerLinkRequest;

export type ClientApiV1MarketingPartnerLinksCreateResponse =
  ApiResponseCreateMarketingPartnerLinkResponse;

export function useClientApiV1CourseCourseIdLikeCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CourseCourseIdLikeCreateResponse,
    unknown,
    ClientApiV1CourseCourseIdLikeCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CourseCourseIdLikeCreateResponse,
    unknown,
    ClientApiV1CourseCourseIdLikeCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1CourseCourseIdLikeCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CourseCourseIdLikeCreateResponse = EmptyOkResponse;

export type ClientApiV1CourseCourseIdLikeCreateVariables = unknown;

export function useClientApiV1CourseCourseIdLikeDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CourseCourseIdLikeDeleteResponse,
    unknown,
    ClientApiV1CourseCourseIdLikeDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CourseCourseIdLikeDeleteResponse,
    unknown,
    ClientApiV1CourseCourseIdLikeDeleteVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1CourseCourseIdLikeDeleteVariables) =>
      fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CourseCourseIdLikeDeleteResponse = EmptyOkResponse;

export type ClientApiV1CourseCourseIdLikeDeleteVariables = unknown;

export const fetchClientApiV1Cart = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1CartResponse>(url, requestOptions);

export interface ClientApiV1CartQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1CartResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CartQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CartQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1Cart(url, requestOptions),
  ...options,
});

export function useClientApiV1CartQuery(params: ClientApiV1CartQueryParams) {
  const result = useQuery<ClientApiV1CartResponse, unknown>(getClientApiV1CartQueryOptions(params));

  return { ...result, data: result.data?.data };
}

/** 클라이언트 장바구니 아이템을 조회한다. */
export type ClientApiV1CartQueryString = Partial<{
  lang: string;
}>;

export type ClientApiV1CartResponse = ApiResponseFindClientCartResponse;

export function useClientApiV1CartCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CartCreateResponse,
    unknown,
    ClientApiV1CartCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CartCreateResponse,
    unknown,
    ClientApiV1CartCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1CartCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CartCreateVariables = AddCartsRequest;

export type ClientApiV1CartCreateResponse = ApiResponseAddCartsResponse;

export function useClientApiV1CartDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CartDeleteResponse,
    unknown,
    ClientApiV1CartDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CartDeleteResponse,
    unknown,
    ClientApiV1CartDeleteVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1CartDeleteVariables) =>
      fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CartDeleteVariables = RemoveCartsRequest;

export type ClientApiV1CartDeleteResponse = EmptyOkResponse;

export const fetchClientApiV2VouchersMy = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV2VouchersMyResponse>(url, requestOptions);

export interface ClientApiV2VouchersMyQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV2VouchersMyResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV2VouchersMyQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2VouchersMyQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2VouchersMy(url, requestOptions),
  ...options,
});

export function useClientApiV2VouchersMyQuery(params: ClientApiV2VouchersMyQueryParams) {
  const result = useQuery<ClientApiV2VouchersMyResponse, unknown>(
    getClientApiV2VouchersMyQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 오프셋기반 페이지네이션 보유한 강의 조회 API */
export type ClientApiV2VouchersMyQueryString = Partial<{
  userId: number;
  searchKeyword: string;
  courseType: CourseType;
  studying: MyVoucherStudyingType;
  isReviewable: boolean;
  isExcludeExpired: boolean;
  isArchived: boolean;
  sort: MyVouchersSortType;
  pageNumber: number;
  pageSize: number;
  lang: string;
}>;

export type ClientApiV2VouchersMyResponse = ApiResponseOffsetPageResponseMyVoucherDto;

export const fetchClientApiV2VouchersEnrollmentVerifications = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV2VouchersEnrollmentVerificationsResponse>(url, requestOptions);

export interface ClientApiV2VouchersEnrollmentVerificationsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV2VouchersEnrollmentVerificationsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV2VouchersEnrollmentVerificationsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2VouchersEnrollmentVerificationsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2VouchersEnrollmentVerifications(url, requestOptions),
  ...options,
});

export function useClientApiV2VouchersEnrollmentVerificationsQuery(
  params: ClientApiV2VouchersEnrollmentVerificationsQueryParams,
) {
  const result = useQuery<ClientApiV2VouchersEnrollmentVerificationsResponse, unknown>(
    getClientApiV2VouchersEnrollmentVerificationsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 수강확인증 목록 조회 API */
export type ClientApiV2VouchersEnrollmentVerificationsQueryString = Partial<{
  requester: Requester;
  userId: number;
  lang: string;
}>;

export type ClientApiV2VouchersEnrollmentVerificationsResponse =
  ApiResponseListGetEnrollmentVerificationResponse;

export const fetchClientApiV2MyListsId = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV2MyListsIdResponse>(url, requestOptions);

export interface ClientApiV2MyListsIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV2MyListsIdResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV2MyListsIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2MyListsIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2MyListsId(url, requestOptions),
  ...options,
});

export function useClientApiV2MyListsIdQuery(params: ClientApiV2MyListsIdQueryParams) {
  const result = useQuery<ClientApiV2MyListsIdResponse, unknown>(
    getClientApiV2MyListsIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 강의폴더에 속한 강의 리스트 조회 */
export type ClientApiV2MyListsIdQueryString = Partial<{
  lang: string;
}>;

export type ClientApiV2MyListsIdResponse = ApiResponseFindMyListCoursesResponse;

export const fetchClientApiV1VouchersMyUpcomingEvents = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1VouchersMyUpcomingEventsResponse>(url, requestOptions);

export interface ClientApiV1VouchersMyUpcomingEventsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1VouchersMyUpcomingEventsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1VouchersMyUpcomingEventsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1VouchersMyUpcomingEventsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1VouchersMyUpcomingEvents(url, requestOptions),
  ...options,
});

export function useClientApiV1VouchersMyUpcomingEventsQuery(
  params: ClientApiV1VouchersMyUpcomingEventsQueryParams,
) {
  const result = useQuery<ClientApiV1VouchersMyUpcomingEventsResponse, unknown>(
    getClientApiV1VouchersMyUpcomingEventsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 내학습 > 도래할 이벤트 조회 API */
export type ClientApiV1VouchersMyUpcomingEventsQueryString = Partial<{
  courseType: CourseType;
  courseId: number;
  lang: string;
}>;

export type ClientApiV1VouchersMyUpcomingEventsResponse = ApiResponseFindMyUpcomingEventsResponse;

export const fetchClientApiV1VoucherMyChallenges = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1VoucherMyChallengesResponse>(url, requestOptions);

export interface ClientApiV1VoucherMyChallengesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1VoucherMyChallengesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1VoucherMyChallengesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1VoucherMyChallengesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1VoucherMyChallenges(url, requestOptions),
  ...options,
});

export function useClientApiV1VoucherMyChallengesQuery(
  params: ClientApiV1VoucherMyChallengesQueryParams,
) {
  const result = useQuery<ClientApiV1VoucherMyChallengesResponse, unknown>(
    getClientApiV1VoucherMyChallengesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 오프셋기반 페이지네이션 보유한 챌린지 강의 조회 API */
export type ClientApiV1VoucherMyChallengesQueryString = Partial<{
  userId: number;
  searchKeyword: string;
  studying: MyVoucherChallengeStudyingType;
  isReviewable: boolean;
  isArchived: boolean;
  pageSize: number;
  pageNumber: number;
  sort: MyChallengeVouchersOffsetSortType;
  lang: string;
}>;

export type ClientApiV1VoucherMyChallengesResponse =
  ApiResponseOffsetPageResponseMyChallengeVoucherDto;

export const fetchClientApiV1UserStatistics = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1UserStatisticsResponse>(url, requestOptions);

export interface ClientApiV1UserStatisticsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1UserStatisticsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1UserStatisticsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1UserStatisticsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1UserStatistics(url, requestOptions),
  ...options,
});

export function useClientApiV1UserStatisticsQuery(params: ClientApiV1UserStatisticsQueryParams) {
  const result = useQuery<ClientApiV1UserStatisticsResponse, unknown>(
    getClientApiV1UserStatisticsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 사용자 통계 정보 */
export type ClientApiV1UserStatisticsQueryString = Partial<{
  withCache: boolean;
}>;

export type ClientApiV1UserStatisticsResponse = ApiResponseUserStatisticsResponse;

export const fetchClientApiV1RouletteResults = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1RouletteResultsResponse>(url, requestOptions);

export interface ClientApiV1RouletteResultsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1RouletteResultsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1RouletteResultsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1RouletteResultsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1RouletteResults(url, requestOptions),
  ...options,
});

export function useClientApiV1RouletteResultsQuery(params: ClientApiV1RouletteResultsQueryParams) {
  const result = useQuery<ClientApiV1RouletteResultsResponse, unknown>(
    getClientApiV1RouletteResultsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1RouletteResultsResponse = ApiResponseRouletteResultsResponse;

export const fetchClientApiV1RoadmapAuthorUserId = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1RoadmapAuthorUserIdResponse>(url, requestOptions);

export interface ClientApiV1RoadmapAuthorUserIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1RoadmapAuthorUserIdResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1RoadmapAuthorUserIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1RoadmapAuthorUserIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1RoadmapAuthorUserId(url, requestOptions),
  ...options,
});

export function useClientApiV1RoadmapAuthorUserIdQuery(
  params: ClientApiV1RoadmapAuthorUserIdQueryParams,
) {
  const result = useQuery<ClientApiV1RoadmapAuthorUserIdResponse, unknown>(
    getClientApiV1RoadmapAuthorUserIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 주어진 유저가 생성한 로드맵을 조회한다 */
export type ClientApiV1RoadmapAuthorUserIdQueryString = Partial<{
  pageNumber: number;
  pageSize: number;
}>;

export type ClientApiV1RoadmapAuthorUserIdResponse = ApiResponseRoadmapsByAuthorResponse;

export const fetchClientApiV1OrderSummaries = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1OrderSummariesResponse>(url, requestOptions);

export interface ClientApiV1OrderSummariesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1OrderSummariesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1OrderSummariesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1OrderSummariesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1OrderSummaries(url, requestOptions),
  ...options,
});

export function useClientApiV1OrderSummariesQuery(params: ClientApiV1OrderSummariesQueryParams) {
  const result = useQuery<ClientApiV1OrderSummariesResponse, unknown>(
    getClientApiV1OrderSummariesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1OrderSummariesResponse = ApiResponseFindOrderSummaryResponse;

export function useClientApiV1MyListsIdDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsIdDeleteResponse,
    unknown,
    ClientApiV1MyListsIdDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsIdDeleteResponse,
    unknown,
    ClientApiV1MyListsIdDeleteVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsIdDeleteVariables) =>
      fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsIdDeleteResponse = EmptyOkResponse;

export type ClientApiV1MyListsIdDeleteVariables = unknown;

export const fetchClientApiV1MarketingPartnersCoursesSearch = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MarketingPartnersCoursesSearchResponse>(url, requestOptions);

export interface ClientApiV1MarketingPartnersCoursesSearchQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MarketingPartnersCoursesSearchResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MarketingPartnersCoursesSearchQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MarketingPartnersCoursesSearchQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MarketingPartnersCoursesSearch(url, requestOptions),
  ...options,
});

export function useClientApiV1MarketingPartnersCoursesSearchQuery(
  params: ClientApiV1MarketingPartnersCoursesSearchQueryParams,
) {
  const result = useQuery<ClientApiV1MarketingPartnersCoursesSearchResponse, unknown>(
    getClientApiV1MarketingPartnersCoursesSearchQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 파트너스 강의 검색 */
export type ClientApiV1MarketingPartnersCoursesSearchQueryString = Partial<{
  userId: number;
  category: string;
  keyword: string;
  sort: SearchPartnerCourseSort;
  pageNumber: number;
  pageSize: number;
  lang: string;
}>;

export type ClientApiV1MarketingPartnersCoursesSearchResponse =
  ApiResponseSearchPartnerCourseResponse;

export const fetchClientApiV1MarketingPartnerLinksCreatePreview = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MarketingPartnerLinksCreatePreviewResponse>(url, requestOptions);

export interface ClientApiV1MarketingPartnerLinksCreatePreviewQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MarketingPartnerLinksCreatePreviewResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MarketingPartnerLinksCreatePreviewQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MarketingPartnerLinksCreatePreviewQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MarketingPartnerLinksCreatePreview(url, requestOptions),
  ...options,
});

export function useClientApiV1MarketingPartnerLinksCreatePreviewQuery(
  params: ClientApiV1MarketingPartnerLinksCreatePreviewQueryParams,
) {
  const result = useQuery<ClientApiV1MarketingPartnerLinksCreatePreviewResponse, unknown>(
    getClientApiV1MarketingPartnerLinksCreatePreviewQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 마케팅 파트너 홍보링크 생성 미리보기를 조회한다 */
export type ClientApiV1MarketingPartnerLinksCreatePreviewQueryString = Partial<{
  courseId: number;
  isAdpickMarketer: boolean;
}>;

export type ClientApiV1MarketingPartnerLinksCreatePreviewResponse =
  ApiResponseGetMarketingPartnerLinkCreatePreviewResponse;

export const fetchClientApiV1CoursesIdSimple = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CoursesIdSimpleResponse>(url, requestOptions);

export interface ClientApiV1CoursesIdSimpleQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CoursesIdSimpleResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CoursesIdSimpleQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CoursesIdSimpleQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CoursesIdSimple(url, requestOptions),
  ...options,
});

export function useClientApiV1CoursesIdSimpleQuery(params: ClientApiV1CoursesIdSimpleQueryParams) {
  const result = useQuery<ClientApiV1CoursesIdSimpleResponse, unknown>(
    getClientApiV1CoursesIdSimpleQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CoursesIdSimpleResponse = ApiResponseCourseSimpleOneResponse;

export const fetchClientApiV1CourseUserUserId = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseUserUserIdResponse>(url, requestOptions);

export interface ClientApiV1CourseUserUserIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseUserUserIdResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseUserUserIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseUserUserIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseUserUserId(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseUserUserIdQuery(
  params: ClientApiV1CourseUserUserIdQueryParams,
) {
  const result = useQuery<ClientApiV1CourseUserUserIdResponse, unknown>(
    getClientApiV1CourseUserUserIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 유저 프로필 강의리스트 조회 */
export type ClientApiV1CourseUserUserIdQueryString = Partial<{
  sort: GetUserProfileCourseSort;
  pageNumber: number;
  pageSize: number;
  lang: string;
}>;

export type ClientApiV1CourseUserUserIdResponse = ApiResponseUserProfileCourseResponse;

export const fetchClientApiV1CourseLikes = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1CourseLikesResponse>(url, requestOptions);

export interface ClientApiV1CourseLikesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1CourseLikesResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseLikesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseLikesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseLikes(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseLikesQuery(params: ClientApiV1CourseLikesQueryParams) {
  const result = useQuery<ClientApiV1CourseLikesResponse, unknown>(
    getClientApiV1CourseLikesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 좋아요한 강의 카드 리스트 조회 */
export type ClientApiV1CourseLikesQueryString = Partial<{
  sort: LikedCourseSort;
  chargeType: LikedCourseChargeType;
  lang: string;
}>;

export type ClientApiV1CourseLikesResponse = ApiResponseCourseByLikesResponse;

export const fetchClientApiV1CourseGiftsSent = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseGiftsSentResponse>(url, requestOptions);

export interface ClientApiV1CourseGiftsSentQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseGiftsSentResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseGiftsSentQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseGiftsSentQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseGiftsSent(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseGiftsSentQuery(params: ClientApiV1CourseGiftsSentQueryParams) {
  const result = useQuery<ClientApiV1CourseGiftsSentResponse, unknown>(
    getClientApiV1CourseGiftsSentQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 보낸 강의 선물 내역 리스트를 조회한다 */
export type ClientApiV1CourseGiftsSentQueryString = Partial<{
  userId: number;
  userAgent: UserAgent;
  pageNumber: number;
  pageSize: number;
}>;

export type ClientApiV1CourseGiftsSentResponse = ApiResponseFindSentCourseGiftResponse;

export const fetchClientApiV1CourseGiftsReceived = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseGiftsReceivedResponse>(url, requestOptions);

export interface ClientApiV1CourseGiftsReceivedQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseGiftsReceivedResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseGiftsReceivedQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseGiftsReceivedQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseGiftsReceived(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseGiftsReceivedQuery(
  params: ClientApiV1CourseGiftsReceivedQueryParams,
) {
  const result = useQuery<ClientApiV1CourseGiftsReceivedResponse, unknown>(
    getClientApiV1CourseGiftsReceivedQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 받은 선물 내역 리스트를 조회한다 */
export type ClientApiV1CourseGiftsReceivedQueryString = Partial<{
  userId: number;
  pageNumber: number;
  pageSize: number;
}>;

export type ClientApiV1CourseGiftsReceivedResponse = ApiResponseFindReceivedCourseGiftResponse;

export const fetchClientApiV1CourseCertificates = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCertificatesResponse>(url, requestOptions);

export interface ClientApiV1CourseCertificatesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCertificatesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseCertificatesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseCertificatesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseCertificates(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseCertificatesQuery(
  params: ClientApiV1CourseCertificatesQueryParams,
) {
  const result = useQuery<ClientApiV1CourseCertificatesResponse, unknown>(
    getClientApiV1CourseCertificatesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 사용자의 수료증 목록을 조회한다 */
export type ClientApiV1CourseCertificatesQueryString = Partial<{
  userId: number;
  isArchived: boolean;
  sort: CourseCertificateSortType;
  lang: string;
}>;

export type ClientApiV1CourseCertificatesResponse = ApiResponseListGetCertificatesResponse;

export const fetchClientApiV1CourseCertificatesUnconfirmed = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCertificatesUnconfirmedResponse>(url, requestOptions);

export interface ClientApiV1CourseCertificatesUnconfirmedQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCertificatesUnconfirmedResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseCertificatesUnconfirmedQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseCertificatesUnconfirmedQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseCertificatesUnconfirmed(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseCertificatesUnconfirmedQuery(
  params: ClientApiV1CourseCertificatesUnconfirmedQueryParams,
) {
  const result = useQuery<ClientApiV1CourseCertificatesUnconfirmedResponse, unknown>(
    getClientApiV1CourseCertificatesUnconfirmedQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CourseCertificatesUnconfirmedResponse = ApiResponseUnconfirmedResponse;

export const fetchClientApiV1CourseCertificatesDetail = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCertificatesDetailResponse>(url, requestOptions);

export interface ClientApiV1CourseCertificatesDetailQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCertificatesDetailResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseCertificatesDetailQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseCertificatesDetailQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseCertificatesDetail(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseCertificatesDetailQuery(
  params: ClientApiV1CourseCertificatesDetailQueryParams,
) {
  const result = useQuery<ClientApiV1CourseCertificatesDetailResponse, unknown>(
    getClientApiV1CourseCertificatesDetailQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 수료증 상세 정보를 조회한다 */
export type ClientApiV1CourseCertificatesDetailQueryString = Partial<{
  userId: number;
  voucherId: number;
  courseId: number;
  lang: string;
}>;

export type ClientApiV1CourseCertificatesDetailResponse = ApiResponseGetCertificateDetailResponse;

export const fetchClientApiV1CourseCertificatesChallengeDetail = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCertificatesChallengeDetailResponse>(url, requestOptions);

export interface ClientApiV1CourseCertificatesChallengeDetailQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCertificatesChallengeDetailResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseCertificatesChallengeDetailQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseCertificatesChallengeDetailQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseCertificatesChallengeDetail(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseCertificatesChallengeDetailQuery(
  params: ClientApiV1CourseCertificatesChallengeDetailQueryParams,
) {
  const result = useQuery<ClientApiV1CourseCertificatesChallengeDetailResponse, unknown>(
    getClientApiV1CourseCertificatesChallengeDetailQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 챌린지 수료증 상세 정보를 조회한다 */
export type ClientApiV1CourseCertificatesChallengeDetailQueryString = Partial<{
  userId: number;
  voucherId: number;
  courseId: number;
  lang: string;
}>;

export type ClientApiV1CourseCertificatesChallengeDetailResponse =
  ApiResponseGetCertificateDetailResponse;

export const fetchClientApiV1CategoryMain = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1CategoryMainResponse>(url, requestOptions);

export interface ClientApiV1CategoryMainQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1CategoryMainResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CategoryMainQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CategoryMainQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CategoryMain(url, requestOptions),
  ...options,
});

export function useClientApiV1CategoryMainQuery(params: ClientApiV1CategoryMainQueryParams) {
  const result = useQuery<ClientApiV1CategoryMainResponse, unknown>(
    getClientApiV1CategoryMainQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 부모 카테고리를 조회한다 */
export type ClientApiV1CategoryMainQueryString = Partial<{
  lang: string;
}>;

export type ClientApiV1CategoryMainResponse = ApiResponseFindParentCategoryResponse;

export function useClientApiV1VouchersIdFreeDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1VouchersIdFreeDeleteResponse,
    unknown,
    ClientApiV1VouchersIdFreeDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1VouchersIdFreeDeleteResponse,
    unknown,
    ClientApiV1VouchersIdFreeDeleteVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1VouchersIdFreeDeleteVariables) =>
      fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1VouchersIdFreeDeleteResponse = EmptyOkResponse;

export type ClientApiV1VouchersIdFreeDeleteVariables = unknown;

export interface PublishRoadmapRequestV2RoadmapItemRequest {
  title: string;
  description: string;
  type: 'COURSE' | 'BOOK' | 'LINK';
  course?: PublishRoadmapRequestV2RoadmapItemRequestCourseRequest;
  book?: PublishRoadmapRequestV2RoadmapItemRequestBookRequest;
  link?: PublishRoadmapRequestV2RoadmapItemRequestLinkRequest;
}

export interface PublishRoadmapRequestV2RoadmapItemRequestBookRequest {
  thumbnailUrl: string;
  url: string;
  authors: string[];
  translators: string[];
  publisher: string;
  isbn: string;
}

export interface PublishRoadmapRequestV2RoadmapItemRequestCourseRequest {
  id: number;
}

export interface PublishRoadmapRequestV2RoadmapItemRequestLinkRequest {
  thumbnailUrl: string;
  url: string;
  iconUrl: string;
}

export interface PublishRoadmapResponse {
  id: number;
}

export interface ModifyRoadmapRequestRoadmapItemRequest {
  title: string;
  description: string;
  type: 'COURSE' | 'BOOK' | 'LINK';
  course?: ModifyRoadmapRequestRoadmapItemRequestCourseRequest;
  book?: ModifyRoadmapRequestRoadmapItemRequestBookRequest;
  link?: ModifyRoadmapRequestRoadmapItemRequestLinkRequest;
}

export interface ModifyRoadmapRequestRoadmapItemRequestBookRequest {
  thumbnailUrl: string;
  url: string;
  authors: string[];
  translators: string[];
  publisher: string;
  isbn: string;
}

export interface ModifyRoadmapRequestRoadmapItemRequestCourseRequest {
  id: number;
}

export interface ModifyRoadmapRequestRoadmapItemRequestLinkRequest {
  thumbnailUrl: string;
  url: string;
  iconUrl?: string | null;
}

export interface SaveDraftRoadmapResponse {
  id: number;
}

export interface MissionAttachmentRequest {
  type: 'LINK' | 'IMAGE' | 'FILE';
  value: string;
}

export interface EmptyOkResponse {
  message: string;
  data?: string | null;
  statusCode: string;
  errorCode?: string | null;
}

export interface ConfirmEnrollmentVerificationRequest {
  voucherId: number;
}

export interface ArchiveVoucherV2Request {
  id: number;
  isArchive: boolean;
}

export interface EntryEvent {
  code: string;
  entryAt: string;
}

export interface BankAccount {
  code:
    | 'NH'
    | 'KB'
    | 'WOORI'
    | 'SHINHAN'
    | 'IBK'
    | 'HANA'
    | 'GYEONGNAM'
    | 'DAEGU'
    | 'BUSAN'
    | 'SUHYUP'
    | 'POST'
    | 'KDB'
    | 'SC'
    | 'CITI'
    | 'DGB'
    | 'GWANGJU'
    | 'JEJU'
    | 'JEONBUK'
    | 'SAE_MAUL'
    | 'SHINHYEOP'
    | 'K_BANK'
    | 'KAKAO_BANK';
  account: string;
  accountHolder: string;
}

export interface UpdateMyListRequest {
  id: number;
  title: string;
}

export interface UpdateCourseInstructorSettingsRequestDto {
  userId: number;
  isQuestionEmailAllowed: boolean;
  type: 'MAIN' | 'SUB';
}

export interface SyncSurveyChoice {
  id?: number | null;
  content: string;
}

export interface SyncSurveyQuestion {
  id?: number | null;
  content: string;
  isRequired: boolean;
  isDefaultQuestion: boolean;
  type: 'SUBJECTIVE' | 'OBJECTIVE' | 'MULTIPLE_OBJECTIVE' | 'FILE';
  choices: SyncSurveyChoice[];
}

export interface UpdateCourseFaqRequestDto {
  id?: number | null;
  body: string;
  title: string;
}

export interface ConfirmCourseCertificateRequestV2 {
  voucherId: number;
}

export interface ConfirmCourseCertificateResponse {
  now: string;
  nowUTC: string;
}

export interface ApiResponseConfirmCourseCertificateResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: ConfirmCourseCertificateResponse;
}

export interface Benefit {
  type: string;
  iconType?: string | null;
  title?: string | null;
}

export interface GetVouchersResponse {
  id: number;
  courseId: number;
  userId: number;
  unitId?: number | null;
  cartId?: number | null;
  orderItemId?: number | null;
  groupId?: number | null;
  type: string;
  progress: string;
  completeCount: number;
  /**
   * 이전에 사용하던 필드로 하위 호환성을 위해 추가(limitedAt을 전달), 블랙팬서 등 사용하지 않게 되면 제거
   */
  limitedDate?: string | null;
  createdAt: string;
  startedAt?: string | null;
  accessedAt?: string | null;
  completedAt?: string | null;
  certificatedAt?: string | null;
  deletedAt?: string | null;
  isArchive: boolean;
  groupCode?: string | null;
  hasDownload: boolean;
  hasVideoDownload: boolean;
  completeRuntime: number;
  memo?: string | null;
  confirmedAt?: string | null;
  certificateConfirmedAt?: string | null;
  limitedAt?: string | null;
  isValid: boolean;
}

export interface ChoiceItem {
  id: number;
  content: string;
}

export interface CommissionRate {
  agreed: number;
  disagreed: number;
}

export interface ExpireSubscriptionVoucherResponse {
  expiredCount: number;
}

export interface UpdateFeaturedChallengeItemRequest {
  courseId: number;
  exposureStartedAtUTC?: string | null;
  exposureEndedAtUTC?: string | null;
}

export interface UpdateRoadmapResponse {
  isNewPublish: boolean;
}

export interface CreateRoadmapResponse {
  id: number;
}

export interface CreateOrderBuyerDto {
  name: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
}

export interface CreateOrderCartDto {
  cartId: number;
  courseId: number;
  title: string;
  duration?: number | null;
  regularPrice: number;
  paymentPrice: number;
  discountId?: number | null;
  discountType?: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
}

export interface DomesticPayMetadataDto {
  payPgType: 'NAVERPAY' | 'TOSS' | 'TOSS_PAY';
  payMethod:
    | 'CARD'
    | 'NAVERPAY'
    | 'NAVERPAY_MONEY'
    | 'KAKAOPAY'
    | 'PAYCO'
    | 'TOSSPAY'
    | 'TOSS_PAY_MONEY'
    | 'APPLEPAY'
    | 'VIRTUAL_ACCOUNT'
    | 'LIFE_EDUCATION'
    | 'OVERSEAS'
    | 'INFPAY'
    | 'BANK_TRANSFER'
    | 'LINKPAY'
    | 'UNKNOWN'
    | 'NONE';
}

export interface CreateOrderResponse {
  id: number;
  merchantUid: string;
  title: string;
  buyer: CreateOrderResponseBuyer;
  returnUrl: string;
}

export interface CreateOrderResponseBuyer {
  email: string;
  phoneNumber: string;
  name: string;
  countryCode: string;
}

export interface CreatePaymentRequestOrderRequestDomesticPayMetadata {
  payPgType: string;
  payMethod: string;
}

export interface CreatePaymentRequestDto {
  cardId: number;
  cardMessage: string;
  payer?: Payer;
}

export interface Payer {
  name?: string | null;
  phone?: string | null;
}

export interface PaymentRequestCartDto {
  cartId: number;
  title: string;
  regularPrice: number;
  paymentPrice: number;
  discountId?: number | null;
  discountType: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
}

export interface CreateGlobalOrderCartDto {
  cartId: number;
  courseId: number;
  title: string;
  regularPrice: number;
  paymentPrice: number;
  discountId?: number | null;
  discountType?: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
}

export interface GlobalPayMetadataDto {
  payPgType: 'PORTONE' | 'TOSS';
  payMethod: 'NONE' | 'PAYPAL';
}

export interface CreateGlobalOrderResponse {
  id: number;
  merchantUid: string;
  title: string;
  buyer: CreateGlobalOrderResponseBuyer;
  paymentPrice: number;
  currency: 'KRW' | 'USD' | 'VND' | 'JPY';
  payMetadata: GlobalPayMetadataDto;
  signatureHash: string;
  token?: string | null;
  returnUrl: string;
}

export interface CreateGlobalOrderResponseBuyer {
  email: string;
  phoneNumber: string;
  name: string;
  countryCode: string;
}

export interface IssueChallengeEventCouponResponse {
  courseIds: number[];
}

export interface StopMessageResponse {
  code: string;
  stopMessage: string;
}

export interface CreateSectionResponse {
  id: number;
}

export interface RouletteDrawResponse {
  resultType: 'PRIZE' | 'POINT';
  prizeName?: unknown;
  point?: unknown;
}

export interface ApiResponseRouletteDrawResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: unknown;
  data: RouletteDrawResponse | null;
}

export interface SaveRoadmapCourseDto {
  id: number;
  description: string;
}

export interface SaveRoadmapHistoryResponse {
  id: number;
}

export interface RestoreRoadmapHistoryResponse {
  id: number;
}

export interface UploadRoadmapContentVideoResponse {
  url: string;
}

export interface UploadRoadmapContentImageResponse {
  url: string;
}

export interface CreateRoadmapContentTemplateResponse {
  id: number;
}

export interface QuizQuestionChoiceAnswerResponse {
  isAnswer: boolean;
  progress: number;
  explanation?: string | null;
}

export interface WebhookData {
  paymentKey: string;
  status:
    | 'READY'
    | 'IN_PROGRESS'
    | 'WAITING_FOR_DEPOSIT'
    | 'DONE'
    | 'CANCELED'
    | 'PARTIAL_CANCELED'
    | 'ABORTED'
    | 'EXPIRED';
}

export interface UploadRoadmapThumbnailResponse {
  id: number;
  url: string;
}

export interface UpdateMyListsSequenceRequest {
  ids: number[];
}

export interface UpdateMyListCourseSequenceRequest {
  listId: number;
  courseIds: number[];
}

export interface AddCourseRequest {
  listId: number;
  courseId: number;
}

export interface CreateMyListRequest {
  title: string;
}

export interface CreateMissionLectureResponse {
  id: number;
}

export interface SignupMarketingPartnerRequest {
  name: string;
  email: string;
  phone: string;
  countryCode: string;
  type: 'BUSINESS' | 'PERSONAL';
  activityLink?: string | null;
}

export interface SignupMarketingPartnerResponse {
  id: number;
  commissionRate: CommissionRate;
}

export interface ApiResponseSignupMarketingPartnerResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: SignupMarketingPartnerResponse;
}

export interface CreateMarketingPartnerLinkRequest {
  courseId: number;
}

export interface CreateMarketingPartnerLinkResponse {
  link: string;
}

export interface ApiResponseCreateMarketingPartnerLinkResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: CreateMarketingPartnerLinkResponse;
}

export interface CreateLiveSessionUnitResponse {
  unitId: number;
}

export interface GetLiveSessionUnitTokenResponse {
  token: string;
  role: 'ADMIN' | 'INSTRUCTOR' | 'STUDENT' | 'STUDENT_VIEWER' | 'RECORDER';
}

export interface ExtendLiveSessionUnitResponse {
  closedAt?: string | null;
  closedAtUTC: string;
}

export interface CreateLectureResponse {
  id: number;
}

export interface BulkCreateLectureRequestLectureItem {
  fileId: number;
  title: string;
}

export interface CheckKoreanImageHttpResponse {
  hangulTexts?: string | null;
  hasHangul: boolean;
}

export interface CreateCourseResponse {
  id: number;
}

export interface UploadCourseSurveyFileResponse {
  name: string;
  url: string;
}

export interface SurveyChoiceRequest {
  id: number;
}

export interface SurveyQuestionRequest {
  id: number;
  answer?: string | null;
  choices: SurveyChoiceRequest[];
}

export interface CourseGift {
  cardId: number;
  cardMessage: string;
  receiveType: 'PHONE' | 'LINK';
  receiver: CreateCourseGiftReceiver;
}

export interface CreateCourseGiftReceiver {
  name: string;
  phone?: string | null;
}

export interface CreateCourseGiftOrderCourseInfo {
  id: number;
  title: string;
  regularPrice: number;
  paymentPrice: number;
  discountId?: number | null;
  discountType: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
}

export interface CreateCourseGiftOrderRequestDomesticPayMetadata {
  payPgType: 'TOSS';
  payMethod:
    | 'CARD'
    | 'NAVERPAY'
    | 'KAKAOPAY'
    | 'TOSSPAY'
    | 'INFPAY'
    | 'BANK_TRANSFER'
    | 'APPLEPAY'
    | 'PAYCO';
}

export interface CreateCourseGiftOrderResponse {
  id: number;
  merchantUid: string;
  title: string;
  buyer: CreateCourseGiftOrderResponseBuyer;
  returnUrl: string;
}

export interface CreateCourseGiftOrderResponseBuyer {
  email: string;
  phoneNumber: string;
  name: string;
  countryCode: string;
}

export interface CourseVideoMetadata {
  isValidSize: boolean;
  isValidType: boolean;
}

export interface CreateVodUploadUrlResponse {
  uploadUrl: string;
  id: number;
  metadata: VodFileMetadata;
}

export interface VodFileMetadata {
  name: string;
  updatedAt: string;
  type: string;
}

export interface CourseCoverMetadata {
  isValidSize: boolean;
  isValidType: boolean;
  isValidKorean: boolean;
  isValidDimension: boolean;
}

export interface UploadCourseCoverThumbnailResponse {
  url: string;
  id: number;
  metadata: UploadCourseCoverThumbnailMetadata;
}

export interface UploadCourseCoverThumbnailMetadata {
  name: string;
  type: string;
  updatedAt: string;
}

export interface UploadCourseCoverImageResponse {
  url: string;
  fileId: number;
}

export interface RetryCourseCoverAiGenerationResponse {
  retryGenerationHistoryId: number;
}

export interface UploadCourseCoverAiGenerationCompositeResponse {
  id: number;
  url: string;
  metadata: UploadCourseCoverAiGenerationCompositeMetadata;
}

export interface UploadCourseCoverAiGenerationCompositeMetadata {
  name: string;
  type: string;
  updatedAt: string;
}

export interface ContractAgreementRequest {
  contractType:
    | 'PLATFORM'
    | 'INFLUENCER'
    | 'EXCLUSIVE'
    | 'NONEXCLUSIVE'
    | 'CHALLENGE'
    | 'OFFLINE'
    | 'E';
  b2bSubscriptionAgreement: boolean;
  inflearnMarketingAgreement: boolean;
}

export interface EarlyBirdSaleRequest {
  earlyBirdSaleTerm: 'AGREE' | 'DISAGREE' | 'DELEGATE';
  salePeriodInWeeks?: number | null;
  saleRate?: number | null;
}

export interface InflearnMarketingConfirmDto {
  courseId: number;
  isAgreed: boolean;
}

export interface UploadCourseContentVideoResponse {
  url: string;
}

export interface UploadCourseContentImageResponse {
  url: string;
}

export interface CreateCourseContentTemplateResponse {
  id: number;
}

export interface CreateCourseContentHistoryResponse {
  id: number;
}

export interface RestoreCourseContentHistoryResponse {
  id: number;
}

export interface CreateManualCourseContentHistoryResponse {
  id: number;
}

export interface GenerateCourseContentResponse {
  generationHistoryId: number;
}

export interface IssueCouponResponse {
  id: number;
  couponId: number;
  title: string;
  targetType: 'ALL' | 'SELECTED' | 'CATEGORY' | 'SKILL' | 'ROADMAP' | 'COMMON_TAG';
  targetIds: number[];
}

export interface CreateInstructorCouponResponse {
  code: string;
}

export interface UpdateCurrentTimeResponse {
  now: number;
  isCompleted: boolean;
  prevProgress: string;
  progress: string;
  isFirstCompleted: boolean;
}

export interface CommonTagResponse {
  id: number;
  title: string;
  subTitle: string;
  slug: string;
  isCuration: boolean;
}

export interface AddCartsRequest {
  courseIds: number[];
  fbclid?: string | null;
  eventId?: string | null;
}

export interface AddCartsResponse {
  items: AddCartDto[];
}

export interface AddCartDto {
  courseId: number;
  cartId: number;
}

export interface ApiResponseAddCartsResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: AddCartsResponse;
}

export interface CreateVoucherItem {
  type:
    | 'LEGACY'
    | 'IS_ADMIN'
    | 'BY_ADMIN'
    | 'IS_INSTRUCTOR'
    | 'BY_INSTRUCTOR'
    | 'FREE'
    | 'ORDER'
    | 'CODE'
    | 'GROUP_ORDER'
    | 'GROUP_SUBSCRIPTION'
    | 'COURSE_GIFT';
  userId: number;
  courseId: number;
  limitedAt?: string | null;
  orderItemId?: number | null;
  cartId?: number | null;
  groupId?: number | null;
  groupCode?: string | null;
}

export interface VerifySignUpDiscountResponse {
  isSignUpDiscountDeleted: boolean;
}

export interface SkillTagInput {
  id: number;
  similarKeywords: string[];
}

export interface GetSkillTagMetricResponse {
  requestId: number;
  skillTagDemands: SkillTagDemands;
  skillTagOpportunities: SkillTagOpportunities;
  levelDistribution: LevelDistribution;
  similarTopCourses: number[];
}

export interface GetSkillTagMetricResponseDemandMonthlyScore {
  yearMonth: string;
  searchCountScore: number;
  detailConversionScore: number;
}

export interface LevelDistribution {
  levels: GetSkillTagMetricResponseLevelInfo[];
}

export interface GetSkillTagMetricResponseLevelInfo {
  id: number;
  title: string;
  percent: number;
}

export interface SkillTagDemand {
  skillTagId: number;
  isTopSearchVolume: boolean;
  months: GetSkillTagMetricResponseDemandMonthlyScore[];
}

export interface SkillTagDemands {
  demands: SkillTagDemand[];
}

export interface SkillTagOpportunities {
  opportunities: SkillTagOpportunity[];
  maxCourseCount: number;
}

export interface SkillTagOpportunity {
  skillTagId: number;
  searchCountScore: number;
  detailConversionScore: number;
  courseCountScore: number;
}

export interface UploadPromotionCardImageResponse {
  id: number;
  url: string;
}

export interface InternalBulkCreateLectureRequestLectureItem {
  fileId: number;
}

export interface CreateSubscriptionVoucherResponse {
  voucherId: number;
  courseId: number;
  createdAt: string;
  limitedAt?: string | null;
}

export interface Enrolment {
  userId: number;
  courseId: number;
  limitedAt?: string | null;
  b2bCoursePrice?: number | null;
  b2bCourseUsdPrice?: number | null;
}

export interface BatchCreateGroupVoucherResponse {
  voucherId: number;
  userId: number;
  courseId: number;
  regularPrice: number;
  orderItemId: number;
  limitedAt?: string | null;
  createdAt: string;
}

export interface MigrateCourseResponse {
  id: number;
}

export interface BulkUpsertCourseCoverAiGenerationPromptsRequestPromptItem {
  id?: number | null;
  prompt: string;
  model: string;
}

export interface UploadCourseCoverAiGenerationReferenceImageResponse {
  items: UploadCourseCoverAiGenerationReferenceImageItem[];
}

export interface UploadCourseCoverAiGenerationReferenceImageItem {
  id: number;
  url: string;
  metadata: UploadCourseCoverAiGenerationReferenceImageMetadata;
}

export interface UploadCourseCoverAiGenerationReferenceImageMetadata {
  name: string;
  type: string;
  updatedAt: string;
}

export interface DetectCourseContentLanguageResponse {
  languageCode: string;
}

export interface ConfirmCourseConfirmationResponse {
  confirmedAt: string;
  confirmedAtUTC: string;
}

export interface UserAgent {
  rawUserAgent: string;
  isMobile: boolean;
  isInflearnApp: boolean;
}

export interface UnitCurrentTime {
  id: number;
  lastCurrentTime: number;
}

export interface BulkUpdateCurrentTimeResponse {
  items: CurrentTimeResponse[];
}

export interface CurrentTimeResponse {
  unitId: number;
  now: number;
  isCompleted: boolean;
  prevProgress: string;
  progress: string;
  isFirstCompleted: boolean;
}

export interface PartnerSkillTagRecommendationResponse {
  items: PartnerSkillTagRecommendationItem[];
}

export interface PartnerSkillTagRecommendationItem {
  id: number;
  title: string;
  slug: string;
}

export interface PartnerCourseRecommendationResponse {
  items: PartnerCourseRecommendationItem[];
}

export interface PartnerCourseRecommendationItem {
  url: string;
  title: string;
  thumbnailUrl: string;
  description: string;
  reviewCount: number;
  reviewAverage: number;
  abilities: string[];
  targets: string[];
  instructorName: string;
  level: string;
  categories: PartnerCourseRecommendationCategory[];
  skills: string[];
}

export interface PartnerCourseRecommendationCategory {
  title: string;
  parentTitle: string;
}

export interface FindParentCategoryResponse {
  categories: ParentCategoryDto[];
}

export interface ParentCategoryDto {
  id: number;
  slug: string;
  title: string;
  originalTitle: string;
  icon: string;
}

export interface ApiResponseFindParentCategoryResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: FindParentCategoryResponse;
}

export interface FindChildCategoryResponse {
  subCategories: ChildCategoryDto[];
}

export interface ChildCategoryDto {
  id: number;
  slug: string;
  title: string;
  originalTitle: string;
}

export interface GetAllLevelsResponse {
  levels: LevelSummaryDto[];
}

export interface LevelSummaryDto {
  title?: string | null;
  slug: string;
  description?: string | null;
  code: string;
}

export interface ApiResponseOffsetPageResponseMyVoucherDto {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: OffsetPageResponseMyVoucherDto;
}

export interface MyVoucherChallengeCourseDto {
  id: number;
  hasSurveyAnswer: boolean;
  startedAt?: string;
  endedAt?: string;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface MyVoucherCourseDto {
  id: number;
  slug: string;
  title: string;
  lectureCount: number;
  thumbnailUrl: string;
  instructorId: number;
  instructorName: string;
  offlineCourse?: MyVoucherOfflineCourseDto;
  challengeCourse?: MyVoucherChallengeCourseDto;
  childCategories: string[];
}

export interface MyVoucherDto {
  id: number;
  userId: number;
  unitId: number;
  progress: string;
  completeCount: number;
  limitedAt?: string;
  isArchived: boolean;
  hasCertificate: boolean;
  type:
    | 'LEGACY'
    | 'IS_ADMIN'
    | 'BY_ADMIN'
    | 'IS_INSTRUCTOR'
    | 'BY_INSTRUCTOR'
    | 'FREE'
    | 'ORDER'
    | 'CODE'
    | 'GROUP_ORDER'
    | 'GROUP_SUBSCRIPTION'
    | 'COURSE_GIFT';
  cancelType?: 'B2B' | 'B2C';
  isArchivable: boolean;
  isReviewable: boolean;
  review?: MyVoucherReviewDto;
  course: MyVoucherCourseDto;
  limitedAtUTC?: string;
}

export interface MyVoucherOfflineCourseDto {
  id: number;
  startedAt: string;
  endedAt: string;
  hasSurveyAnswer: boolean;
  canAnswerSurvey: boolean;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface MyVoucherReviewDto {
  id: number;
  content: string;
  rating: number;
  reasons: string[];
}

export interface OffsetPageResponseMyVoucherDto {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: MyVoucherDto[];
}

export interface Requester {
  id: number;
  isAdmin: boolean;
  isBlacklist: boolean;
  isInstructor: boolean;
  isNotAdmin: boolean;
  isNotInstructor: boolean;
}

export interface ApiResponseListGetEnrollmentVerificationResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: GetEnrollmentVerificationResponse[];
}

export interface GetEnrollmentVerificationResponse {
  id: number;
  courseId: number;
  courseSlug: string;
  courseTitle: string;
  courseThumbnailUrl: string;
  isConfirmed: boolean;
  createdAt: string;
  createdAtUTC: string;
}

export interface SearchSkillResponse {
  skills: SkillSummaryDto[];
}

export interface SkillSummaryDto {
  id: number;
  title: string;
  slug: string;
}

export interface BankResponse {
  name: string;
  accountNumber: string;
  limitedAt: string;
  limitedAtUTC: string;
}

export interface ChallengeInfoResponse {
  canAnswerSurvey: boolean;
  hasSurveyAnswer: boolean;
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
  applyStartedAtUTC?: string | null;
  applyEndedAtUTC?: string | null;
}

export interface CommonDiscountResponse {
  id?: number | null;
  title: string;
  type: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
  discountRate: number;
  discountPrice: number;
  discountStrategy: 'PERCENT' | 'PRICE';
  couponId?: number | null;
}

export interface CourseResponse {
  id: number;
  slug: string;
  title: string;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  studentCount: number;
  totalStar: number;
  reviewCount: number;
  likeCount: number;
  thumbnailUrl: string;
  publishedAt?: string | null;
  lastUpdatedAt?: string | null;
  revenueRatio: number;
  providesInstructorAnswer: boolean;
  instructor: OrderPaymentCommonInstructorResponse;
  metadata: OrderPaymentCommonCourseMetadataResponse;
  voucher?: VoucherResponse;
  offline?: OfflineInfoResponse;
  challenge?: ChallengeInfoResponse;
  publishedAtUTC: string;
  lastUpdatedAtUTC: string;
}

export interface OfflineInfoResponse {
  canAnswerSurvey: boolean;
  hasSurveyAnswer: boolean;
  surveyDueDateUTC?: string | null;
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
  applyStartedAtUTC?: string | null;
  applyEndedAtUTC?: string | null;
}

export interface OrderDetailPaymentResponse {
  id: number;
  userId: number;
  createdAt?: string | null;
  status:
    | 'CANCEL_DELETE'
    | 'MENTEE_CANCEL'
    | 'PARTIAL_CANCEL'
    | 'CANCEL_NOT_ENOUGH_PROGRESS'
    | 'GROUP_PAYMENT_CANCEL'
    | 'PAYMENT_CANCEL'
    | 'ORDER_FORM'
    | 'CONFLICT_CANCEL'
    | 'AUTO_CANCEL'
    | 'PAYMENT_READY'
    | 'TOTAL_CANCEL'
    | 'PAYMENT_FAIL'
    | 'PAYMENT_STOP'
    | 'CODE_ORDER'
    | 'ORDER_CANCEL'
    | 'PAYMENT_COMPLETE'
    | 'PAYMENT_COMPLETE_PROCESSING'
    | 'ADMIN_CANCEL'
    | 'MENTOR_CANCEL'
    | 'DEPOSIT_WAIT'
    | 'WC_COMPLETED'
    | 'MANUAL_DELETE'
    | 'REFUND_REQUEST';
  paymentMethodType?:
    | 'CARD'
    | 'NAVERPAY'
    | 'NAVERPAY_MONEY'
    | 'KAKAOPAY'
    | 'TOSSPAY'
    | 'TOSS_PAY_MONEY'
    | 'APPLEPAY'
    | 'VIRTUAL_ACCOUNT'
    | 'LIFE_EDUCATION'
    | 'OVERSEAS'
    | 'INFPAY'
    | 'BANK_TRANSFER'
    | 'PAYCO'
    | 'UNKNOWN'
    | 'NONE'
    | 'LINKPAY'
    | 'LINKPAY_NAVERPAY'
    | 'LINKPAY_KAKAOPAY'
    | 'LINKPAY_TOSSPAY'
    | 'LINKPAY_PAYCO'
    | 'LINKPAY_APPLEPAY'
    | 'LINKPAY_CARD'
    | 'LINKPAY_BANK_TRANSFER'
    | 'FPX'
    | 'SOFORT'
    | 'PAYNOW'
    | 'GRABPAYPH'
    | 'GRABPAYMY'
    | 'BOOST'
    | 'TOUCHNGO'
    | 'KONBINI'
    | 'PAYSHOP'
    | 'BANCONTACT'
    | 'EPS'
    | 'MULTIBANCO'
    | 'PRZELEWY24'
    | 'BLIK'
    | 'IDEAL'
    | 'PLATFORM'
    | 'POLI'
    | 'INSTANT_BANK_TRANSFER'
    | 'UPI';
  regularPrice: number;
  paymentPrice: number;
  discountPrice: number;
  point: number;
  receiptUrl: string;
  isReceiptAvailable: boolean;
  canGetInvoice: boolean;
  isOldDataWithNoReceipt: boolean;
  paymentChannel:
    | 'TOSS'
    | 'PAYMENTWALL'
    | 'TAZAPAY'
    | 'PAYPAL'
    | 'PAYLETTER'
    | 'IAMPORT'
    | 'NAVERPAY'
    | 'NONE'
    | 'TOSS_PAY';
  bank?: BankResponse;
  currency: 'KRW' | 'USD' | 'VND' | 'JPY';
  timezone: string;
  paidAtUTC?: string | null;
  isGlobalPayment: boolean;
  refundedPoint: number;
  refundStatus: 'IDLE' | 'PENDING' | 'COMPLETE' | 'REJECT';
  items: OrderItemResponse[];
  requestGift?: RequestGiftResponse;
  createdAtUTC: string;
}

export interface RequestGiftResponse {
  type: 'LINK' | 'PHONE';
  paymentLink: string;
  createdAtUTC: string;
  expiredAtUTC: string;
  requester: RequesterResponse;
  payer: PayerResponse;
  photoCard: RequestGiftResponsePhotoCardResponse;
}

export interface PayerResponse {
  name?: string | null;
  phone?: string | null;
}

export interface RequestGiftResponsePhotoCardResponse {
  id: number;
  imageUrl: string;
  message: string;
}

export interface RequesterResponse {
  name: string;
  email: string;
  phone: string;
  countryCode: string;
}

export interface OrderItemResponse {
  cartId: number;
  title: string;
  status:
    | 'AUTO_CANCEL'
    | 'REFUND'
    | 'CANCEL'
    | 'PAID'
    | 'ADMIN_CANCEL'
    | 'CANCEL_MENTOR'
    | 'WC_COMPLETED'
    | 'WAIT'
    | 'MANUAL_DELETE'
    | 'CANCEL_DELETE'
    | 'CANCEL_PROGRESS'
    | 'CANCEL_MENTEE'
    | 'GROUP_PAYMENT_CANCEL'
    | 'ORDER'
    | 'ORDER_CANCEL';
  regularPrice: number;
  paymentPrice: number;
  courseId: number;
  krwRegularPrice: number;
  krwPaymentPrice: number;
  isRefundable: boolean;
  discount?: CommonDiscountResponse;
  mentoringCode: MentoringCodeResponse;
  course?: CourseResponse;
}

export interface MentoringCodeResponse {
  isProvided: boolean;
  isUsed: boolean;
}

export interface OrderPaymentCommonCourseMetadataResponse {
  skillTags: OrderPaymentCommonSkillTagResponse[];
  categories: OrderPaymentCommonCourseCategoryResponse[];
  levels: CourseMetadataLevelResponse[];
}

export interface CourseMetadataLevelResponse {
  code: string;
}

export interface OrderPaymentCommonCourseCategoryResponse {
  id: number;
  title: string;
  slug: string;
  parent: OrderPaymentCommonCourseMetadataResponseParentCategoryResponse;
}

export interface OrderPaymentCommonSkillTagResponse {
  id: number;
  title: string;
  slug: string;
}

export interface OrderPaymentCommonCourseMetadataResponseParentCategoryResponse {
  id: number;
  title: string;
  slug: string;
}

export interface OrderPaymentCommonInstructorResponse {
  userId: number;
  userName: string;
  userSlug?: string | null;
}

export interface VoucherResponse {
  hasDownloadResource: boolean;
  hasDownloadVideo: boolean;
  progress: string;
  createdAt?: string | null;
  limitedAt?: string | null;
  createdAtUTC: string;
  limitedAtUTC?: string | null;
}

export interface GetMyRoadmapResponseV2 {
  id: number;
  status: 'DRAFT' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PRIVATE';
  accessCode?: string | null;
  title: string;
  thumbnail?: GetMyRoadmapResponseV2Thumbnail;
  jsonDescription: string;
  htmlDescription: string;
  items: RoadmapItemResponse[];
  latestPublishedHistory?: GetMyRoadmapResponseV2PublishedHistory;
}

export interface GetMyRoadmapResponseV2PublishedHistory {
  id: number;
  name: string;
  savedAt: string;
  savedAtUTC: string;
}

export interface GetMyRoadmapResponseV2Thumbnail {
  id: number;
  url: string;
}

export interface RoadmapItemResponse {
  id: number;
  title: string;
  description: string;
  type: 'COURSE' | 'BOOK' | 'LINK';
  course?: RoadmapCourseResponse;
  book?: RoadmapBookResponse;
  link?: RoadmapLinkResponse;
}

export interface RoadmapBookResponse {
  thumbnailUrl: string;
  url: string;
  authors: string[];
  translators: string[];
  publisher: string;
  isbn: string;
}

export interface RoadmapCourseResponse {
  id: number;
  thumbnailUrl: string;
  instructorName: string;
}

export interface RoadmapLinkResponse {
  thumbnailUrl: string;
  url: string;
  iconUrl?: string | null;
}

export interface LatestRoadmapHistoryResponse {
  id: number;
  status: 'RESTORE' | 'PUBLISH' | 'DRAFT';
  accessCode?: string | null;
  title?: string | null;
  thumbnail: LatestRoadmapHistoryResponseThumbnail;
  jsonDescription?: string | null;
  htmlDescription?: string | null;
  savedAt: string;
  items: RoadmapItemResponse[];
  savedAtUTC: string;
}

export interface LatestRoadmapHistoryResponseThumbnail {
  id?: number | null;
  url: string;
}

export interface FindMyListCoursesResponse {
  id: number;
  myListTitle?: MyListTitle;
  courses: FindMyListCourseInfo[];
  title: string;
}

export interface FindMyListCourseInfo {
  id: number;
  thumbnailUrl: string;
  courseTitle?: CourseTitle;
  slug: string;
  courseInstructorName?: string | null;
  courseInstructorSlug?: string | null;
  language?: 'KO' | 'EN' | 'JA' | 'VI';
  duration?: number | null;
  totalStar?: number | null;
  reviewCount?: number | null;
  tags: string[];
  lectureCount: number;
  canStudy: boolean;
  voucher?: FindMyListCourseVoucherInfo;
  title: string;
  expireInMonth?: number | null;
  rating: number;
  instructorName: string;
}

export interface FindMyListCourseVoucherInfo {
  limitedAt?: string | null;
  completedLectureCount: number;
  progress: number;
  lastUnitId?: number | null;
  limitedAtUTC?: string | null;
}

export interface ApiResponseFindMyListCoursesResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: FindMyListCoursesResponse;
}

export interface CourseTitle {
  desanitized?: string | null;
  sanitized?: string | null;
}

export interface MyListTitle {
  value: string;
  desanitized: string;
}

export interface CourseByUserResponse {
  items: CourseByUserItemResponse[];
}

export interface CourseByUserItemResponse {
  id: number;
  title: string;
  slug: string;
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  vodOriginalLanguage:
    | 'KO'
    | 'EN'
    | 'JA'
    | 'VI'
    | 'CN'
    | 'TW'
    | 'HI'
    | 'FR'
    | 'CA'
    | 'ES'
    | 'MX'
    | 'PT';
  type: 'ON' | 'OFF' | 'CHALLENGE';
  isExclusive: boolean;
  thumbnailUrl: string;
  price?: number | null;
}

export interface CourseQuizSummaryResponse {
  totalQuestionCount: number;
  totalCorrectCount: number;
  isAllCompleted: boolean;
  score: number;
  correctRate: number;
}

export interface CourseStepResponse {
  info: boolean;
  curriculum: boolean;
  detail: boolean;
  coverImage: boolean;
  setting: boolean;
  autoMessage: boolean;
  faq: boolean;
  instructor: boolean;
}

export interface CourseCategoryTitleResponse {
  firstCategory: string[];
  secondCategory: string[];
}

export interface CourseInstructorResponse {
  id: number;
  name: string;
  slug?: string | null;
}

export interface CourseOneByInstructorResponse {
  id: number;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  title: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  slug: string;
  price?: number | null;
  sourceCurrency: 'KRW' | 'USD' | 'VND' | 'JPY';
  duration?: number | null;
  courseType: 'ONLINE' | 'CLASS' | 'CONFERENCE' | 'CHALLENGE';
  skills: CourseSkillOneResponse[];
  representativeInstructor: CourseInstructorResponse;
  category: CourseCategoryTitleResponse;
  thumbnailUrl: string;
  levels: CourseOneByInstructorLevelResponse[];
  discountedPrice?: number | null;
  discountStartedAtUTC?: string | null;
  discountEndedAtUTC?: string | null;
  instructorCount: number;
  studentCount: number;
  appliedStudentCount: number;
  targetPublishAtUTC?: string | null;
  submittedAtUTC?: string | null;
  challengeCourse?: CompanionChallengeCourseResponse;
  rejectCode?: 'AWKWARD_MATCHING' | 'AI_CONTENT_CHECK_REQUIRED';
}

export interface CompanionChallengeCourseResponse {
  originalCourseId?: number | null;
  challengeCourseType: 'NORMAL' | 'LIGHTENING_LIVE';
}

export interface CourseOneByInstructorLevelResponse {
  level: string;
  levelCode: string;
}

export interface CourseSkillOneResponse {
  id: number;
  title: string;
  slug: string;
  subTitle: string;
}

export interface CurriculumLectureUnitResponse {
  id: number;
  title: string;
  type: 'SECTION' | 'LECTURE' | 'MISSION' | 'DOCUMENT' | 'QUIZ' | 'LIVE_SESSION' | 'LABLUP';
  runtime: number;
  isPreview: boolean;
  hasAttachment: boolean;
  isComplete: boolean;
  body?: string | null;
  hasBody: boolean;
  hasVideo: boolean;
  submissionStatus?: 'NONE' | 'DRAFT' | 'REJECTED' | 'SUBMITTED' | 'ACCEPTED';
  quizId?: number | null;
  quizStatus?: QuizStatusResponse;
  isChallengeOnly: boolean;
  liveSession?: CurriculumLiveSessionResponse;
  mission?: CurriculumMissionResponse;
  readingTime?: number | null;
}

export interface CurriculumLiveSessionResponse {
  status: 'NOT_STARTED' | 'ON_LIVE' | 'ENDED';
  attendanceStatus: 'NONE' | 'ATTENDED' | 'ABSENT';
  recordingOption: 'PROVIDED' | 'NOT_PROVIDED';
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
}

export interface CurriculumMissionResponse {
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
}

export interface CurriculumSectionResponse {
  id: number;
  title: string;
  units: CurriculumLectureUnitResponse[];
}

export interface GetCurriculumResponse {
  publishedAt: string;
  lastUpdatedAt: string;
  curriculum: CurriculumSectionResponse[];
  publishedAtUTC?: string | null;
  lastUpdatedAtUTC: string;
}

export interface QuizStatusResponse {
  status: 'IN_PROGRESS' | 'RETRYING' | 'COMPLETED';
  progress: number;
  correctCount: number;
  questionCount: number;
  retryable: boolean;
  retryCount: number;
  isCompleted: boolean;
  score: number;
}

export interface GetContentsResponse {
  requestLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  responseLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  level: string;
  levelCode: string;
  description: string;
  body: string;
  editorType: 'EDITOR' | 'BUILDER';
  shownCategory: string[];
  abilities: string[];
  targets: string[];
  based: string[];
  faqs: FaqResponse[];
  mainInstructors: MainInstructorWithStatResponse[];
  challengeCourse?: GetContentsChallengeCourseInfo;
  publishedAt?: string | null;
  lastUpdatedAt: string;
  lastUpdatedAtUTC: string;
  publishedAtUTC?: string | null;
}

export interface FaqResponse {
  title: string;
  body: string;
}

export interface GetContentsChallengeCourseInfo {
  originalCourseId?: number | null;
  originalCourseTitle?: string | null;
  originalCourseSlug?: string | null;
  challengePeriod?: Period;
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
}

export interface MainInstructorWithStatResponse {
  id: number;
  name: string;
  slug: string;
  introduce: string;
  thumbnail: string;
  courseCount?: number | null;
  studentCount?: number | null;
  reviewCount?: number | null;
  totalStar?: number | null;
  answerCount?: number | null;
  introduceContentLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  introduceOriginalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
}

export interface Period {
  startedAt?: string | null;
  endedAt?: string | null;
  days?: number | null;
  valid?: boolean | null;
  periodDays?: number | null;
  within?: boolean | null;
  none?: boolean | null;
}

export interface BaseCourseChallengeResponse {
  startedAt?: string | null;
  endedAt?: string | null;
  applyStartedAt?: string | null;
  applyEndedAt?: string | null;
  applyClosedAt?: string | null;
  capacity: number;
  appliedStudentCount: number;
  displayRemainingApplicantCount?: number | null;
  hasSurveyAnswer: boolean;
  benefits: ChallengeBenefitResponse[];
  originalCourseId?: number | null;
  inquiryEmail?: string | null;
  inquiryLinkUrl?: string | null;
  startedAtUTC: string;
  endedAtUTC: string;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  applyClosedAtUTC?: string | null;
}

export interface ChallengeBenefitResponse {
  title: string;
  type: string;
  iconType: string;
}

export interface CartResponse {
  id: number;
}

export interface CourseVoucherResponseV2 {
  isValid: boolean;
  lastUnitId: number;
  progress: string;
  completeCount: number;
}

export interface GroupEnrolmentResponse {
  id: number;
}

export interface ReviewResponse {
  isShowing: boolean;
  count: number;
  averageStar: number;
}

export interface UnitSummaryResponse {
  lectureUnitCount: number;
  missionUnitCount: number;
  challengeUnitCount: number;
  liveSessionUnitCount: number;
  /**
   * previewVideoLectureCount를 사용해주세요
   */
  previewVideoUnitCount: number;
  previewUnitCount: number;
  previewVideoLectureCount: number;
  runtime: number;
  hasIntroVideo: boolean;
  totalPreviewVideoCount: number;
}

export interface CourseCategoryResponse {
  main: CourseCategoryTitleWithSlug;
  sub: CourseCategoryTitleWithSlug;
}

export interface CourseCategoryTitleWithSlug {
  title: string;
  slug: string;
}

export interface CourseLevelResponse {
  title: string;
  code: string;
  slug: string;
  isActive: boolean;
}

export interface CoursePaymentResponse {
  type:
    | 'ENROLLED'
    | 'PURCHASE_UNAVAILABLE'
    | 'B2B_SUBSCRIPTION'
    | 'B2B_ENROLMENT_PENDING'
    | 'B2B_PURCHASE'
    | 'B2B_PURCHASE_RESTRICTED'
    | 'FREE'
    | 'DISCOUNTED_PURCHASE'
    | 'REGULAR_PRICE_PURCHASE';
  regularPrice: number;
  payPrice: number;
  discountRate: number;
  discount?: CoursePaymentDiscountInfoResponse;
  group?: GroupResponse;
  krwRegularPrice: number;
  krwPaymentPrice: number;
}

export interface CoursePaymentDiscountInfoResponse {
  title: string;
  endedAt: string;
  type: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
  discountId?: number | null;
  endedAtUTC: string;
}

export interface GroupResponse {
  name: string;
  approvalRequired: boolean;
  hidePrice: boolean;
}

export interface GetChallengeCourseDetailInfoResponse {
  requestLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  responseLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  id: number;
  slug?: string | null;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  thumbnailUrl: string;
  isNew: boolean;
  isBest: boolean;
  title: string;
  studentCount: number;
  duration?: number | null;
  providesCertificate: boolean;
  providesInstructorAnswer: boolean;
  providesInquiry: boolean;
  isLiked: boolean;
  likeCount: number;
  description: string;
  unitSummary: UnitSummaryResponse;
  groupEnrolment?: GroupEnrolmentResponse;
  cart?: CartResponse;
  category: CourseCategoryResponse;
  voucher?: CourseVoucherResponseV2;
  paymentInfo: CoursePaymentResponse;
  review: ReviewResponse;
  instructors: CourseInstructorResponse[];
  levels: CourseLevelResponse[];
  skillTags: CourseSkillOneResponse[];
  publishedAt?: string | null;
  lastUpdatedAt?: string | null;
  commonTags: CommonTagResponse[];
  isGiftable?: boolean | null;
  isRequestGiftable: boolean;
  courseType: 'ON' | 'OFF' | 'CHALLENGE';
  isMonetization: boolean;
  challenge: BaseCourseChallengeResponse;
  publishedAtUTC?: string | null;
  lastUpdatedAtUTC: string;
}

export interface GetChallengePersonalInfoResponse {
  isLiked: boolean;
  voucher?: CourseVoucherResponseV2;
  cart?: CartResponse;
  paymentInfo: CoursePaymentResponse;
  survey: GetChallengePersonalInfoResponseSurveyResponse;
}

export interface GetChallengePersonalInfoResponseSurveyResponse {
  hasSurveyAnswer: boolean;
}

export interface CourseCardItem {
  id: number;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  tagTitles: string[];
  isHidePrice: boolean;
  hasMyReaction: boolean;
  hasMentoring: boolean;
  isKdt: boolean;
  offlineCourse?: CourseCardOfflineCourseResponse;
  challengeCourse?: CourseCardChallengeCourseResponse;
  course: CourseCardCourseResponse;
  instructor: CourseCardInstructorResponse;
  listPrice: CourseCardCoursePriceResponse;
  cart: CourseCardCartResponse;
  voucher?: CourseCardVoucherResponse;
  krwRegularPrice: number;
  krwPaymentPrice: number;
}

export interface CourseCardCartResponse {
  isInCart: boolean;
  canUseCart: boolean;
}

export interface CourseCardChallengeCourseResponse {
  applyStartedAt?: string | null;
  applyEndedAt?: string | null;
  eventStartedAt?: string | null;
  eventEndedAt?: string | null;
  challengeStatus?:
    | 'BEFORE_APPLY'
    | 'APPLYING'
    | 'AFTER_APPLY'
    | 'PROCESSING'
    | 'PARTICIPATING'
    | 'COMPLETED';
  capacity: number;
  originalCourseStudentCount: number;
  applicantCount: number;
  canAnswerSurvey: boolean;
  hasSurveyAnswer: boolean;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  eventStartedAtUTC: string;
  eventEndedAtUTC: string;
}

export interface CourseCardCoursePriceResponse {
  isDiscount: boolean;
  isEarlyBirdDiscount: boolean;
  payPrice: number;
  regularPrice: number;
  isFree: boolean;
  discountRate: number;
  discountEndedAt?: string | null;
  discountId?: number | null;
  discountTitle?: string | null;
  discountEndedAtUTC?: string | null;
}

export interface CourseCardCourseResponse {
  id: number;
  slug: string;
  thumbnailUrl: string;
  title: string;
  description: string;
  reviewCount: number;
  studentCount: number;
  likeCount: number;
  star: number;
  isExclusive: boolean;
  isNew: boolean;
  isUpdated: boolean;
  updatedAt: string;
  publishedAt: string;
  metadata: SearchCourseMetadataResponse;
  abilities: string[];
  courseTags: CourseCardCourseResponseCourseTagResponse[];
  targets: string[];
  runtimeSecond: number;
  updatedAtUTC: string;
  publishedAtUTC?: string | null;
}

export interface CourseCardCourseResponseCourseTagResponse {
  tag:
    | 'IS_ACTIVE_IN_QUESTION'
    | 'IS_B2B_SUBSCRIPTION'
    | 'HAS_MISSION'
    | 'IS_CHALLENGE_APPLYING'
    | 'IS_CHALLENGE_BEFORE_APPLY'
    | 'IS_CHALLENGE_AFTER_APPLY'
    | 'IS_CHALLENGE_PROCESSING'
    | 'IS_CHALLENGE_COMPLETED'
    | 'IS_CHALLENGE_PARTICIPATING';
}

export interface CourseCardInstructorResponse {
  id: number;
  name: string;
  isInflearnGuaranteed: boolean;
}

export interface CourseCardOfflineCourseResponse {
  type: 'CONFERENCE' | 'CLASS';
  applyStartedAt: string;
  applyEndedAt: string;
  eventStartedAt: string;
  eventEndedAt: string;
  capacity: number;
  applicantCount: number;
  canAnswerSurvey: boolean;
  hasSurveyAnswer: boolean;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  eventStartedAtUTC: string;
  eventEndedAtUTC: string;
}

export interface CourseCardVoucherResponse {
  completeCount: number;
  lectureCount: number;
  progress: string;
}

export interface SearchCourseMetadataResponse {
  level: string;
  levelCode: string;
  /**
   * Use skillTags instead
   */
  skills: string[];
  skillTags: SearchCourseMetadataSkillTagResponse[];
  categories: CourseCardItemCourseCategoryResponse[];
  /**
   * Use categories instead
   */
  parentCategories: string[];
  /**
   * Use categories instead
   */
  childCategories: string[];
}

export interface CourseCardItemCourseCategoryResponse {
  id: number;
  title: string;
  slug: string;
  parent: SearchCourseMetadataResponseParentCategoryResponse;
}

export interface SearchCourseMetadataResponseParentCategoryResponse {
  id: number;
  title: string;
  slug: string;
}

export interface SearchCourseMetadataSkillTagResponse {
  id: number;
  title: string;
  slug: string;
}

export interface SearchCourseResponse {
  attributionToken: string;
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: CourseCardItem[];
}

export interface UserOnboardingLlmCourseRecommendationV2Response {
  items: CourseCardWithReasonItem[];
}

export interface CourseCardWithReasonItem {
  course: CourseCardItem;
  reason?: string | null;
}

export interface ChallengeCourseRecommendResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: RecommendedChallenge[];
  attributionToken: string;
}

export interface RecommendedChallenge {
  id: number;
  isNew: boolean;
  thumbnailUrl?: string | null;
  title: string;
  slug: string;
  description?: string | null;
  instructor: ChallengeCourseRecommendInstructorInfo;
  representativeCategory: RecommendedChallengeCategoryInfo;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  applyClosedAtUTC?: string | null;
  eventStartedAtUTC: string;
  eventEndedAtUTC: string;
  price: number;
  regularPrice: number;
  payPrice: number;
  discountRate?: number | null;
  isFree: boolean;
  isDiscounting: boolean;
  hasLiveSession: boolean;
  isApplyEnded: boolean;
  challengeStatus:
    | 'BEFORE_APPLY'
    | 'APPLYING'
    | 'AFTER_APPLY'
    | 'PROCESSING'
    | 'PARTICIPATING'
    | 'COMPLETED';
  capacity: number;
  applicantCount: number;
  displayRemainingApplicantCount?: number | null;
  level: string;
  levelCode: string;
  challengeDurationDays: number;
  applicantRate: number;
}

export interface RecommendedChallengeCategoryInfo {
  id: number;
  slug: string;
  title: string;
}

export interface ChallengeCourseRecommendInstructorInfo {
  id: number;
  name: string;
  slug?: string | null;
}

export interface RecentLearningResponse {
  unitId?: number | null;
  courseSlug: string;
}

export interface ApiResponseFindMyUpcomingEventsResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: FindMyUpcomingEventsResponse;
}

export interface FindMyUpcomingEventsResponse {
  items: UpcomingEventResponse[];
}

export interface UpcomingEventResponse {
  courseId: number;
  courseTitle: string;
  unitId: number;
  unitTitle: string;
  unitType: 'SECTION' | 'LECTURE' | 'MISSION' | 'DOCUMENT' | 'QUIZ' | 'LIVE_SESSION' | 'LABLUP';
  startedAtUTC?: string;
  endedAtUTC?: string;
}

export interface FindMyRecentLearningVoucherResponse {
  items: VoucherAndCourse[];
  isRecentLearned: boolean;
}

export interface VoucherAndCourse {
  id: number;
  courseId: number;
  courseSlug: string;
  title?: CourseTitle;
  courseLectureCount: number;
  courseThumbnailUrl: string;
  progress: string;
  completeCount: number;
  unitId: number;
  accessedAtRaw?: string | null;
  instructorUserId: number;
  instructorUserName: string;
  mainCourseCategory: string;
  courseTitle: string;
  accessedAt: string;
  accessedAtUTC: string;
}

export interface FindMyChallengeVoucherByCursorResponse {
  pageSize: number;
  nextCursor?: string | null;
  hasNext: boolean;
  items: MyChallengeVoucherDto[];
}

export interface MyChallengeVoucherChallengeCourseDto {
  id: number;
  startedAt: string;
  endedAt: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'ENDED';
  missions: MissionInfo[];
  liveSessions: LiveSessionInfo[];
  survey: SurveyInfo;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface LiveSessionInfo {
  id: number;
  unitId: number;
  title: string;
  startedAt: string;
  isParticipated: boolean;
  status: 'NOT_STARTED' | 'ROOM_OPENED' | 'ROOM_ENDED';
  startedAtUTC: string;
}

export interface MissionInfo {
  id: number;
  unitId: number;
  submissionStatus?: 'DRAFT' | 'REJECTED' | 'SUBMITTED' | 'ACCEPTED';
}

export interface SurveyInfo {
  hasSurveyAnswer: boolean;
}

export interface MyChallengeVoucherCourseDto {
  id: number;
  slug: string;
  title: string;
  lectureCount: number;
  thumbnailUrl: string;
  instructorId: number;
  instructorName: string;
  challengeCourse: MyChallengeVoucherChallengeCourseDto;
  childCategories: string[];
}

export interface MyChallengeVoucherDto {
  id: number;
  userId: number;
  unitId: number;
  progress: string;
  completeCount: number;
  limitedAt?: string;
  isArchived: boolean;
  hasCertificate: boolean;
  type:
    | 'LEGACY'
    | 'IS_ADMIN'
    | 'BY_ADMIN'
    | 'IS_INSTRUCTOR'
    | 'BY_INSTRUCTOR'
    | 'FREE'
    | 'ORDER'
    | 'CODE'
    | 'GROUP_ORDER'
    | 'GROUP_SUBSCRIPTION'
    | 'COURSE_GIFT';
  cancelType?: 'B2B' | 'B2C';
  isArchivable: boolean;
  isReviewable: boolean;
  review?: MyVoucherReviewDto;
  course: MyChallengeVoucherCourseDto;
  limitedAtUTC?: string;
}

export interface ApiResponseOffsetPageResponseMyChallengeVoucherDto {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: OffsetPageResponseMyChallengeVoucherDto;
}

export interface OffsetPageResponseMyChallengeVoucherDto {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: MyChallengeVoucherDto[];
}

export interface FindMyVoucherByCursorResponse {
  pageSize: number;
  nextCursor?: string | null;
  hasNext: boolean;
  items: MyVoucherDto[];
}

export interface PageableObject {
  offset?: number | null;
  sort?: SortObject;
  paged?: boolean | null;
  pageNumber?: number | null;
  pageSize?: number | null;
  unpaged?: boolean | null;
}

export interface SortObject {
  empty?: boolean | null;
  sorted?: boolean | null;
  unsorted?: boolean | null;
}

export interface CourseChallengeResponse {
  page?: PageCourseChallengeResponseCourseChallengeItemResponse;
  instructorCount: number;
  adminCount: number;
  allCount: number;
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: CourseChallengeResponseCourseChallengeItemResponse[];
}

export interface CourseChallengeResponseCourseChallengeItemResponse {
  id: number;
  userId: number;
  userName: string;
  appliedAt?: string | null;
  paidAt?: string | null;
  surveyAnswer?: CourseChallengeResponseSurveyAnswerResponse;
  progress: string;
  lectureCount: number;
  missionCount: number;
  liveSessionCount: number;
  completedLectureCount: number;
  completedMissionCount: number;
  completedLiveSessionCount: number;
  isCertificateIssued: boolean;
  isAdmin: boolean;
  appliedAtUTC: string;
  paidAtUTC?: string | null;
}

export interface CourseChallengeResponseSurveyAnswerResponse {
  surveyId: number;
  name: string;
  email: string;
  phone: string;
  answeredAtUTC: string;
  createdAtUTC: string;
}

export interface PageCourseChallengeResponseCourseChallengeItemResponse {
  totalElements?: number | null;
  totalPages?: number | null;
  first?: boolean | null;
  last?: boolean | null;
  size?: number | null;
  content?: CourseChallengeResponseCourseChallengeItemResponse[] | null;
  number?: number | null;
  sort?: SortObject;
  numberOfElements?: number | null;
  pageable?: PageableObject;
  empty?: boolean | null;
}

export interface FindMyVoucherCategoryResponse {
  items: FindVoucherCourseCategoryResult[];
}

export interface FindVoucherCourseCategoryResult {
  id: number;
  title: string;
  count: number;
}

export interface ApiResponseUserStatisticsResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: UserStatisticsResponse;
}

export interface UserStatisticsResponse {
  userCourses: number[];
  userCourseCnt: number;
  userCourseTotalPaymentPrice: number;
  preferCategories: string[];
}

export interface FindSkillResponse {
  skills: SkillSummaryDto[];
}

export interface SectionOneSummaryResponse {
  summaryContents: SummaryDetail[];
}

export interface SummaryContent {
  title: string;
  startSeconds?: number | null;
  endSeconds?: number | null;
  content: string;
}

export interface SummaryDetail {
  id: number;
  unitId: number;
  unitTitle: string;
  type: 'VOD_SUBTITLE' | 'UNIT_BODY';
  contents: SummaryContent[];
  hasReaction: boolean;
  reactionType?: 'LIKE' | 'DISLIKE';
  sequence: number;
}

export interface SectionWithSummaryInfoResponse {
  sections: SectionDetail[];
}

export interface SectionDetail {
  id: number;
  title: string;
  isSummaryVisible: boolean;
}

export interface RouletteResultsResponse {
  /**
   * 룰렛 추첨 이력 목록. 서비스데이 오름차순(과거순)
   */
  items: RouletteResultsResponseItem[];
}

export interface RouletteResultsResponseItem {
  resultType: 'PRIZE' | 'POINT';
  prizeName?: unknown;
  point?: unknown;
  /**
   * 추첨에 사용한 응모권의 서비스데이(새벽 4시 경계 발급 기준일)
   */
  serviceDate: string;
}

export interface ApiResponseRouletteResultsResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: unknown;
  data: RouletteResultsResponse | null;
}

export interface MyRoadmapDto {
  id: number;
  latestHistoryId?: number | null;
  title: string;
  enrolledCount: number;
  commentCount: number;
  type: 'EDITOR' | 'BUILDER';
  status: 'DRAFT' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PRIVATE';
  createdAt: string;
  updatedAt: string;
  isRemovable: boolean;
  createdAtUTC: string;
  updatedAtUTC: string;
}

export interface OffsetPageResponseMyRoadmapDto {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: MyRoadmapDto[];
}

export interface RoadmapSummaryListResponse {
  items: RoadmapSummaryDto[];
}

export interface RoadmapSummaryDto {
  id: number;
  title: string;
  likeCount: number;
  enrollCount: number;
  authorName: string;
  authorThumbnailUrl: string;
  thumbnailUrl: string;
  /**
   * Use authorName instead
   */
  userName: string;
  /**
   * Use thumbnailUrl instead
   */
  coverFileUrl: string;
}

export interface SearchRoadmapResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: SearchedRoadmapCard[];
}

export interface SearchedRoadmapCard {
  id: number;
  title: string;
  thumbnail: string;
  authorName: string;
  authorThumbnail: string;
  enrollCount: number;
  isEnrolled: boolean;
  roadmapItems: SearchedRoadmapItemDto[];
  skills: SearchedRoadmapSkillDto[];
  coupon?: SearchedRoadmapCouponDto;
}

export interface SearchedRoadmapCouponDto {
  id: number;
  discount: SearchedRoadmapCouponDiscountDto;
  isUnlimited: boolean;
  remainCount: number;
  displayRemainCount: number;
}

export interface SearchedRoadmapCouponDiscountDto {
  type: 'PERCENT' | 'PRICE';
  value: number;
}

export interface SearchedRoadmapItemDto {
  id: number;
  type: 'COURSE' | 'BOOK' | 'LINK';
  title: string;
  thumbnail: string;
  instructorName?: string | null;
}

export interface SearchedRoadmapSkillDto {
  id: number;
  title: string;
}

export interface EnrolledRoadmapDto {
  id: number;
  title: string;
  courseCount: number;
  completedCourseCount: number;
  thumbnailUrl: string;
  authorName: string;
  completeCount: number;
  lectureCount: number;
  firstCourseId?: number | null;
  recentCourseId?: number | null;
}

export interface OffsetPageResponseEnrolledRoadmapDto {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: EnrolledRoadmapDto[];
}

export interface RoadmapsByAuthorResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: RoadmapSummaryDto[];
}

export interface ApiResponseRoadmapsByAuthorResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: RoadmapsByAuthorResponse;
}

export interface FindRoadmapHistoriesResponse {
  id: number;
  name: string;
  savedAt: string;
  type: 'RESTORE' | 'PUBLISH' | 'DRAFT';
  userName: string;
  userId: number;
  origin?: RoadmapHistoryOrigin;
  savedAtUTC: string;
}

export interface RoadmapHistoryOrigin {
  id: number;
  name: string;
  savedAt: string;
  type: 'RESTORE' | 'PUBLISH' | 'DRAFT';
  userName: string;
  userId: number;
  savedAtUTC: string;
}

export interface FindRegistrableRoadmapCourseResponse {
  totalCount: number;
  pageSize: number;
  nextCursor?: string | null;
  hasNext: boolean;
  items: RegistrableCourseDto[];
}

export interface RegistrableCourseDto {
  id: number;
  title: string;
  slug: string;
  description: string;
  thumbnailUrl: string;
  instructorName: string;
}

export interface FindRoadmapContentTemplateResponse {
  id: number;
  title: string;
  authorType: 'INFLAB' | 'USER';
  htmlDescription: string;
  jsonDescription: string;
  createdAt: string;
  createdAtUTC: string;
}

export interface OffsetPageResponseFindRoadmapContentTemplateResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: FindRoadmapContentTemplateResponse[];
}

export interface GetRecap2025Response {
  rank: 'MASTER' | 'SCHOLAR' | 'SKILL_BUILDER' | 'LEARNER' | 'STARTER';
  totalStudyingTimeSeconds: number;
  totalStudyingCourseCount: number;
  studyingCourses: GetRecap2025CourseInfo[];
  totalCompleteCourseCount: number;
  completeCourses: GetRecap2025CourseInfo[];
  totalSkillTagCount: number;
  skillTags: SkillTagInfo[];
  firstUnitComplete: FirstUnitCompleteInfo;
  totalInstructorCount: number;
  instructors: GetRecap2025InstructorInfo[];
  maxCompleteCourseInstructor: GetRecap2025InstructorInfo;
  maxStudyingTimeCompletedAt?: string | null;
  maxStudyingTimeMonths: number[];
  maxStudyingTimeDayOfWeek:
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
    | 'SUNDAY';
  maxStudyingTimeTimeOfDay:
    | 'EARLY_MORNING'
    | 'MORNING'
    | 'AFTERNOON'
    | 'EVENING'
    | 'NIGHT'
    | 'DAWN';
  maxContinuousStudyDay: number;
  totalCourseReviewCount: number;
  courseReviews: CourseReviewInfo[];
  totalQuestionCount: number;
  questions: QuestionInfo[];
  totalNoteCount: number;
  displayTotalStudyingTime: DisplayTotalStudyingTime;
  maxStudyingTimeCompletedAtUTC: string;
  displayMaxStudyingTimeCompletedAtDate: string;
  displayMaxStudyingTimeMonths: string;
  displayMaxStudyingTimeTimeOfDayTime: string;
  displayMaxStudyingTimeTimeOfDayTimeSlot: string;
  displayMaxStudyingTimeDayOfWeek: string;
}

export interface CourseReviewInfo {
  id: number;
  rating: number;
  content: string;
  createdAtUTC: string;
  displayCreatedAt: string;
}

export interface DisplayTotalStudyingTime {
  hours: number;
  minutes: number;
}

export interface FirstUnitCompleteInfo {
  id: number;
  courseId: number;
  courseTitle: string;
  courseThumbnail: string;
  representativeInstructorName: string;
  completedAtUTC: string;
  displayFirstUnitCompleteDate: string;
}

export interface GetRecap2025CourseInfo {
  id: number;
  title: string;
  thumbnail: string;
  representativeInstructorName: string;
}

export interface GetRecap2025InstructorInfo {
  id: number;
  name: string;
  thumbnail: string;
}

export interface QuestionInfo {
  id: number;
  title: string;
  createdAtUTC: string;
  displayCreatedAt: string;
}

export interface SkillTagInfo {
  id: number;
  title: string;
}

export interface CheckRecap2025ExistsResponse {
  hasRecap: boolean;
}

export interface QuizFindOneResponse {
  id: number;
  quizUserStatus: 'IN_PROGRESS' | 'RETRYING' | 'COMPLETED';
  questions: QuizQuestionOneResponse[];
}

export interface QuizQuestionChoiceOneResponse {
  id: number;
  content: string;
}

export interface QuizQuestionOneResponse {
  id: number;
  title: string;
  choices: QuizQuestionChoiceOneResponse[];
  retryCount: number;
  quizQuestionUserStatus: 'PASS' | 'FAIL' | 'NOT_ATTEMPTED';
  reactionType?: 'LIKE' | 'DISLIKE';
  hasReaction: boolean;
}

export interface QuizLectureUserStatsResponse {
  quizId: number;
  correctCount: number;
  questionCount: number;
  retryLeftCount: number;
  retryable: boolean;
}

export interface QuizFindOneStatusResponse {
  status: 'NOT_STARTED' | 'NO_PERMISSION' | 'IN_PROGRESS' | 'COMPLETED';
}

export interface QuizFindOneResultResponse {
  id: number;
  questions: QuizFindOneQuestion[];
  retryLeftCount: number;
  retryable: boolean;
}

export interface QuizFindOneQuestion {
  questionId: number;
  questionUserId: number;
  title: string;
  explanation?: string | null;
  lastChoiceId: number;
  status: 'PASS' | 'FAIL' | 'NOT_ATTEMPTED';
  choices: QuizFindOneResultChoice[];
  reactionType?: 'LIKE' | 'DISLIKE';
  hasReaction: boolean;
}

export interface QuizFindOneResultChoice {
  sequence: number;
  content: string;
  choiceId: number;
  isAnswer?: boolean | null;
}

export interface QuizQuestionPreviewResponse {
  quizQuestionId: number;
  sectionNumber: number;
  title: string;
  choices: ChoiceResponse[];
  explanation?: string | null;
}

export interface ChoiceResponse {
  id: number;
  sequence: number;
  content: string;
  isAnswer?: boolean | null;
}

export interface PromotionLinkEntryEventsDetailResponse {
  instructorPromotionLinkEntryEvents: InstructorPromotionLinkEntryEventsDetailResponseItem[];
  marketingPromotionLinkEntryEvents: MarketingPromotionLinkEntryEventDetailResponseItem[];
}

export interface EntryMarketingPartnerResponse {
  id: number;
  userId: number;
  name: string;
  type: 'BUSINESS' | 'PERSONAL';
}

export interface EntryPromotionLinkResponse {
  id: number;
}

export interface InstructorPromotionLinkEntryCourseResponse {
  id: number;
  title: string;
  slug: string;
}

export interface InstructorPromotionLinkEntryEventsDetailResponseItem {
  entryAt: string;
  code: string;
  expiredAt?: string | null;
  course?: InstructorPromotionLinkEntryCourseResponse;
}

export interface MarketingPromotionLinkEntryCourseResponse {
  id: number;
  title: string;
  slug: string;
  isInflearnMarketingAgreed: boolean;
}

export interface MarketingPromotionLinkEntryEventDetailResponseItem {
  entryAt: string;
  code: string;
  expiredAt?: string | null;
  course?: MarketingPromotionLinkEntryCourseResponse;
  promotionLink?: EntryPromotionLinkResponse;
  marketingPartner?: EntryMarketingPartnerResponse;
}

export interface SearchPromotionItem {
  id: number;
  type: 'COURSE' | 'URL';
  course?: SearchPromotionCourseItem;
  url?: SearchPromotionUrlItem;
  title: string;
  sequence: number;
}

export interface SearchPromotionCourseItem {
  tagTitle: string;
  course: CourseCardItem;
  url?: string | null;
}

export interface SearchPromotionUrlItem {
  image: string;
  altText: string;
  url: string;
}

export interface SearchPromotionCardsResponse {
  items: SearchPromotionItem[];
}

export interface FindPromotionBannerListResponse {
  items: PromotionBannerItem[];
}

export interface PromotionBannerItem {
  type: 'COURSE' | 'URL' | 'GROUP_BANNER_COURSE' | 'GROUP_BANNER_URL' | 'FULL_IMAGE';
  course?: CourseBanner;
  url?: UrlBanner;
}

export interface CourseBanner {
  id: number;
  courseId: number;
  title: string;
  description: string;
  slug: string;
  thumbnailUrl: string;
  discount?: FindPromotionBannerDiscountInfo;
  tagTitle: string;
  bannerTitle?: string | null;
  bannerDescription?: string | null;
  endedAtUTC?: string | null;
  startedAtUTC: string;
  url?: string | null;
}

export interface FindPromotionBannerDiscountInfo {
  isEarlyBirdDiscount: boolean;
  title: string;
  rate: number;
  endedAtUTC: string;
}

export interface UrlBanner {
  id: number;
  bannerTitle?: string | null;
  bannerDescription?: string | null;
  url: string;
  altText: string;
  bannerImageId: number;
  bannerImageUrl: string;
  bannerMobileImageId: number;
  bannerMobileImageUrl: string;
  isDDayActive: boolean;
  endedAtUTC?: string | null;
  startedAtUTC: string;
}

export interface GetMyPointsResponse {
  point: number;
}

export interface GetPaymentRequestStatusResponse {
  status:
    | 'CANCEL_DELETE'
    | 'MENTEE_CANCEL'
    | 'PARTIAL_CANCEL'
    | 'CANCEL_NOT_ENOUGH_PROGRESS'
    | 'GROUP_PAYMENT_CANCEL'
    | 'PAYMENT_CANCEL'
    | 'ORDER_FORM'
    | 'CONFLICT_CANCEL'
    | 'AUTO_CANCEL'
    | 'PAYMENT_READY'
    | 'TOTAL_CANCEL'
    | 'PAYMENT_FAIL'
    | 'PAYMENT_STOP'
    | 'CODE_ORDER'
    | 'ORDER_CANCEL'
    | 'PAYMENT_COMPLETE'
    | 'PAYMENT_COMPLETE_PROCESSING'
    | 'ADMIN_CANCEL'
    | 'MENTOR_CANCEL'
    | 'DEPOSIT_WAIT'
    | 'WC_COMPLETED'
    | 'MANUAL_DELETE'
    | 'REFUND_REQUEST';
  requesterName: string;
  createdAtUTC: string;
  paymentPrice: number;
  orderId: number;
}

export interface GetPaymentRequestCouponEligibilityResponse {
  requesterName: string;
  discountPercent: number;
  expireOffsetDay: number;
}

export interface GetValidPaymentRequestResponse {
  paymentUrl: string;
  expiredAtUTC: string;
  createdAtUTC: string;
  requesterName: string;
  photoCard: GetValidPaymentRequestResponsePhotoCardResponse;
}

export interface GetValidPaymentRequestResponsePhotoCardResponse {
  id: number;
  imageUrl: string;
  message: string;
}

export interface GetPaymentRequestPhotoCardsResponse {
  items: PaymentRequestPhotoCardResponse[];
}

export interface PaymentRequestPhotoCardResponse {
  id: number;
  title: string;
  url: string;
  isDefault: boolean;
}

export interface FindOrderSummaryResponse {
  items: OrderSummaryDto[];
  statistics: UserPurchaseStatisticsResult;
}

export interface OrderSummaryDto {
  id: number;
  title: string;
  createdAt: string;
  status:
    | 'CANCEL_DELETE'
    | 'MENTEE_CANCEL'
    | 'PARTIAL_CANCEL'
    | 'CANCEL_NOT_ENOUGH_PROGRESS'
    | 'GROUP_PAYMENT_CANCEL'
    | 'PAYMENT_CANCEL'
    | 'ORDER_FORM'
    | 'CONFLICT_CANCEL'
    | 'AUTO_CANCEL'
    | 'PAYMENT_READY'
    | 'TOTAL_CANCEL'
    | 'PAYMENT_FAIL'
    | 'PAYMENT_STOP'
    | 'CODE_ORDER'
    | 'ORDER_CANCEL'
    | 'PAYMENT_COMPLETE'
    | 'PAYMENT_COMPLETE_PROCESSING'
    | 'ADMIN_CANCEL'
    | 'MENTOR_CANCEL'
    | 'DEPOSIT_WAIT'
    | 'WC_COMPLETED'
    | 'MANUAL_DELETE'
    | 'REFUND_REQUEST';
  orderMode:
    | 'NORMAL'
    | 'B2B_CODE'
    | 'B2C_CODE'
    | 'CODE'
    | 'MENTOR'
    | 'MENTORING'
    | 'COURSE_GIFT'
    | 'CLIP'
    | 'COURSE_PACKAGE';
  regularPrice: number;
  paymentPrice: number;
  totalCourseAmount: number;
  itemsCount: number;
  receiptUrl: string;
  paymentChannel:
    | 'TOSS'
    | 'PAYMENTWALL'
    | 'TAZAPAY'
    | 'PAYPAL'
    | 'PAYLETTER'
    | 'IAMPORT'
    | 'NAVERPAY'
    | 'NONE'
    | 'TOSS_PAY';
  paymentMethodType?:
    | 'CARD'
    | 'NAVERPAY'
    | 'NAVERPAY_MONEY'
    | 'KAKAOPAY'
    | 'TOSSPAY'
    | 'TOSS_PAY_MONEY'
    | 'APPLEPAY'
    | 'VIRTUAL_ACCOUNT'
    | 'LIFE_EDUCATION'
    | 'OVERSEAS'
    | 'INFPAY'
    | 'BANK_TRANSFER'
    | 'PAYCO'
    | 'UNKNOWN'
    | 'NONE'
    | 'LINKPAY'
    | 'LINKPAY_NAVERPAY'
    | 'LINKPAY_KAKAOPAY'
    | 'LINKPAY_TOSSPAY'
    | 'LINKPAY_PAYCO'
    | 'LINKPAY_APPLEPAY'
    | 'LINKPAY_CARD'
    | 'LINKPAY_BANK_TRANSFER'
    | 'FPX'
    | 'SOFORT'
    | 'PAYNOW'
    | 'GRABPAYPH'
    | 'GRABPAYMY'
    | 'BOOST'
    | 'TOUCHNGO'
    | 'KONBINI'
    | 'PAYSHOP'
    | 'BANCONTACT'
    | 'EPS'
    | 'MULTIBANCO'
    | 'PRZELEWY24'
    | 'BLIK'
    | 'IDEAL'
    | 'PLATFORM'
    | 'POLI'
    | 'INSTANT_BANK_TRANSFER'
    | 'UPI';
  /**
   * use isReceiptAvailable
   */
  canGetReceipt: boolean;
  isReceiptAvailable: boolean;
  canGetInvoice: boolean;
  isOldDataWithNoReceipt: boolean;
  currency: 'KRW' | 'USD' | 'VND' | 'JPY';
  timezone: string;
  createdAtUTC: string;
  isPaymentRequest: boolean;
}

export interface UserPurchaseStatisticsResult {
  totalCount: number;
  totalOrderAmount: number;
  totalPaymentAmount: number;
  totalDiscountAmount: number;
}

export interface ApiResponseFindOrderSummaryResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: FindOrderSummaryResponse;
}

export interface OfflineCourseStepResponse {
  info: boolean;
  detail: boolean;
  curriculum: boolean;
  survey: boolean;
  coverImage: boolean;
  setting: boolean;
  faq: boolean;
  instructor: boolean;
}

export interface FindLatestDraftResponse {
  id: number;
  savedAt: string;
  savedAtUTC: string;
}

export interface FindMyListByCourseResponse {
  items: ItemResponse[];
}

export interface ItemResponse {
  id: number;
  title: string;
  hasCourse: boolean;
}

export interface ApiResponseFindMyListByCourseResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: FindMyListByCourseResponse;
}

export interface FindMyListResponse {
  id: number;
  title: string;
  courseCount: number;
}

export interface ApiResponseListFindMyListResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: FindMyListResponse[];
}

export interface MissionLectureOneResponse {
  id: number;
  title: string;
  missionId: number;
  body: string;
  attachments: MissionLectureAttachmentResponse[];
  hasAttachment: boolean;
  isMissionStarted: boolean;
  author: MissionLectureAuthorOneResponse;
  mainInstructor: MissionLectureOneResponseMainInstructor;
  startedAt?: string | null;
  endedAt?: string | null;
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
}

export interface MissionLectureOneResponseMainInstructor {
  id: number;
  name: string;
  slug?: string | null;
  thumbnailUrl: string;
}

export interface MissionLectureAttachmentResponse {
  type: 'LINK' | 'IMAGE' | 'FILE';
  url: string;
  fileId?: number | null;
  name?: string | null;
  value: string;
}

export interface MissionLectureAuthorOneResponse {
  id: number;
  name: string;
  slug?: string | null;
  thumbnailUrl: string;
}

export interface MySubmissionStatusResponse {
  submissionStatus: 'NONE' | 'DRAFT' | 'REJECTED' | 'SUBMITTED' | 'ACCEPTED';
  submissionId?: number | null;
}

export interface MissionLectureOneByInstructorResponse {
  id: number;
  title: string;
  missionId: number;
  body: string;
  hasAutoFeedback: boolean;
  autoFeedbackBody?: string | null;
  attachments: MissionLectureOneAttachment[];
  hasAttachment: boolean;
  author: MissionLectureAuthor;
  startedAt?: string | null;
  endedAt?: string | null;
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
}

export interface MissionLectureAuthor {
  id: number;
  name: string;
  slug?: string | null;
  thumbnailUrl: string;
}

export interface MissionLectureOneAttachment {
  type: 'LINK' | 'IMAGE' | 'FILE';
  url?: string | null;
  fileId?: number | null;
  name?: string | null;
  value: string;
}

export interface GetSummariesByCourseResponse {
  items: GetSummariesByCourseItem[];
}

export interface GetSummariesByCourseItem {
  missionId: number;
  missionLectureId: number;
  title: string;
}

export interface SearchPartnerCourseResponse {
  page?: PagePartnerCourseResponse;
  partner: SearchPartnerResponse;
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: PartnerCourseResponse[];
}

export interface PartnerCourseResponse {
  course: PartnerCourseInfoResponse;
  listPrice: PartnerCoursePriceResponse;
  instructor: PartnerCourseInstructorResponse;
  commissionRate: number;
  expectedRevenue: number;
}

export interface PartnerCourseInfoResponse {
  id: number;
  slug: string;
  thumbnailUrl: string;
  title: string;
  description: string;
  reviewCount: number;
  studentCount: number;
  likeCount: number;
  star: number;
  updatedAt: string;
  publishedAt?: string | null;
  updatedAtUTC: string;
  publishedAtUTC?: string | null;
  metadata: PartnerCourseMetadataResponse;
  abilities: string[];
  targets: string[];
  runtimeSecond: number;
}

export interface PartnerCourseMetadataResponse {
  level: string;
  levelCode: string;
  skills: string[];
  skillTags: SearchPartnerCourseSkillTagResponse[];
  categories: SearchPartnerCourseCategoryResponse[];
}

export interface SearchPartnerCourseCategoryResponse {
  id: number;
  title: string;
  slug: string;
  parent: SearchPartnerCourseCategoryResponseParentCategoryResponse;
}

export interface SearchPartnerCourseCategoryResponseParentCategoryResponse {
  id: number;
  title: string;
  slug: string;
}

export interface SearchPartnerCourseSkillTagResponse {
  id: number;
  title: string;
  slug: string;
}

export interface PartnerCourseInstructorResponse {
  id: number;
  name: string;
}

export interface PartnerCoursePriceResponse {
  isDiscount: boolean;
  originalPrice: number;
  payPrice: number;
  regularPrice: number;
  discountRate: number;
  discountEndedAt?: string | null;
  discountEndedAtUTC?: string | null;
  discountId?: number | null;
  discountTitle?: string | null;
}

export interface SearchPartnerResponse {
  id: number;
  type: 'BUSINESS' | 'PERSONAL';
  commissionRate: CommissionRateResponse;
  hasSettlementInfo: boolean;
}

export interface CommissionRateResponse {
  agreed: number;
  disagreed: number;
}

export interface ApiResponseSearchPartnerCourseResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: SearchPartnerCourseResponse;
}

export interface PagePartnerCourseResponse {
  totalElements?: number | null;
  totalPages?: number | null;
  first?: boolean | null;
  last?: boolean | null;
  size?: number | null;
  content?: PartnerCourseResponse[] | null;
  number?: number | null;
  sort?: SortObject;
  numberOfElements?: number | null;
  pageable?: PageableObject;
  empty?: boolean | null;
}

export interface GetMarketingPartnerLinkCreatePreviewResponse {
  canCreate: boolean;
  commissionRate: number;
  attributeWindowDays: number;
}

export interface ApiResponseGetMarketingPartnerLinkCreatePreviewResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: GetMarketingPartnerLinkCreatePreviewResponse;
}

export interface LiveSessionUnitsResponse {
  title: string;
  units: LiveSessionUnit[];
}

export interface LiveSessionUnit {
  id: number;
  title: string;
  recordingOption: 'PROVIDED' | 'NOT_PROVIDED';
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface GetLiveSessionAttendedResponse {
  isAttended: boolean;
}

export interface GetLiveSessionUnitResponse {
  id: number;
  title: string;
  body: string;
  liveSessionId: number;
  attachment?: AttachmentFile;
  hasAttachment: boolean;
  status: 'NOT_STARTED' | 'ON_LIVE' | 'ENDED';
  type: 'STANDARD' | 'LARGE_SCALE';
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
}

export interface AttachmentFile {
  id: number;
  name: string;
}

export interface GetLiveSessionUnitManageResponse {
  id: number;
  title: string;
  body: string;
  recordingOption: 'PROVIDED' | 'NOT_PROVIDED';
  attachment?: LiveSessionUnitAttachmentResponse;
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
}

export interface LiveSessionUnitAttachmentResponse {
  id: number;
  name: string;
}

export interface GetLiveSessionUnitsManageResponse {
  totalCount: number;
  completedCount: number;
  user: GetLiveSessionUnitsManageResponseUserResponse;
  liveSessions: LiveSessionManageItem[];
}

export interface LiveSessionManageItem {
  unitId: number;
  unitTitle: string;
  liveSessionId: number;
  isCompleted: boolean;
  period?: LiveSessionPeriodResponse;
}

export interface LiveSessionPeriodResponse {
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
}

export interface GetLiveSessionUnitsManageResponseUserResponse {
  name: string;
}

export interface LectureAttachmentOneResponse {
  id: number;
  name: string;
}

export interface LectureOneResponse {
  title: string;
  body: string;
  isPreview: boolean;
  video?: LectureVideoOneResponse;
  attachment?: LectureAttachmentOneResponse;
}

export interface LectureVideoOneResponse {
  id: number;
  name: string;
}

export interface GetLectureUnitsManageResponse {
  totalCount: number;
  completedCount: number;
  user: GetLectureUnitsManageResponseUserResponse;
  lectures: LectureManageItem[];
}

export interface LectureManageItem {
  unitId: number;
  unitTitle: string;
  isCompleted: boolean;
}

export interface GetLectureUnitsManageResponseUserResponse {
  name: string;
}

export interface GetKeywordNotificationSubscriptionResponse {
  isAlreadySubscription: boolean;
}

export interface GetRegisterChannelTypesResponse {
  registerChannelTypes: (
    | 'INTERNET_SEARCH'
    | 'SNS'
    | 'LINKEDIN_MESSAGE'
    | 'ONLINE_ADS'
    | 'REFERRAL'
    | 'ETC'
  )[];
}

export interface GetRecommendedInstructorsResponse {
  items: RecommendedInstructorDto[];
}

export interface RecommendedInstructorDto {
  id: number;
  name: string;
  slug?: string | null;
  thumbnail: string;
}

export interface PaginatedCourseByUserDetailResponse {
  items: PaginatedCourseByUserDetailResponseCourseDetailInfo[];
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface PaginatedCourseByUserDetailResponseCourseDetailInfo {
  id: number;
  title: string;
  slug: string;
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  vodOriginalLanguage:
    | 'KO'
    | 'EN'
    | 'JA'
    | 'VI'
    | 'CN'
    | 'TW'
    | 'HI'
    | 'FR'
    | 'CA'
    | 'ES'
    | 'MX'
    | 'PT';
  type: 'ON' | 'OFF' | 'CHALLENGE';
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  price?: number | null;
  regularPrice?: number | null;
  sourceCurrency: 'KRW' | 'USD' | 'VND' | 'JPY';
  promotionCode: string;
  isPromotion: boolean;
  isExclusive: boolean;
  thumbnailUrl: string;
  reviewCount: number;
  totalStar: number;
  studentCount: number;
  unAnsweredQuestionCount: number;
  discountInfo?: PaginatedCourseDiscountInfo;
  availableDiscountRange?: AvailableDiscountRange;
  contractStatus?: 'DRAFT' | 'NOT_SIGNED' | 'CANCELED' | 'SIGNED' | 'TERMINATED' | 'NO_REQUIRED';
  contractType?:
    | 'PLATFORM'
    | 'INFLUENCER'
    | 'EXCLUSIVE'
    | 'NONEXCLUSIVE'
    | 'CHALLENGE'
    | 'OFFLINE'
    | 'E';
  publishedAtUTC?: string | null;
  instructor: PaginatedCourseByUserInstructorInfo;
  offlineCourse?: PaginatedCourseByUserDetailResponseCourseDetailInfoOfflineCourseInfo;
  challengeCourse?: PaginatedCourseByUserChallengeCourseInfo;
  isRejected: boolean;
}

export interface AvailableDiscountRange {
  minSupplyPrice: number;
  maxSupplyPrice: number;
}

export interface PaginatedCourseByUserDetailResponseCourseDetailInfoOfflineCourseInfo {
  id: number;
  type: 'CONFERENCE' | 'CLASS';
  capacity: number;
  startedAt: string;
  endedAt: string;
  applyStartedAt: string;
  applyEndedAt: string;
  trainingExpenses?: number | null;
  isKdt: boolean;
}

export interface PaginatedCourseByUserChallengeCourseInfo {
  id: number;
  capacity: number;
  startedAt?: string | null;
  endedAt?: string | null;
  applyStartedAt?: string | null;
  applyEndedAt?: string | null;
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
  applyStartedAtUTC?: string | null;
  applyEndedAtUTC?: string | null;
}

export interface PaginatedCourseByUserInstructorInfo {
  userId: number;
  isRepresentative: boolean;
}

export interface PaginatedCourseDiscountInfo {
  isEarlyBirdDiscountOngoing: boolean;
  discountStatus: 'CAN_DISCOUNT' | 'ON_DISCOUNT' | 'COOL_TIME' | 'UNAVAILABLE';
  discountedPrice: number;
  discountedRegularPrice: number;
  startedAt: string;
  endedAt: string;
  startedAtUTC: string;
  endedAtUTC: string;
  coolTimeEndsAtUTC?: string | null;
}

export interface GetIsFirstCreatedCourseByInstructorResponse {
  isFirstCreatedCourse: boolean;
}

export interface GetInstructorStatusResponse {
  hasCourse: boolean;
}

export interface GetIsInstructorResponse {
  isInstructor: boolean;
}

export interface MainInstructorsResponse {
  items: MainInstructorResponse[];
}

export interface MainInstructorResponse {
  id: number;
  name: string;
  slug: string;
  introduce: string;
  thumbnail: string;
  isRepresentative: boolean;
}

export interface FindCouponAvailableCourseForInstructorResponse {
  items: CouponAvailableCourseByInstructorDto[];
}

export interface CouponAvailableCourseByInstructorDto {
  courseId: number;
  title: string;
  price: number;
  surtaxPrice: number;
}

export interface GetInstructorByEmailResponse {
  id: number;
  name: string;
  thumbnailImageUrl: string;
  isAdmin: boolean;
  isInstructor: boolean;
}

export interface GetCourseInstructorSettingsResponse {
  courseId: number;
  instructors: GetInstructorSettingsItem[];
  whetherAnswer: boolean;
}

export interface GetInstructorSettingsItem {
  userId: number;
  sequence: number;
  name: string;
  type: 'MAIN' | 'SUB';
  isQuestionEmailAllowed: boolean;
  thumbnailImageUrl: string;
  isRepresentative: boolean;
}

export interface FindInstitutionCurationResponse {
  curationList: Curation[];
}

export interface Curation {
  company: string;
  type: 'VERIFIED' | 'INTERESTED_MATCHING' | 'INTERESTED';
  iconUrl: string;
}

export interface GetGiftPhotoCardsResponse {
  items: GiftPhotoCardResponse[];
}

export interface GiftPhotoCardResponse {
  id: number;
  title: string;
  url: string;
  isDefault: boolean;
}

export interface FindBestDiscountResponse {
  discountType: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
  discountId?: number | null;
  discountTitle: string;
  endedAtUTC?: string | null;
  paymentPrice: number;
  discountRate: number;
}

export interface AttachmentOneResponse {
  name: string;
}

export interface CurriculumLectureOneResponse {
  id: number;
  isPreview: boolean;
  isVisible: boolean;
  hasAutoFeedback: boolean;
  sequence: number;
  hasVideo: boolean;
  hasAttachment: boolean;
  title: string;
  type: string;
  video?: VideoOneResponse;
  runtime?: number | null;
  attachment?: AttachmentOneResponse;
  attachmentType: 'NONE' | 'LINK' | 'IMAGE_FILE' | 'ATTACHMENT_FILE';
  quizId?: number | null;
  isChallengeOnly: boolean;
  missionStartedAtUTC?: string | null;
  missionEndedAtUTC?: string | null;
  liveSession?: LiveSessionCurriculumResponse;
}

export interface LiveSessionCurriculumResponse {
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
}

export interface CurriculumResponse {
  curriculum: SectionOneResponse[];
}

export interface SectionOneResponse {
  id: number;
  title: string;
  sequence: number;
  isVisible: boolean;
  units: CurriculumLectureOneResponse[];
}

export interface VideoOneResponse {
  name: string;
}

export interface GetCourseSlugByIdResponse {
  slug: string;
}

export interface CourseSimpleOneResponse {
  id: number;
  title: string;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  slug?: string | null;
  regularPrice?: number | null;
  duration?: number | null;
  studentCount: number;
  publishedAt?: string | null;
  submittedAt?: string | null;
  challengeCourse?: CourseSimpleOneResponseChallengeCourseResponse;
  submittedAtUTC?: string | null;
  publishedAtUTC?: string | null;
}

export interface CourseSimpleOneResponseChallengeCourseResponse {
  originalCourseId?: number | null;
}

export interface ApiResponseCourseSimpleOneResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: CourseSimpleOneResponse;
}

export interface CourseVoucherResponse {
  isValid: boolean;
  lastUnitId: number;
  progress: string;
  completeCount: number;
}

export interface GetCourseDetailInfoResponse {
  requestLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  responseLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  id: number;
  slug?: string | null;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  thumbnailUrl: string;
  isNew: boolean;
  isBest: boolean;
  title: string;
  studentCount: number;
  duration?: number | null;
  providesCertificate: boolean;
  providesInstructorAnswer: boolean;
  providesInquiry: boolean;
  isLiked: boolean;
  likeCount: number;
  description: string;
  unitSummary: UnitSummaryResponse;
  groupEnrolment?: GroupEnrolmentResponse;
  cart?: CartResponse;
  category: CourseCategoryResponse;
  voucher?: CourseVoucherResponse;
  paymentInfo: CoursePaymentResponse;
  review: ReviewResponse;
  instructors: CourseInstructorResponse[];
  levels: CourseLevelResponse[];
  skillTags: CourseSkillOneResponse[];
  publishedAt?: string | null;
  lastUpdatedAt?: string | null;
  commonTags: CommonTagResponse[];
  isGiftable?: boolean | null;
  isRequestGiftable: boolean;
  courseType: 'ON' | 'OFF' | 'CHALLENGE';
  challenge?: BaseCourseChallengeResponse;
  publishedAtUTC?: string | null;
  lastUpdatedAtUTC: string;
}

export interface GroupRecommendCourseResponse {
  items: CourseCardItem[];
}

export interface GetCourseCreationAvailabilityResponse {
  canCreateCourse: boolean;
  draftCourseCount: DraftCourseCount;
}

export interface DraftCourseCount {
  online: number;
  offline: number;
  challenge: number;
}

export interface GetChatbotRecommendedCoursesResponse {
  courses: CourseCardItem[];
}

export interface CourseAccessInfoResponse {
  id: number;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  slug: string;
  enSlug?: string | null;
  accessibleMenu: CourseAccessibleMenuResponse;
}

export interface CourseAccessibleMenuResponse {
  intro: boolean;
  dashboard: boolean;
  inquiries: boolean;
  community: boolean;
  news: boolean;
  manage: boolean;
  edit: boolean;
}

export interface CourseOneResponse {
  id: number;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  title: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  slug: string;
  price?: number | null;
  duration?: number | null;
  courseType: 'ONLINE' | 'CLASS' | 'CONFERENCE' | 'CHALLENGE';
  skills: CourseSkillOneResponse[];
  representativeInstructor: CourseInstructorResponse;
  category: CourseCategoryTitleResponse;
  thumbnailUrl: string;
  levels: CourseOneLevelResponse[];
  discountedPrice?: number | null;
  discountStartedAtUTC?: string | null;
  discountEndedAtUTC?: string | null;
  instructorCount: number;
  studentCount: number;
  targetPublishAtUTC?: string | null;
  submittedAtUTC?: string | null;
}

export interface CourseOneLevelResponse {
  level: string;
  levelCode: string;
}

export interface StudyProgressResponse {
  totalUnitCount: number;
  isCertificateIssued: boolean;
  isCertificateProvided: boolean;
  completeCount: number;
  progress: string;
  totalCompleteRuntime: number;
  voucherId: number;
  lastUnitId: number;
}

export interface RelatedChallengeCourseResponse {
  items: CourseCardItem[];
}

export interface GetOnlineCourseInfoResponse {
  requestLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  responseLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  id: number;
  slug: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  thumbnailUrl: string;
  isNew: boolean;
  isBest: boolean;
  title: string;
  studentCount: number;
  duration?: number | null;
  providesCertificate: boolean;
  providesInstructorAnswer: boolean;
  providesInquiry: boolean;
  isLiked: boolean;
  likeCount: number;
  description: string;
  unitSummary: UnitSummaryResponse;
  groupEnrolment?: GroupEnrolmentResponse;
  cart?: CartResponse;
  category: CourseCategoryResponse;
  voucher?: CourseVoucherResponse;
  paymentInfo: CoursePaymentResponse;
  review: ReviewResponse;
  instructors: CourseInstructorResponse[];
  levels: CourseLevelResponse[];
  skillTags: CourseSkillOneResponse[];
  publishedAt?: string | null;
  lastUpdatedAt: string;
  commonTags: CommonTagResponse[];
  isGiftable: boolean;
  isRequestGiftable: boolean;
  isMonetization: boolean;
  isMooc: boolean;
  isPromotion: boolean;
  publishedAtUTC?: string | null;
  lastUpdatedAtUTC: string;
}

export interface GetOfflineCourseInfoResponse {
  id: number;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  thumbnailUrl: string;
  isNew: boolean;
  isBest: boolean;
  title: string;
  isLiked: boolean;
  likeCount: number;
  providesInquiry: boolean;
  offlineInfo: OfflineCourseResponse;
  category: CourseCategoryResponse;
  voucher?: OfflineCourseVoucherResponse;
  paymentInfo: CoursePaymentResponse;
  instructors: CourseInstructorResponse[];
  levels: CourseLevelResponse[];
  skillTags: CourseSkillOneResponse[];
}

export interface KdtResponse {
  trainingExpenses: number;
}

export interface OfflineCourseResponse {
  applyStartedAt: string;
  applyEndedAt: string;
  startedAt: string;
  endedAt: string;
  capacity: number;
  applicantCount: number;
  type: 'CONFERENCE' | 'CLASS';
  kdt?: KdtResponse;
  survey: GetOfflineCourseInfoResponseSurveyResponse;
  locationType: 'ONLINE' | 'OFFLINE';
  onlineMeetingLink?: string | null;
  offlineAddressMain?: string | null;
  offlineAddressDetail?: string | null;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface OfflineCourseVoucherResponse {
  isValid: boolean;
}

export interface GetOfflineCourseInfoResponseSurveyResponse {
  hasSurveyAnswer: boolean;
  canAnswerSurvey: boolean;
}

export interface CourseMetaResponse {
  description: string;
  keywords: string;
  categorySlugs: string[];
  skillSlugs: string[];
  commonTagsSlugs: string[];
  exposure: boolean;
}

export interface GetChallengeCourseInfoResponse {
  requestLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  responseLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  id: number;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  thumbnailUrl: string;
  isNew: boolean;
  isBest: boolean;
  title: string;
  studentCount: number;
  duration?: number | null;
  providesCertificate: boolean;
  providesInstructorAnswer: boolean;
  providesInquiry: boolean;
  isLiked: boolean;
  likeCount: number;
  description: string;
  unitSummary: UnitSummaryResponse;
  groupEnrolment?: GroupEnrolmentResponse;
  cart?: CartResponse;
  category: CourseCategoryResponse;
  voucher?: CourseVoucherResponse;
  paymentInfo: CoursePaymentResponse;
  review: ReviewResponse;
  instructors: CourseInstructorResponse[];
  levels: CourseLevelResponse[];
  skillTags: CourseSkillOneResponse[];
  publishedAt?: string | null;
  lastUpdatedAt?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  applyStartedAt?: string | null;
  applyEndedAt?: string | null;
  capacity: number;
  appliedStudentCount: number;
  publishedAtUTC?: string | null;
  lastUpdatedAtUTC: string;
  startedAtUTC: string;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  endedAtUTC: string;
}

export interface UserProfileCourseResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: CourseCardItem[];
}

export interface ApiResponseUserProfileCourseResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: UserProfileCourseResponse;
}

export interface CourseBySkillIdsResponse {
  items: CourseCardItem[];
  attributeToken: string;
}

export interface CourseShortCodeResponse {
  items: CourseCardItem[];
}

export interface CourseByLikesResponse {
  items: CourseCardItem[];
}

export interface ApiResponseCourseByLikesResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: CourseByLikesResponse;
}

export interface CourseByInstructorIdResponse {
  items: CourseCardItem[];
}

export interface GetGeneratedTitleResponse {
  generatedTitles: string[];
}

export interface CourseAutoCompleteResponse {
  items: string[];
  attributionToken: string;
}

export interface FindSurveyQuestionResponse {
  surveyQuestions: FindSurveyQuestion[];
}

export interface FindSurveyChoice {
  id: number;
  content: string;
  sequence: number;
}

export interface FindSurveyQuestion {
  id: number;
  content: string;
  sequence: number;
  isRequired: boolean;
  isDefaultQuestion: boolean;
  type: 'SUBJECTIVE' | 'OBJECTIVE' | 'MULTIPLE_OBJECTIVE' | 'FILE';
  choices: FindSurveyChoice[];
}

export interface FindSurveyAnswerUploadedFileResponse {
  url: string;
}

export interface FindCourseSurveyByUserCourseResponse {
  id: number;
  title: string;
  challengeCourse: FindCourseSurveyChallengeCourseInfo;
  instructor: FindCourseSurveyInstructorInfo;
}

export interface FindCourseSurveyChallengeCourseInfo {
  startedAt: string;
  endedAt: string;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface FindCourseSurveyInstructorInfo {
  name: string;
}

export interface FindCourseSurveyByUserOneResponse {
  questionId: number;
  questionSequence: number;
  questionContent: string;
  isDefault: boolean;
  isRequired: boolean;
  isAnswered: boolean;
  type: 'OBJECTIVE' | 'MULTIPLE_OBJECTIVE' | 'SUBJECTIVE' | 'FILE';
  answerContent?: string | null;
  choices: Choice[];
  isFileUploaded: boolean;
  fileName?: string | null;
}

export interface Choice {
  id: number;
  content: string;
  sequence: number;
  isSelected: boolean;
}

export interface FindCourseSurveyByUserResponse {
  course: FindCourseSurveyByUserCourseResponse;
  surveyQuestions: FindCourseSurveyByUserOneResponse[];
}

export interface GetCourseSkillsResponse {
  courseId: number;
  skills: CourseSkillDto[];
}

export interface CourseSkillDto {
  id: number;
  title?: string | null;
  subTitle?: string | null;
  slug?: string | null;
}

export interface GetCourseSettingsResponse {
  id: number;
  price?: number | null;
  completeMessage?: string | null;
  startMessage?: string | null;
  whetherAnswer: boolean;
  hasInquiry: boolean;
  questionGuide?: string | null;
  duration?: number | null;
  exposure: boolean;
  isB2BAvailable: boolean;
  b2bPrice?: number | null;
  b2bPriceNextSyncAtUTC?: string | null;
  sourceCurrency: 'KRW' | 'USD' | 'VND' | 'JPY';
  isAiContent: boolean;
}

export interface GetChallengeCourseSettingsResponse {
  id: number;
  price?: number | null;
  exposure: boolean;
  capacity: number;
  minCapacity: number;
  hasInquiry: boolean;
  whetherAnswer: boolean;
  questionGuide?: string | null;
  inquiryEmail?: string | null;
  inquiryLink?: string | null;
}

export interface CourseRecommendationResponse {
  items: CourseCardItem[];
}

export interface UserOnboardingCourseRecommendationResponse {
  items: CourseCardItem[];
}

export interface UserOnboardingLlmCourseRecommendationResponse {
  items: CourseCardItem[];
}

export interface FreeCourseRecommendationResponse {
  items: CourseCardItem[];
}

export interface GetCourseInfoResponse {
  id: number;
  abilities: string[];
  based: string[];
  categoryIds: number[];
  mainCategoryId?: number | null;
  description: string;
  levelIds: number[];
  /**
   * use skillTags instead
   */
  skillTagIds: number[];
  targets: string[];
  title: string;
  skillTags: CourseInfoSkillTagDto[];
  commonTagIds: number[];
  commonTags: CommonTagResponse[];
  challenge?: ChallengeCourseBenefitsResponse;
}

export interface ChallengeCourseBenefitResponse {
  title: string;
  type: string;
  iconType: string;
  isDefault: boolean;
  isSelected: boolean;
}

export interface ChallengeCourseBenefitsResponse {
  benefits: ChallengeCourseBenefitResponse[];
}

export interface CourseInfoSkillTagDto {
  id: number;
  subTitle?: string | null;
  slug: string;
  relatedCount: number;
  title: string;
}

export interface GetCourseInfoAiGenerationResultResponse {
  id: number;
  requestId: number;
  courseId: number;
  title: string;
  description: string;
  categories: IdTitleResponse[];
  skillTags: IdTitleResponse[];
  level?: LevelResponse;
  abilities: string[];
  targets: string[];
  based: string[];
}

export interface IdTitleResponse {
  id: number;
  title: string;
}

export interface LevelResponse {
  id: number;
  title: string;
  code: string;
}

export interface FindLatestCourseInfoAiGenerationRequestResponse {
  id: number;
  courseId: number;
  userId: number;
  content: string;
  language: 'KO' | 'EN' | 'JA' | 'VI';
  status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  requestedAt: string;
  completedAt?: string | null;
}

export interface FindLatestCourseInfoAiAnalysisRequestResponse {
  id: number;
  courseId: number;
  userId: number;
  language: 'KO' | 'EN' | 'JA' | 'VI';
  status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  requestedAt: string;
  completedAt?: string | null;
  remainingCount: number;
}

export interface GetCourseInfoAiAnalysisReportResponse {
  isEmpty: boolean;
  category: GetCourseInfoAiAnalysisReportResponseCategoryInfo;
  skills: SkillInfo[];
  summaryInsightTitle?: string | null;
  summaryInsight?: string | null;
  keywordDemands: KeywordDemandsWithInsight;
  keywordOpportunities: KeywordOpportunitiesWithInsight;
  levelDistribution: LevelDistributionWithInsight;
  similarTopCoursesSummary: SimilarTopCoursesSummaryWithInsight;
  recommendedCoursePlan: RecommendedCoursePlan;
}

export interface GetCourseInfoAiAnalysisReportResponseCategoryInfo {
  id: number;
  title: string;
}

export interface GetCourseInfoAiAnalysisReportResponseDemandMonthlyScore {
  yearMonth: string;
  searchCountScore: number;
  detailConversionScore: number;
}

export interface KeywordDemand {
  keyword: string;
  isTopSearchVolume: boolean;
  maxSearchCount: number;
  maxDetailConversionRate: number;
  months: GetCourseInfoAiAnalysisReportResponseDemandMonthlyScore[];
}

export interface KeywordDemandsWithInsight {
  demands: KeywordDemand[];
  insight?: string | null;
}

export interface KeywordOpportunitiesWithInsight {
  opportunities: KeywordOpportunity[];
  insight?: string | null;
}

export interface KeywordOpportunity {
  label: string;
  x: number;
  y: number;
  size: number;
}

export interface LevelDistributionWithInsight {
  levels: GetCourseInfoAiAnalysisReportResponseLevelInfo[];
  insight?: string | null;
}

export interface GetCourseInfoAiAnalysisReportResponseLevelInfo {
  id: number;
  title: string;
  code: string;
  percent: number;
}

export interface RecommendedCoursePlan {
  title?: string | null;
  description?: string | null;
  abilities: string[];
  targets: string[];
}

export interface SimilarTopCoursesSummaryWithInsight {
  averageStudentCount?: number | null;
  averageSettlementPrice?: number | null;
  insights: string[];
}

export interface SkillInfo {
  id: number;
  title: string;
}

export interface FindOneSentCourseGiftResponse {
  id: number;
  orderId: number;
  orderTitle: string;
  status: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
  senderUserId: number;
  senderName: string;
  receiverName: string;
  receiverPhoneNumber?: string | null;
  message: string;
  giftPhotoCardId: number;
  giftPhotoCardImageUrl: string;
  code: string;
  refundLimitedAt: string;
  canceledAt?: string | null;
  isRefundable: boolean;
  giftSendType: 'PHONE' | 'LINK';
  course: FindOneSentCourseInfo;
  paymentInfo: FindOneSentCoursePaymentInfo;
  refundLimitedAtUTC: string;
  canceledAtUTC?: string | null;
}

export interface FindOneSentCourseGiftDiscountInfo {
  id?: number | null;
  title: string;
  discountRate: number;
}

export interface FindOneSentCourseGiftInstructorInfo {
  id: number;
  name: string;
}

export interface FindOneSentCourseInfo {
  id: number;
  title: string;
  slug: string;
  instructor: FindOneSentCourseGiftInstructorInfo;
}

export interface FindOneSentCoursePaymentInfo {
  isReceiptAvailable: boolean;
  receiptUrl: string;
  paymentPrice: number;
  paidAt?: string | null;
  payMethod?:
    | 'CARD'
    | 'NAVERPAY'
    | 'NAVERPAY_MONEY'
    | 'KAKAOPAY'
    | 'TOSSPAY'
    | 'TOSS_PAY_MONEY'
    | 'SAMSUNGPAY'
    | 'APPLEPAY'
    | 'PAYCO'
    | 'VIRTUAL_ACCOUNT'
    | 'BANK_TRANSFER'
    | 'LIFE_EDUCATION'
    | 'OVERSEAS'
    | 'INFPAY'
    | 'VBANK'
    | 'CARD_LEGACY'
    | 'NAVERPAY_LEGACY'
    | 'POINT'
    | 'PHONE'
    | 'EMPTY'
    | 'NONE'
    | 'FPX'
    | 'SOFORT'
    | 'PAYNOW'
    | 'GRABPAYPH'
    | 'GRABPAYMY'
    | 'BOOST'
    | 'TOUCHNGO'
    | 'KONBINI'
    | 'PAYSHOP'
    | 'BANCONTACT'
    | 'EPS'
    | 'MULTIBANCO'
    | 'PRZELEWY24'
    | 'BLIK'
    | 'IDEAL'
    | 'PLATFORM'
    | 'POLI'
    | 'INSTANT_BANK_TRANSFER'
    | 'UPI';
  paymentChannel:
    | 'TOSS'
    | 'PAYMENTWALL'
    | 'TAZAPAY'
    | 'PAYPAL'
    | 'PAYLETTER'
    | 'IAMPORT'
    | 'NAVERPAY'
    | 'NONE'
    | 'TOSS_PAY';
  pg?:
    | 'KAKAOPAY'
    | 'NAVERPAY'
    | 'TOSS_WIDGET'
    | 'TOSS'
    | 'TOSS_V2'
    | 'TOSS_GLOBAL'
    | 'TOSS_PAY_LEGACY'
    | 'TOSS_PAY'
    | 'IMP_KAKAOPAY'
    | 'IMP_NAVERPAY'
    | 'IMP_NICE'
    | 'IMP_JTNET'
    | 'PORTONE';
  discountInfo?: FindOneSentCourseGiftDiscountInfo;
  paidAtUTC?: string | null;
}

export interface FindSentCourseGiftResponse {
  items: FindSentCourseGiftItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPage: number;
}

export interface FindSentCourseGiftItem {
  id: number;
  receiptUrl: string;
  status: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
  createdAt: string;
  orderTitle: string;
  courseTitle: string;
  courseSlug: string;
  receiverName: string;
  isDeletedCourse: boolean;
  isRefundable: boolean;
  refundLimitedAt: string;
  regularPrice: number;
  paymentPrice: number;
  currency: 'KRW' | 'USD' | 'VND' | 'JPY';
  timezone: string;
  paymentChannel:
    | 'TOSS'
    | 'PAYMENTWALL'
    | 'TAZAPAY'
    | 'PAYPAL'
    | 'PAYLETTER'
    | 'IAMPORT'
    | 'NAVERPAY'
    | 'NONE'
    | 'TOSS_PAY';
  isReceiptAvailable: boolean;
  isInvoiceAvailable: boolean;
  createdAtUTC: string;
  refundLimitedAtUTC?: string | null;
}

export interface ApiResponseFindSentCourseGiftResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: FindSentCourseGiftResponse;
}

export interface FindReceivedCourseGiftResponse {
  items: FindReceivedCourseGiftItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPage: number;
}

export interface FindReceivedCourseGiftItem {
  id: number;
  code: string;
  giftStatus?: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
  giftPhotoCardId: number;
  giftPhotoCardImageUrl: string;
  createdAt: string;
  orderTitle: string;
  courseTitle: string;
  courseSlug: string;
  isDeletedCourse: boolean;
  senderName: string;
  limitedAt?: string | null;
  canceledAt?: string | null;
  approvedAt: string;
  duration?: number | null;
  createdAtUTC: string;
  limitedAtUTC?: string | null;
  approvedAtUTC: string;
  canceledAtUTC?: string | null;
  isCanceled: boolean;
  status: 'RECEIVED' | 'CANCELED';
}

export interface ApiResponseFindReceivedCourseGiftResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: FindReceivedCourseGiftResponse;
}

export interface CourseGiftOrderCompleteResponse {
  id: number;
  title: string;
  refundLimitedAt: string;
  canceledAt?: string | null;
  courseGift: CourseGiftItem;
  course: CourseGiftOrderCourseItem;
  mainInstructor: CourseGiftOrderCompleteResponseMainInstructor;
  paymentInfo: CourseGiftPaymentInfo;
  refundLimitedAtUTC: string;
  canceledAtUTC?: string | null;
}

export interface BuyerInfo {
  name: string;
  phone: string;
  email: string;
  countryCode: string;
}

export interface CourseGiftItem {
  id: number;
  giftStatus?: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
  message: string;
  giftPhotoCardId: number;
  giftPhotoCardImageUrl: string;
  code: string;
  giftSendType: 'PHONE' | 'LINK';
  receiver: CourseGiftOrderCompleteReceiver;
  status: 'READY_TO_RECEIVE' | 'RECEIVED' | 'CANCELED';
}

export interface CourseGiftOrderCompleteReceiver {
  name: string;
  phoneNumber?: string | null;
}

export interface CourseGiftOrderCourseItem {
  id: number;
  title: string;
  slug: string;
}

export interface CourseGiftPaymentInfo {
  isReceiptAvailable: boolean;
  receiptUrl: string;
  paymentPrice: number;
  paidAt: string;
  payMethod:
    | 'CARD'
    | 'NAVERPAY'
    | 'NAVERPAY_MONEY'
    | 'KAKAOPAY'
    | 'TOSSPAY'
    | 'TOSS_PAY_MONEY'
    | 'SAMSUNGPAY'
    | 'APPLEPAY'
    | 'PAYCO'
    | 'VIRTUAL_ACCOUNT'
    | 'BANK_TRANSFER'
    | 'LIFE_EDUCATION'
    | 'OVERSEAS'
    | 'INFPAY'
    | 'VBANK'
    | 'CARD_LEGACY'
    | 'NAVERPAY_LEGACY'
    | 'POINT'
    | 'PHONE'
    | 'EMPTY'
    | 'NONE'
    | 'FPX'
    | 'SOFORT'
    | 'PAYNOW'
    | 'GRABPAYPH'
    | 'GRABPAYMY'
    | 'BOOST'
    | 'TOUCHNGO'
    | 'KONBINI'
    | 'PAYSHOP'
    | 'BANCONTACT'
    | 'EPS'
    | 'MULTIBANCO'
    | 'PRZELEWY24'
    | 'BLIK'
    | 'IDEAL'
    | 'PLATFORM'
    | 'POLI'
    | 'INSTANT_BANK_TRANSFER'
    | 'UPI';
  paymentChannel:
    | 'TOSS'
    | 'PAYMENTWALL'
    | 'TAZAPAY'
    | 'PAYPAL'
    | 'PAYLETTER'
    | 'IAMPORT'
    | 'NAVERPAY'
    | 'NONE'
    | 'TOSS_PAY';
  pg?:
    | 'KAKAOPAY'
    | 'NAVERPAY'
    | 'TOSS_WIDGET'
    | 'TOSS'
    | 'TOSS_V2'
    | 'TOSS_GLOBAL'
    | 'TOSS_PAY_LEGACY'
    | 'TOSS_PAY'
    | 'IMP_KAKAOPAY'
    | 'IMP_NAVERPAY'
    | 'IMP_NICE'
    | 'IMP_JTNET'
    | 'PORTONE';
  priceBreakdown: PriceBreakdown;
  buyer: BuyerInfo;
  paidAtUTC: string;
}

export interface Discount {
  id?: number | null;
  title: string;
  rate: number;
  amount: number;
}

export interface CourseGiftOrderCompleteResponseMainInstructor {
  userId: number;
  name: string;
}

export interface PriceBreakdown {
  coursePrice: number;
  regularPrice: number;
  sellingPrice: number;
  discount?: Discount;
}

export interface FindOneCourseGiftResponse {
  id: number;
  orderTitle: string;
  status: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
  senderName: string;
  message: string;
  sendType: 'PHONE' | 'LINK';
  approvedAt?: string | null;
  approvedLimitedAt: string;
  canceledAt?: string | null;
  isApproveAvailable: boolean;
  alreadyHasVoucher: boolean;
  isMyReceived: boolean;
  receivedUserId?: number | null;
  giftPhotoCard: GiftPhotoCardInfo;
  loginUser?: LoginUser;
  course: FindOneCourseGiftCourseInfo;
  courseGiftVoucher?: FindOneCourseGiftVoucherInfo;
  approvedAtUTC?: string | null;
  approvedLimitedAtUTC: string;
  canceledAtUTC?: string | null;
}

export interface FindOneCourseGiftCourseInfo {
  id: number;
  title: string;
  slug: string;
  coverFileUrl: string;
  duration?: number | null;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  isDeleted: boolean;
  mainInstructor: MainInstructorInfo;
}

export interface FindOneCourseGiftVoucherInfo {
  id: number;
  limitedAt?: string | null;
  limitedAtUTC?: string | null;
}

export interface GiftPhotoCardInfo {
  id: number;
  url: string;
}

export interface LoginUser {
  name: string;
  email: string;
  isBlocked: boolean;
}

export interface MainInstructorInfo {
  userId: number;
  name: string;
}

export interface GetCourseFaqsResponse {
  hasFaq?: boolean | null;
  items: GetCourseFaqResponse[];
}

export interface GetCourseFaqResponse {
  id: number;
  title: string;
  body: string;
}

export interface FindLatestCourseCurriculumAiGenerationRequestHistoryResponse {
  id: number;
  courseId: number;
  userId: number;
  language: 'KO' | 'EN' | 'JA' | 'VI';
  status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  mode: 'APPEND' | 'OVERWRITE';
  requestedAt: string;
  completedAt?: string | null;
  checkedAt?: string | null;
  sectionCount?: number | null;
  lectureCount?: number | null;
}

export interface GetCourseCoverVodResponse {
  url?: string | null;
  id?: number | null;
  fileName?: string | null;
  metadata?: CourseVideoMetadata;
}

export interface GetCourseCoverImageResponse {
  url: string;
  id?: number | null;
  metadata?: CourseCoverMetadata;
}

export interface GetCourseCoverAiGenerationRetryInfoResponse {
  retryable: boolean;
  availableRetryCount: number;
}

export interface GetCourseCoverAiGenerationHistoriesResponse {
  items: HistoryItem[];
}

export interface HistoryItem {
  id: number;
  requestedAt: string;
  completedAt: string;
  templateId: number;
  templateTitle: string;
  compositeImageFileName?: string | null;
  compositeImageUrl?: string | null;
  images: ImageItem[];
}

export interface ImageItem {
  id?: number | null;
  url?: string | null;
  isDisliked: boolean;
}

export interface GetCourseCoverAiGenerationLatestHistoryResponse {
  id?: number | null;
  status: 'NONE' | 'NOT_REQUESTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
}

export interface GetCourseCoverAiGenerationTemplatesResponse {
  items: CourseCoverAiGenerationTemplateItem[];
}

export interface CourseCoverAiGenerationTemplateItem {
  id: number;
  title: string;
  description: string;
  requiredImageType: 'NONE' | 'PERSON';
  thumbnailUrl: string;
  templateKey: string;
  exampleImageItems: ExampleImageItem[];
}

export interface ExampleImageItem {
  url: string;
}

export interface ContractInfo {
  current?: CurrentContract;
  defaults: DefaultContractResponse[];
}

export interface CurrentContract {
  contractType:
    | 'PLATFORM'
    | 'INFLUENCER'
    | 'EXCLUSIVE'
    | 'NONEXCLUSIVE'
    | 'CHALLENGE'
    | 'OFFLINE'
    | 'E';
  isExclusive: boolean;
  isPromotion: boolean;
  isInflearnMarketing: boolean;
  inflearnMarketingConfirmedAt?: string | null;
  rate: RateInfo;
  inflearnMarketingConfirmedAtUTC?: string | null;
}

export interface DefaultContractResponse {
  contractType:
    | 'PLATFORM'
    | 'INFLUENCER'
    | 'EXCLUSIVE'
    | 'NONEXCLUSIVE'
    | 'CHALLENGE'
    | 'OFFLINE'
    | 'E';
  isPromotion?: boolean | null;
  isExclusive?: boolean | null;
  allowedUsageAi: boolean;
  rate: RateInfo;
}

export interface GetCourseContractCourseInfo {
  id: number;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  title: string;
  price?: number | null;
  isB2bAvailable: boolean;
  allowedUsageAi?: boolean | null;
  isB2bSubscription: boolean;
  isAiContent: boolean;
}

export interface GetCourseContractResponse {
  course: GetCourseContractCourseInfo;
  contract: ContractInfo;
}

export interface RateInfo {
  baseRate?: number | null;
  b2bRate?: number | null;
  promotionLinkRate?: number | null;
  openingMonthRate?: number | null;
  instructorCouponRate?: number | null;
  inflearnMarketingRate?: number | null;
  additionalMarketingRate?: number | null;
}

export interface GetCourseContractDetailResponse {
  courseId: number;
  courseTitle: string;
  courseType: string;
  contractedAt?: string | null;
  contractedAtUTC?: string | null;
  contractDurationMonth: number;
  contractAgreement: GetCourseContractAgreementDetail;
  contractor: GetCourseContractorDetail;
  bankAccount: GetCourseContractBankAccountDetail;
}

export interface GetCourseContractAgreementDetail {
  contractType:
    | 'PLATFORM'
    | 'INFLUENCER'
    | 'EXCLUSIVE'
    | 'NONEXCLUSIVE'
    | 'CHALLENGE'
    | 'OFFLINE'
    | 'E';
  salesTermAgreement: boolean;
  exclusiveTermAgreement?: boolean | null;
  b2bSubscriptionAgreement: boolean;
  inflearnMarketingAgreement: boolean;
  contentTermAgreement: boolean;
}

export interface GetCourseContractBankAccountDetail {
  bank: string;
  bankAccountHolder: string;
  bankAccountNumber: string;
}

export interface GetCourseContractorDetail {
  subjectType?: 'WITHHOLDING_TAX' | 'NON_WITHHOLDING_TAX' | 'BUSINESS';
  instructorName: string;
  name: string;
  rrn: string;
  brn: string;
  paypalLink: string;
}

export interface GetInflearnMarketingContentTermResponse {
  content: string;
}

export interface GetCourseContractContentTermResponse {
  term: string;
  termType: 'NEW_CONTRACT' | 'MODIFICATION_CONTRACT';
  courseType: 'ON' | 'OFF' | 'CHALLENGE';
}

export interface FindInflearnMarketingConfirmRequiredCoursesResponse {
  items: InflearnMarketingConfirmRequiredItem[];
}

export interface InflearnMarketingConfirmRequiredItem {
  id: number;
  title: string;
}

export interface FindCourseContentResponse {
  jsonBody: string;
  htmlBody: string;
  courseOriginalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  editorType?: 'EDITOR' | 'BUILDER';
}

export interface FindCourseContentTemplateResponse {
  id: number;
  title: string;
  authorType: 'INFLAB' | 'INSTRUCTOR';
  htmlBody: string;
  jsonBody: string;
  createdAt: string;
  thumbnailUrl?: string | null;
  createdAtUTC: string;
}

export interface OffsetPageResponseFindCourseContentTemplateResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: FindCourseContentTemplateResponse[];
}

export interface FindCourseContentHistoryResponse {
  id: number;
  name: string;
  savedAt: string;
  type: string;
  userName: string;
  userId: number;
  editorType?: 'EDITOR' | 'BUILDER';
  origin?: CourseContentHistoryOrigin;
  savedAtUTC: string;
}

export interface CourseContentHistoryOrigin {
  id: number;
  name: string;
  savedAt: string;
  type: string;
  userName: string;
  userId: number;
  savedAtUTC: string;
}

export interface FindOneCourseContentHistoryResponse {
  id: number;
  name: string;
  savedAt: string;
  userName: string;
  type: string;
  editorType?: 'EDITOR' | 'BUILDER';
  jsonBody: string;
  htmlBody: string;
  savedAtUTC: string;
}

export interface GetLatestCourseContentGenerationHistoryResponse {
  id?: number | null;
  templateId?: number | null;
  jsonBody?: string | null;
  status: 'NONE' | 'NOT_REQUESTED' | 'COMPLETED' | 'FAILED';
}

export interface GetCourseContentGenerationTemplatesResponse {
  items: CourseContentGenerationTemplateItem[];
}

export interface CourseContentGenerationTemplateItem {
  id: number;
  title: string;
  description: string;
  requiredInfoType: 'NONE' | 'INSTRUCTOR_PROFILE';
  templateKey: 'PROBLEM' | 'RESULT' | 'STORY';
  blocks: TemplateItemComponentBlock[];
}

export interface TemplateItemComponentBlock {
  title: string;
  type: 'NORMAL' | 'HOOKING';
}

export interface GetCertificatesResponse {
  userId: number;
  voucherId: number;
  courseId: number;
  courseSlug: string;
  courseTitle: string;
  courseType: 'ON' | 'OFF' | 'CHALLENGE';
  courseThumbnailUrl: string;
  certificateConfirmedAt?: string | null;
  certificatedAt?: string | null;
  certificateConfirmedAtUTC?: string | null;
  certificatedAtUTC?: string | null;
}

export interface ApiResponseListGetCertificatesResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: GetCertificatesResponse[];
}

export interface UnconfirmedResponse {
  hasUnconfirmedCertificate: boolean;
}

export interface ApiResponseUnconfirmedResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: UnconfirmedResponse;
}

export interface GetCertificateDetailResponse {
  userId: number;
  courseId: number;
  voucherId: number;
  realName: string;
  title: string;
  runtime: number;
  instructorName: string;
  startedAt: string;
  certificatedAt: string;
  startedAtUTC: string;
  certificatedAtUTC: string;
}

export interface ApiResponseGetCertificateDetailResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: GetCertificateDetailResponse;
}

export interface GetCouponsResponse {
  items: CouponItem[];
}

export interface CouponDiscountResponse {
  type: 'PERCENT' | 'PRICE';
  value: number;
}

export interface CouponItem {
  couponId: number;
  couponUserId: number;
  title: string;
  discount: CouponDiscountResponse;
  target: GetCouponsResponseCouponTargetResponse;
  endedAt: string;
  canResurrect: boolean;
  isAvailable: boolean;
  endedAtUTC: string;
}

export interface GetCouponsResponseCouponTargetResponse {
  type: 'ALL' | 'COURSE' | 'ROADMAP';
  courseIds: number[];
  requiredCourseIds?: number[] | null;
  roadmap?: RaodmapWithProgress;
}

export interface RaodmapWithProgress {
  id: number;
  title: string;
  progress: number;
  enrolledCount: number;
  likeCount: number;
  commentCount: number;
  items: RoadmapItemSummary[];
}

export interface RoadmapCourse {
  id: number;
}

export interface RoadmapItemSummary {
  id: number;
  title: string;
  type: 'COURSE' | 'BOOK' | 'LINK';
  course?: RoadmapCourse;
}

export interface FindCouponTargetsResponse {
  items: FindCouponTargetsResponseCouponTargetResponse[];
  nextCursor?: string | null;
  hasNext: boolean;
  pageSize: number;
}

export interface FindCouponTargetsResponseCouponTargetResponse {
  id: number;
  slug: string;
  title: string;
  duration?: number | null;
  thumbnail: string;
  regularPrice: number;
  paymentPrice: number;
  krwRegularPrice: number;
  krwPaymentPrice: number;
  publishedAt?: string | null;
  updatedAt?: string | null;
  discount?: FindCouponTargetsResponseDiscountDto;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  studentCount: number;
  reviewCount: number;
  reviewAverage: number;
  likeCount: number;
  level?: FindCouponTargetsResponseCourseLevelResponse;
  categories: FindCouponTargetCourseCategoryResponse[];
  skills: FindCouponTargetsResponseCourseSkillResponse[];
  instructorId: number;
  instructorName: string;
  kdtPrice?: number | null;
  isInCart: boolean;
  publishedAtUTC?: string | null;
  updatedAtUTC?: string | null;
}

export interface FindCouponTargetsResponseCourseLevelResponse {
  id: number;
  title: string;
  code: string;
}

export interface FindCouponTargetsResponseCourseSkillResponse {
  id: number;
  title: string;
  slug: string;
}

export interface FindCouponTargetsResponseDiscountDto {
  id?: number | null;
  title: string;
  discountStrategy: 'PERCENT' | 'PRICE';
  type: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
  endedAt: string;
  discountRate: number;
  discountPrice: number;
  endedAtUTC: string;
}

export interface FindCouponTargetCourseCategoryResponse {
  id: number;
  title: string;
  slug: string;
  parent: FindCouponTargetsResponseParentCategoryResponse;
}

export interface FindCouponTargetsResponseParentCategoryResponse {
  id: number;
  title: string;
  slug: string;
}

export interface GetCouponIssuableResponse {
  canIssueCoupon: boolean;
  coupon: IssuableCouponResponse;
}

export interface IssuableCouponResponse {
  id: number;
  discountType: 'PERCENT' | 'PRICE';
  discountValue: number;
}

export interface GetCouponExpirationResponse {
  now: string;
  endedAt?: string | null;
  usageExpiredAt: string;
  isActive: boolean;
  nowUTC: string;
  endedAtUTC?: string | null;
  usageExpiredAtUTC: string;
}

export interface FindCommonTagResponse {
  items: FindCommonTagResponseItem[];
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface FindCommonTagResponseItem {
  id: number;
  title: string;
  subTitle: string;
  slug: string;
  relatedCount: number;
}

export interface FindRecommendCommonTagResponse {
  items: FindCommonTagResponseItem[];
}

export interface LegacyChallengeBySkillResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: CourseCardItem[];
}

export interface ChallengeStudyProgressResponse {
  totalLectureCount: number;
  isCertificateIssued: boolean;
  isCertificateProvided: boolean;
  completeCount: number;
  progress: string;
  totalCompleteRuntimeSecond: number;
  voucherId: number;
  lastUnitId?: number | null;
  totalLiveSessionCount: number;
  completedLiveSessionCount: number;
  totalMissionCount: number;
  completedMissionCount: number;
}

export interface ChallengeStepStatusResponse {
  info: boolean;
  curriculum: boolean;
  detail: boolean;
  coverImage: boolean;
  survey: boolean;
  setting: boolean;
  autoMessage: boolean;
  faq: boolean;
  instructor: boolean;
}

export interface GetChallengeSchedulesResponse {
  applyPeriod?: Period;
  challengePeriod?: Period;
  liveSessions: ChallengeScheduleLiveSessionPeriodResponse[];
  applyStartedAtUTC?: string | null;
  applyEndedAtUTC?: string | null;
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
}

export interface ChallengeScheduleLiveSessionPeriodResponse {
  id: number;
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
  isVisible: boolean;
}

export interface GetOnlineCourseResponse {
  id: number;
  slug: string;
  thumbnailUrl: string;
  title: string;
  totalStar: number;
  reviewCount: number;
  studentCount: number;
  representativeCategory: CategoryDto;
  mainCategories: CategoryDto[];
  subCategories: CategoryDto[];
  skillTags: CategoryDto[];
}

export interface CategoryDto {
  id: number;
  slug: string;
  title: string;
}

export interface GetUnownedCompletedChallengesResponse {
  items: UnownedCompletedChallenge[];
}

export interface UnownedCompletedChallenge {
  id: number;
  title: string;
  slug: string;
  thumbnailUrl: string;
  applicantCount: number;
}

export interface GetChallengeUsedSkillTagsResponse {
  items: UsedSkillTagItem[];
}

export interface UsedSkillTagItem {
  id: number;
  slug: string;
}

export interface LegacyChallengeCourseRecommendResponse {
  attributionToken: string;
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: CourseCardItem[];
}

export interface FreeLiveChallengeResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: FreeLiveChallenge[];
}

export interface FreeLiveChallenge {
  id: number;
  thumbnailUrl?: string | null;
  title: string;
  slug: string;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  liveSessionStartedAtUTC?: string | null;
  liveSessionEndedAtUTC?: string | null;
  isAlreadyApplied: boolean;
  canApply: boolean;
  instructor: FreeLiveChallengeInstructorInfo;
  representativeCategory: FreeLiveChallengeCategoryInfo;
  isAlreadySubscribed: boolean;
}

export interface FreeLiveChallengeCategoryInfo {
  id: number;
  slug: string;
  title: string;
}

export interface FreeLiveChallengeInstructorInfo {
  id: number;
  slug?: string | null;
  name: string;
}

export interface GetFeaturedChallengesResponse {
  items: GetFeaturedChallengeItemResponse[];
}

export interface GetFeaturedChallengeItemResponse {
  id: number;
  thumbnailUrl?: string | null;
  title: string;
  slug: string;
  description?: string | null;
  instructor: GetFeaturedChallengesInstructorInfo;
  representativeCategory: GetFeaturedChallengeItemResponseCategoryInfo;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  price: number;
  regularPrice: number;
  payPrice: number;
  discountRate?: number | null;
  isFree: boolean;
  isDiscounting: boolean;
  hasLiveSession: boolean;
  /**
   * use applyClosedAtUTC
   */
  isApplyEnded: boolean;
  challengeStatus:
    | 'BEFORE_APPLY'
    | 'APPLYING'
    | 'AFTER_APPLY'
    | 'PROCESSING'
    | 'PARTICIPATING'
    | 'COMPLETED';
  capacity: number;
  applicantCount: number;
  displayRemainingApplicantCount?: number | null;
  applyClosedAt?: string | null;
  applyClosedAtUTC?: string | null;
  applicantRate: number;
}

export interface GetFeaturedChallengeItemResponseCategoryInfo {
  id: number;
  slug: string;
  title: string;
}

export interface GetFeaturedChallengesInstructorInfo {
  id: number;
  name: string;
  slug?: string | null;
}

export interface GetEndedChallengeCourseResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: EndedChallenge[];
}

export interface EndedChallenge {
  id: number;
  thumbnailUrl?: string | null;
  title: string;
  slug: string;
  description?: string | null;
  instructor: GetEndedChallengeInstructorInfo;
  representativeCategory: EndedChallengeCategoryInfo;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  price: number;
  regularPrice: number;
  payPrice: number;
  discountRate?: number | null;
  isFree: boolean;
  isDiscounting: boolean;
  hasLiveSession: boolean;
  /**
   * use applyClosedAtUTC
   */
  isApplyEnded: boolean;
  challengeStatus:
    | 'BEFORE_APPLY'
    | 'APPLYING'
    | 'AFTER_APPLY'
    | 'PROCESSING'
    | 'PARTICIPATING'
    | 'COMPLETED';
  capacity: number;
  applicantCount: number;
  displayRemainingApplicantCount?: number | null;
  isAlreadySubscribed: boolean;
  applyClosedAt?: string | null;
  applyClosedAtUTC?: string | null;
  applicantRate: number;
}

export interface EndedChallengeCategoryInfo {
  id: number;
  slug: string;
  title: string;
}

export interface GetEndedChallengeInstructorInfo {
  id: number;
  name: string;
  slug?: string | null;
}

export interface GetChallengeDashboardResponse {
  totalCount: number;
  pageSize: number;
  nextCursor?: string | null;
  hasNext: boolean;
  items: ChallengeDashboardItem[];
  lectureCount: number;
  missionCount: number;
  liveSessionCount: number;
  unitCount: number;
}

export interface ChallengeDashboardItem {
  completedUnitCount: number;
  completedLectureCount: number;
  completedMissionCount: number;
  completedLiveSessionCount: number;
  userId: number;
  name: string;
  profileImageUrl: string;
  isMine: boolean;
  units: DashboardUnitItem[];
}

export interface DashboardUnitItem {
  title: string;
  type: 'SECTION' | 'LECTURE' | 'MISSION' | 'DOCUMENT' | 'QUIZ' | 'LIVE_SESSION' | 'LABLUP';
  isCompleted: boolean;
  submissionId?: number | null;
}

export interface GetCustomIconsResponse {
  icons: CustomIconResponse[];
}

export interface CustomIconResponse {
  iconType: string;
}

export interface GetChallengeNotificationSubscriptionsResponse {
  items: ChallengeSubscriptionItem[];
}

export interface ChallengeSubscriptionItem {
  id: number;
  type: 'CHALLENGE_APPLY_STARTED' | 'NEW_CHALLENGE_OPENED';
}

export interface SearchCategoryResponse {
  items: SearchCategoryDto[];
}

export interface SearchCategoryDto {
  id: number;
  title: string;
  slug: string;
  parent?: ParentCategory;
}

export interface ParentCategory {
  id: number;
  title: string;
  slug: string;
}

export interface FindAllCategoriesResponse {
  categories: ParentCategoryWithChildrenDto[];
}

export interface ParentCategoryWithChildrenDto {
  id: number;
  slug: string;
  title: string;
  originalTitle: string;
  icon: string;
  subCategories: ParentCategoryWithChildrenDtoChildCategoryDto[];
}

export interface ParentCategoryWithChildrenDtoChildCategoryDto {
  id: number;
  slug: string;
  title: string;
  originalTitle: string;
}

export interface FindClientCartResponse {
  items: ClientCartItemDto[];
  now: string;
  nowUTC: string;
}

export interface ChallengeCartResponse {
  startedAt?: string | null;
  endedAt?: string | null;
  applyStartedAt?: string | null;
  startedAtUTC: string;
  endedAtUTC: string;
  applyStartedAtUTC: string;
}

export interface ClientCartItemDto {
  id: number;
  courseId: number;
  slug: string;
  title: string;
  duration?: number | null;
  thumbnailUrl: string;
  regularPrice: number;
  paymentPrice: number;
  krwRegularPrice: number;
  krwPaymentPrice: number;
  usdRegularPrice: number;
  usdPaymentPrice: number;
  publishedAt?: string | null;
  updatedAt?: string | null;
  discount?: FindClientCartResponseDiscountDto;
  isPromotion: boolean;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  studentCount: number;
  reviewCount: number;
  reviewAverage: number;
  likeCount: number;
  level?: FindClientCartResponseCourseLevelResponse;
  categories: FindClientCartCourseCategoryResponse[];
  skills: FindClientCartResponseCourseSkillResponse[];
  instructorId: number;
  instructorName: string;
  kdtPrice?: number | null;
  currency: 'KRW' | 'USD' | 'VND' | 'JPY';
  challenge?: ChallengeCartResponse;
  updatedAtUTC: string;
  publishedAtUTC: string;
}

export interface FindClientCartResponseCourseLevelResponse {
  id: number;
  title: string;
  code: string;
}

export interface FindClientCartResponseCourseSkillResponse {
  id: number;
  title: string;
  slug: string;
}

export interface FindClientCartResponseDiscountDto {
  id?: number | null;
  title: string;
  discountStrategy: 'PERCENT' | 'PRICE';
  type: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
  endedAt: string;
  discountRate: number;
  discountPrice: number;
  usdDiscountPrice: number;
  endedAtUTC: string;
}

export interface FindClientCartCourseCategoryResponse {
  id: number;
  title: string;
  slug: string;
  parent: FindClientCartResponseParentCategoryResponse;
}

export interface FindClientCartResponseParentCategoryResponse {
  id: number;
  title: string;
  slug: string;
}

export interface ApiResponseFindClientCartResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string | null;
  data: FindClientCartResponse;
}

export interface BuyerInfoResponse {
  email?: string | null;
  phoneNumber?: string | null;
  name?: string | null;
  countryCode?: string | null;
  useGlobalCard: boolean;
  country: string;
}

export interface AdpickCourseResponse {
  total_rows: number;
  current_page: number;
  list: AdpickCourse[];
}

export interface AdpickCourse {
  goodsNo: string;
  goodsNm: string;
  goodsPriceOrg: number;
  goodsPrice: number;
  link: string;
  imageUrl: string;
  reviewCnt: number;
  commission: number;
}

export interface MarketingDisagreedCourse {
  p_cd: string;
  commission: number;
}

export interface GetCourseDetailResponse {
  id: number;
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  type: 'ON' | 'OFF' | 'CHALLENGE';
  courseType: 'ONLINE' | 'CLASS' | 'CONFERENCE' | 'CHALLENGE';
  title: string;
  slug: string;
  thumbnailUrl: string;
  textBody: string;
  abilities: string[];
  allowedUsageAi: boolean;
  description?: string | null;
  keywords?: string | null;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  publishedAt?: string | null;
  exposure: boolean;
  runtime: number;
  courseTags: GetCourseDetailResponseCourseTagResponse[];
  price: number;
  regularPrice: number;
  discount?: CourseDetailDiscountResponse;
  revenueRatio: number;
  totalSequence: number;
  popularSequence: number;
  likeCount: number;
  isPromotion: boolean;
  isB2bSubscription: boolean;
  categories: GetCourseDetailCourseCategoryResponse[];
  skills: GetCourseDetailResponseCourseSkillResponse[];
  levels: GetCourseDetailResponseCourseLevelResponse[];
  ratings: RatingResponse;
  mainInstructors: GetCourseDetailInstructorResponse[];
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  challengeCourse?: GetCourseDetailResponseChallengeCourseResponse;
  publishedAtUTC?: string | null;
  createdAtUTC: string;
  updatedAtUTC: string;
  deletedAtUTC?: string | null;
}

export interface GetCourseDetailResponseChallengeCourseResponse {
  applyStartedAtUTC?: string | null;
  applyEndedAtUTC?: string | null;
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
  capacity: number;
  applicantCount: number;
  remainingApplicantCount: number;
  status?:
    | 'BEFORE_APPLY'
    | 'APPLYING'
    | 'AFTER_APPLY'
    | 'PROCESSING'
    | 'PARTICIPATING'
    | 'COMPLETED';
}

export interface CourseDetailDiscountResponse {
  title: string;
  discountedPrice: number;
  startedAt: string;
  endedAt: string;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface GetCourseDetailResponseCourseLevelResponse {
  id: number;
  title: string;
}

export interface GetCourseDetailResponseCourseSkillResponse {
  id: number;
  title: string;
}

export interface GetCourseDetailResponseCourseTagResponse {
  id: number;
  tag: string;
}

export interface GetCourseDetailCourseCategoryResponse {
  id: number;
  title: string;
  parent: GetCourseDetailResponseParentCategoryResponse;
}

export interface GetCourseDetailInstructorResponse {
  id: number;
  name: string;
  slug?: string | null;
  isRepresentative: boolean;
}

export interface GetCourseDetailResponseParentCategoryResponse {
  id: number;
  title: string;
}

export interface RatingResponse {
  totalCount: number;
  average: number;
  histogram: RatingHistogramResponse;
}

export interface RatingHistogramResponse {
  one: number;
  two: number;
  three: number;
  four: number;
  five: number;
}

export interface FindCartItemsResponse {
  items: CartItemResponse[];
}

export interface CartItemResponse {
  course: CartItemCourseResponse;
  discount?: CartItemDiscountResponse;
}

export interface CartItemCourseResponse {
  id: number;
  title: string;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  price: number;
  regularPrice: number;
}

export interface CartItemDiscountResponse {
  isDiscounting: boolean;
  discountedPrice: number;
  regularPrice: number;
  startedAt?: string | null;
  endedAt?: string | null;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface GetVoucherResponse {
  id: number;
  groupId?: number | null;
  progress: string;
  createdAt: string;
  endedAt?: string | null;
}

export interface GetRecentLearningsResponse {
  user: UserInfo;
  recentLearnings: RecentLearningItem[];
}

export interface RecentLearningItem {
  progress: string;
  accessedAt?: string | null;
  accessedAtUTC?: string | null;
  courseId: number;
  courseTitle: string;
  childCategories: string[];
  lectureCount: number;
  completeCount: number;
  completeUnits: CompleteUnit[];
}

export interface CompleteUnit {
  id: number;
  title: string;
  hasVideo: boolean;
  vodId?: string | null;
}

export interface UserInfo {
  id: number;
  name: string;
}

export interface GetIsExperienceGroupResponse {
  items: VoucherExperienceGroup[];
}

export interface VoucherExperienceGroup {
  voucherId: number;
  isExperienceGroup: boolean;
}

export interface VoucherCourseResponse {
  id: number;
  courseId: number;
  unitId?: number | null;
  userId: number;
  groupCode?: string | null;
  progress: string;
  completeCount: number;
  limitedAt?: string | null;
  createdAt: string;
  accessedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  payPrice: number;
  courseTitle: string;
  courseDescription?: string | null;
  courseDuration?: number | null;
  regularPrice: number;
  limitedAtUTC?: string | null;
  createdAtUTC: string;
  accessedAtUTC?: string | null;
  startedAtUTC?: string | null;
  completedAtUTC?: string | null;
}

export interface InternalCourseChallengeResponse {
  page?: PageInternalCourseChallengeResponseCourseChallengeItemResponse;
  instructorCount: number;
  adminCount: number;
  allCount: number;
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: InternalCourseChallengeResponseCourseChallengeItemResponse[];
}

export interface ChallengeUnitResponse {
  id: number;
  type: 'SECTION' | 'LECTURE' | 'MISSION' | 'DOCUMENT' | 'QUIZ' | 'LIVE_SESSION' | 'LABLUP';
  title: string;
  isCompleted: boolean;
}

export interface InternalCourseChallengeResponseCourseChallengeItemResponse {
  id: number;
  userId: number;
  userName: string;
  appliedAt?: string | null;
  paidAt?: string | null;
  hasSurveyAnswer: boolean;
  surveyAnswers: InternalCourseChallengeResponseSurveyAnswerResponse[];
  units: ChallengeUnitResponse[];
  progress: string;
  missionProgress: number;
  liveSessionProgress: number;
  lectureCount: number;
  missionCount: number;
  liveSessionCount: number;
  completedLectureCount: number;
  completedMissionCount: number;
  completedLiveSessionCount: number;
  isCertificateIssued: boolean;
  isAdmin: boolean;
  appliedAtUTC: string;
  paidAtUTC?: string | null;
}

export interface InternalCourseChallengeResponseSurveyAnswerResponse {
  surveyId: number;
  userId: number;
  questionId: number;
  content: string;
  createdAt?: string | null;
  answeredAt?: string | null;
  answeredAtUTC?: string | null;
  createdAtUTC?: string | null;
}

export interface PageInternalCourseChallengeResponseCourseChallengeItemResponse {
  totalElements?: number | null;
  totalPages?: number | null;
  first?: boolean | null;
  last?: boolean | null;
  size?: number | null;
  content?: InternalCourseChallengeResponseCourseChallengeItemResponse[] | null;
  number?: number | null;
  sort?: SortObject;
  numberOfElements?: number | null;
  pageable?: PageableObject;
  empty?: boolean | null;
}

export interface FindCompletedVoucherResponse {
  vouchers: CompletedVoucher[];
}

export interface CompletedVoucher {
  id: number;
  courseId: number;
}

export interface GetUnitOneResponse {
  id: number;
  type: 'SECTION' | 'LECTURE' | 'MISSION' | 'DOCUMENT' | 'QUIZ' | 'LIVE_SESSION' | 'LABLUP';
  title: string;
  body?: string | null;
}

export interface UnitTranslationListResponse {
  items: Item[];
}

export interface Item {
  unitId: number;
  title: string;
}

export interface UnitTranslationResponse {
  items: UnitTranslationItemResponse[];
}

export interface UnitTranslationItemResponse {
  id: number;
  courseId: number;
  language: 'KO' | 'EN' | 'JA' | 'VI';
  title: string;
  body: string;
}

export interface GetUserOfUnansweredSurveyResponse {
  items: UserOfUnansweredSurveyDto[];
}

export interface UserOfUnansweredSurveyDto {
  userId: number;
  courseId: number;
  title: string;
  createdAt: string;
}

export interface FindRemainSettlementAmountResponse {
  amount: number;
}

export interface SectionFindByAdminResponse {
  items: SectionFindByAdminItem[];
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface SectionFindByAdminItem {
  sectionId: number;
  courseId: number;
  courseTitle: string;
  sectionTitle: string;
  hasQuizLecture: boolean;
  hasLecture: boolean;
  quizId?: number | null;
  canRequestQuizGeneration: boolean;
}

export interface CourseQuizByAdminResponse {
  courseId: number;
  courseTitle: string;
  mainInstructorName?: string | null;
  quizzes: QuizItem[];
}

export interface CourseQuizByAdminQuestion {
  id: number;
  title: string;
  explanation: string;
  answerContent: string;
  sequence: number;
}

export interface QuizItem {
  id: number;
  quizLectureTitle: string;
  quizQuestions: CourseQuizByAdminQuestion[];
}

export interface QuizFindByAdminResponse {
  items: QuizFindByAdminItem[];
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface QuizFindByAdminItem {
  id: number;
  courseId?: number | null;
  quizLectureId?: number | null;
  mainInstructorUserId?: number | null;
  quizLectureTitle?: string | null;
  createdAt: string;
}

export interface QuizQuestionFindByAdminResponse {
  quizQuestions: QuizQuestionFindByAdminQuestion[];
  courseTitle?: string | null;
  quizLectureTitle?: string | null;
  quizLectureId?: number | null;
  hasInProgressRegenerationRequest: boolean;
}

export interface QuizQuestionFindByAdminChoice {
  id: number;
  content: string;
  isAnswer: boolean;
  sequence: number;
}

export interface QuizQuestionFindByAdminQuestion {
  id: number;
  sequence: number;
  choices: QuizQuestionFindByAdminChoice[];
  title: string;
  explanation: string;
}

export interface GetOnePromotionCardResponse {
  id: number;
  title: string;
  type: 'COURSE' | 'URL';
  course?: PromotionCardCourse;
  url?: string | null;
  tagTitle: string;
  sequence: number;
  imageId?: number | null;
  imageUrl?: string | null;
  bannerImageId?: number | null;
  bannerImageUrl?: string | null;
  bannerMobileImageId?: number | null;
  bannerMobileImageUrl?: string | null;
  bannerTitle?: string | null;
  bannerDescription?: string | null;
  isDDayActive: boolean;
  altText?: string | null;
  activeStatus: 'ACTIVE' | 'INACTIVE';
  startedAt: string;
  endedAt?: string | null;
  categoryIds: number[];
  keywords: string[];
  isExposeAllCategory: boolean;
  isExposeAll: boolean;
  isExpose: boolean;
  startedAtUTC: string;
  endedAtUTC?: string | null;
}

export interface PromotionCardCourse {
  id: number;
  title: string;
  slug: string;
}

export interface GetPromotionCardListResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: GetOnePromotionCardResponse[];
}

export interface FindActivePlatformDiscountResponse {
  discount?: PlatformDiscountResponse;
}

export interface PlatformDiscountResponse {
  id: number;
  title: string;
  value: number;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface FindOrderItemResponse {
  id: number;
  regularPrice: number;
  paymentPrice: number;
  createdAt: string;
  title: string;
}

export interface GetMyRoadmapResponse {
  id: number;
  status: 'DRAFT' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PRIVATE';
  title: string;
  thumbnail?: GetMyRoadmapResponseThumbnail;
  jsonDescription: string;
  htmlDescription: string;
  courses: RoadmapCourseDto[];
  latestPublishedHistory?: GetMyRoadmapResponsePublishedHistory;
}

export interface GetMyRoadmapResponsePublishedHistory {
  id: number;
  name: string;
  savedAt: string;
  savedAtUTC: string;
}

export interface RoadmapCourseDto {
  id: number;
  title: string;
  thumbnailUrl: string;
  defaultDescription: string;
  instructorName: string;
  description?: string | null;
}

export interface GetMyRoadmapResponseThumbnail {
  id: number;
  url: string;
}

export interface FindMissionByAdminResponse {
  items: FindMissionByAdminItem[];
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface FindMissionByAdminItem {
  missionId: number;
  missionTitle: string;
  courseTitle: string;
  courseId: number;
  courseSlug: string;
  unitId: number;
  instructorUserId: number;
  attachmentType: 'LINK' | 'IMAGE' | 'FILE' | 'NONE';
  link?: string | null;
  imageFileId?: number | null;
  attachmentFileId?: number | null;
}

export interface MentoringOrderResponse {
  id: number;
  title: string;
  regularPrice: number;
  paymentPrice: number;
  status:
    | 'CANCEL_DELETE'
    | 'MENTEE_CANCEL'
    | 'PARTIAL_CANCEL'
    | 'CANCEL_NOT_ENOUGH_PROGRESS'
    | 'GROUP_PAYMENT_CANCEL'
    | 'PAYMENT_CANCEL'
    | 'ORDER_FORM'
    | 'CONFLICT_CANCEL'
    | 'AUTO_CANCEL'
    | 'PAYMENT_READY'
    | 'TOTAL_CANCEL'
    | 'PAYMENT_FAIL'
    | 'PAYMENT_STOP'
    | 'CODE_ORDER'
    | 'ORDER_CANCEL'
    | 'PAYMENT_COMPLETE'
    | 'PAYMENT_COMPLETE_PROCESSING'
    | 'ADMIN_CANCEL'
    | 'MENTOR_CANCEL'
    | 'DEPOSIT_WAIT'
    | 'WC_COMPLETED'
    | 'MANUAL_DELETE'
    | 'REFUND_REQUEST';
  paymentChannel:
    | 'TOSS'
    | 'PAYMENTWALL'
    | 'TAZAPAY'
    | 'PAYPAL'
    | 'PAYLETTER'
    | 'IAMPORT'
    | 'NAVERPAY'
    | 'NONE'
    | 'TOSS_PAY';
  paymentMethod:
    | 'CARD'
    | 'NAVERPAY'
    | 'NAVERPAY_MONEY'
    | 'KAKAOPAY'
    | 'TOSSPAY'
    | 'TOSS_PAY_MONEY'
    | 'SAMSUNGPAY'
    | 'APPLEPAY'
    | 'PAYCO'
    | 'VIRTUAL_ACCOUNT'
    | 'BANK_TRANSFER'
    | 'LIFE_EDUCATION'
    | 'OVERSEAS'
    | 'INFPAY'
    | 'VBANK'
    | 'CARD_LEGACY'
    | 'NAVERPAY_LEGACY'
    | 'POINT'
    | 'PHONE'
    | 'EMPTY'
    | 'NONE'
    | 'FPX'
    | 'SOFORT'
    | 'PAYNOW'
    | 'GRABPAYPH'
    | 'GRABPAYMY'
    | 'BOOST'
    | 'TOUCHNGO'
    | 'KONBINI'
    | 'PAYSHOP'
    | 'BANCONTACT'
    | 'EPS'
    | 'MULTIBANCO'
    | 'PRZELEWY24'
    | 'BLIK'
    | 'IDEAL'
    | 'PLATFORM'
    | 'POLI'
    | 'INSTANT_BANK_TRANSFER'
    | 'UPI';
  paidAtUTC?: string | null;
  receiptUrl?: string | null;
}

export interface FindMarketingPartnerResponse {
  page?: PageMarketingPartnerItem;
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: MarketingPartnerItem[];
}

export interface MarketingPartnerItem {
  user: MarketingPartnerUser;
  marketing: MarketingPartnerMarketing;
}

export interface MarketingPartnerMarketing {
  commissionRate: CommissionRate;
  reservedCommissionRate?: ReservedCommissionRate;
  attributionWindow: number;
  items: unknown[];
  activityLinks: string[];
  type: 'BUSINESS' | 'PERSONAL';
  status: 'PENDING' | 'APPROVED';
}

export interface ReservedCommissionRate {
  agreed?: number | null;
  disagreed?: number | null;
  startedAt?: string | null;
}

export interface MarketingPartnerUser {
  id: number;
  email: string;
  nickname: string;
  realName: string;
  createdAt: string;
  accessedAt?: string | null;
}

export interface PageMarketingPartnerItem {
  totalElements?: number | null;
  totalPages?: number | null;
  first?: boolean | null;
  last?: boolean | null;
  size?: number | null;
  content?: MarketingPartnerItem[] | null;
  number?: number | null;
  sort?: SortObject;
  numberOfElements?: number | null;
  pageable?: PageableObject;
  empty?: boolean | null;
}

export interface GetMarketingPartnerHistoriesResponse {
  items: MarketingPartnerHistoryItem[];
}

export interface MarketingPartnerHistoryItem {
  updater: Updater;
  startedAt: string;
  updatedAt: string;
  commissionRate: MarketingPartnerHistoryItemCommissionRate;
  attributionWindow: number;
}

export interface MarketingPartnerHistoryItemCommissionRate {
  agreed: number;
  disagreed: number;
}

export interface Updater {
  userId?: number | null;
  name?: string | null;
}

export interface CourseByUserDetailResponse {
  items: CourseByUserDetailResponseCourseDetailInfo[];
}

export interface CourseByUserDetailResponseCourseDetailInfo {
  id: number;
  title: string;
  slug: string;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  price?: number | null;
  promotionCode: string;
  isPromotion: boolean;
  thumbnailUrl: string;
  reviewCount: number;
  totalStar: number;
  studentCount: number;
  unAnsweredQuestionCount: number;
  discountInfo?: CourseByUserDiscountInfo;
  contractStatus?: 'DRAFT' | 'NOT_SIGNED' | 'CANCELED' | 'SIGNED' | 'TERMINATED' | 'NO_REQUIRED';
  contractType?:
    | 'PLATFORM'
    | 'INFLUENCER'
    | 'EXCLUSIVE'
    | 'NONEXCLUSIVE'
    | 'CHALLENGE'
    | 'OFFLINE'
    | 'E';
  instructor: CourseByUserDetailInstructorInfo;
  offlineCourse?: CourseByUserDetailResponseCourseDetailInfoOfflineCourseInfo;
  challengeCourse?: CourseByUserDetailChallengeCourseInfo;
}

export interface CourseByUserDetailChallengeCourseInfo {
  id: number;
  capacity: number;
  startedAt?: string | null;
  endedAt?: string | null;
  applyStartedAt?: string | null;
  applyEndedAt?: string | null;
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
  applyStartedAtUTC?: string | null;
  applyEndedAtUTC?: string | null;
}

export interface CourseByUserDetailInstructorInfo {
  userId: number;
  isRepresentative: boolean;
}

export interface CourseByUserDiscountInfo {
  discountStatus: 'CAN_DISCOUNT' | 'ON_DISCOUNT' | 'COOL_TIME' | 'UNAVAILABLE';
  discountedPrice: number;
  startedAt: string;
  endedAt: string;
}

export interface CourseByUserDetailResponseCourseDetailInfoOfflineCourseInfo {
  id: number;
  type: 'CONFERENCE' | 'CLASS';
  capacity: number;
  startedAt: string;
  endedAt: string;
  applyStartedAt: string;
  applyEndedAt: string;
  trainingExpenses?: number | null;
}

export interface FindInstitutionResponse {
  id: number;
  title: string;
  iconUrl?: string | null;
}

export interface OffsetPageResponseGetVouchersResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: GetVouchersResponse[];
}

export interface FindLearningStatusResponse {
  courseId: number;
  courseTitle: string;
  curriculum: Curriculum[];
}

export interface Curriculum {
  sectionId: number;
  title: string;
  units: FindLearningStatusResponseUnit[];
}

export interface FindLearningStatusResponseUnit {
  id: number;
  title: string;
  runtime: number;
  completedAt?: string | null;
}

export interface GetCoursesResponse {
  courses: GetCourseItemResponse[];
}

export interface GetCourseItemResponse {
  id: number;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  title: string;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  based: string[];
  abilities: string[];
  targets: string[];
  description: string;
  exposure: boolean;
  runtime: number;
  totalStar: number;
  reviewCnt: number;
  studentCnt: number;
  slug: string;
  url: string;
  instructorName: string;
  thumbnailUrl: string;
  publishedAt?: string | null;
  lastUpdatedAt?: string | null;
  publishedAtUTC?: string | null;
  lastUpdatedAtUTC: string;
}

export interface CourseOverviewInstructorResponse {
  id: number;
  name: string;
  slug?: string | null;
  thumbnail: string;
}

export interface CourseOverviewSectionResponse {
  id: number;
  title: string;
  units: CourseOverviewUnitResponse[];
}

export interface CourseOverviewUnitResponse {
  id: number;
  title: string;
  type: 'SECTION' | 'LECTURE' | 'MISSION' | 'DOCUMENT' | 'QUIZ' | 'LIVE_SESSION' | 'LABLUP';
  runtime: number;
  isPreview: boolean;
  hasAttachment: boolean;
  isComplete: boolean;
  hasBody: boolean;
  hasVideo: boolean;
  submissionStatus?: 'NONE' | 'DRAFT' | 'REJECTED' | 'SUBMITTED' | 'ACCEPTED';
  quizId?: number | null;
  quizStatus?: QuizStatusResponse;
  isChallengeOnly: boolean;
  liveSession?: CurriculumLiveSessionResponse;
  mission?: CurriculumMissionResponse;
  readingTime?: number | null;
}

export interface GetCourseOverviewResponse {
  courseTitle: string;
  instructors: CourseOverviewInstructorResponse[];
  publishedAt: string;
  lastUpdatedAt: string;
  curriculum: CourseOverviewSectionResponse[];
  publishedAtUTC?: string | null;
  lastUpdatedAtUTC: string;
}

export interface GetAdminCourseDetailResponse {
  id: number;
  title: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  type: 'ON' | 'OFF' | 'CHALLENGE';
  slug?: string | null;
  enSlug?: string | null;
  price?: number | null;
  publishedAt?: string | null;
  lastUpdatedAt: string;
  keywords?: string | null;
  exposure: boolean;
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  contractStatus: 'DRAFT' | 'NOT_SIGNED' | 'CANCELED' | 'SIGNED' | 'TERMINATED' | 'NO_REQUIRED';
  revenueUserId: number;
  revenueUsername: string;
  contractAgreedAtList?: string[] | null;
  levels: AdminCourseLevelResponse[];
  categories: GetAdminDetailCourseCategoryResponse[];
  skillTags: GetAdminCourseDetailSkillTagResponse[];
  commonTags: GetAdminCourseDetailResponseCommonTagResponse[];
  challengeInfo: ChallengeInfo;
  courseManagerInfo?: CourseManagerInfo;
  instructors: GetAdminCourseDetailInstructorResponse[];
  contractAgreedAtUTCList?: string[] | null;
}

export interface AdminCourseLevelResponse {
  id: number;
  name: string;
  isSelected: boolean;
}

export interface ChallengeInfo {
  startedAt?: string | null;
  endedAt?: string | null;
  applyStartedAt?: string | null;
  applyEndedAt?: string | null;
  capacity: number;
  minCapacity: number;
  startedAtUTC?: string | null;
  endedAtUTC?: string | null;
  applyStartedAtUTC?: string | null;
  applyEndedAtUTC?: string | null;
}

export interface GetAdminCourseDetailResponseCommonTagResponse {
  id: number;
  title: string;
  slug: string;
}

export interface CourseManagerInfo {
  id: number;
  courseId: number;
  manager: string;
  createdAt?: string | null;
  createdAtUTC: string;
}

export interface GetAdminCourseDetailInstructorResponse {
  instructorUserId: number;
  instructorUserEmail: string;
  instructorUsername: string;
  instructorType: 'MAIN' | 'SUB';
}

export interface GetAdminCourseDetailSkillTagResponse {
  id: number;
  name: string;
}

export interface GetAdminDetailCourseCategoryResponse {
  id: number;
  title: string;
  slug: string;
  parentTitle: string;
  parentSlug: string;
}

export interface GetAvailableCoursesResponse {
  units: AvailableUnit[];
  vods: AvailableVod[];
}

export interface AvailableUnit {
  courseId: number;
  unitId: number;
}

export interface AvailableVod {
  vodId: string;
  courseId: number;
}

export interface GetInstructorCoursesResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: CourseItem[];
}

export interface CourseItem {
  id: number;
  courseTitle: string;
  type: 'ON' | 'OFF' | 'CHALLENGE';
}

export interface GetCourseDetailsResponse {
  items: CourseCardItem[];
}

export interface GetTranslatedCourseDetailResponse {
  requestLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  responseLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  title: string;
  level: string;
  levelCode: string;
  description: string;
  shownCategory: string[];
  abilities: string[];
  targets: string[];
  based: string[];
  mainInstructors: MainInstructorResponse[];
  publishedAt?: string | null;
  lastUpdatedAt: string;
  curriculum: Section[];
  lastUpdatedAtUTC: string;
  publishedAtUTC?: string | null;
}

export interface Section {
  id: number;
  title: string;
  units: GetTranslatedCourseDetailResponseUnit[];
}

export interface GetTranslatedCourseDetailResponseUnit {
  id: number;
  title: string;
  runtime: number;
  isPreview: boolean;
  hasAttachment: boolean;
}

export interface CourseSummaryResponse {
  items: CourseSummaryDto[];
}

export interface CourseSummaryDto {
  id: number;
  title: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  slug: string;
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  vodOriginalLanguage:
    | 'KO'
    | 'EN'
    | 'JA'
    | 'VI'
    | 'CN'
    | 'TW'
    | 'HI'
    | 'FR'
    | 'CA'
    | 'ES'
    | 'MX'
    | 'PT';
  thumbnailUrl: string;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  isExclusive: boolean;
  price?: number | null;
  instructor: Instructor;
}

export interface Instructor {
  userId: number;
  name: string;
}

export interface GetCourseStudentResponse {
  items: CourseStudentCountDto[];
}

export interface CourseStudentCountDto {
  id: number;
  studentCount: number;
}

export interface GetIsMoocResponse {
  items: CourseMooc[];
}

export interface CourseMooc {
  courseId: number;
  isMooc: boolean;
}

export interface CourseTranslationListResponse {
  items: CourseTranslationResponse[];
}

export interface CourseTranslationResponse {
  courseId: number;
  title: string;
  description: string;
  language: 'KO' | 'EN' | 'JA' | 'VI';
}

export interface MultipleCourseTranslationListResponse {
  items: MultipleCourseTranslationResponse[];
}

export interface MultipleCourseTranslationResponse {
  courseId: number;
  title: string;
  description: string;
  language: 'KO' | 'EN' | 'JA' | 'VI';
  htmlBody: string;
  abilities: string[];
}

export interface FindAnswerableCourseSurveyResponse {
  items: FindAnswerableCourseItem[];
}

export interface FindAnswerableCourseItem {
  id: number;
  canAnswer: boolean;
}

export interface FindCourseSurveyAnswerResponse {
  id: number;
  createdAt: string;
  userId: number;
  userName: string;
  questionId?: number | null;
  content?: string | null;
  answeredAt?: string | null;
}

export interface GetCourseProgressRateResponse {
  items: CourseProgressRateDto[];
}

export interface CourseProgressRateDto {
  courseId: number;
  progressRateHistogram: ProgressRateHistogramItem[];
}

export interface ProgressRateHistogramItem {
  percent: number;
  count: number;
}

export interface GetCourseCrossStudentResponse {
  items: CourseCrossStudentItem[];
}

export interface CourseCrossStudentItem {
  instructorId: number;
  title: string;
  students: number;
}

export interface GetCourseCompleteRateResponse {
  items: CourseCompleteRateItem[];
}

export interface CourseCompleteRateItem {
  id: number;
  title: string;
  percent: number;
}

export interface CourseSkillAggregateResult {
  count: number;
  completedAt?: string | null;
  certificatedAt?: string | null;
  startedAt: string;
  createdAt?: string | null;
  isCompleted: boolean;
}

export interface GetCourseInfoAiGenerationRequestResponse {
  id: number;
  courseId: number;
  userId: number;
  content: string;
  language: 'KO' | 'EN' | 'JA' | 'VI';
  status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  requestedAt: string;
  completedAt?: string | null;
}

export interface GetLatestCourseInfoAiGenerationRequestContentResponse {
  content: string;
}

export interface GetCourseInfoSnapshotResponse {
  requestId: number;
  courseId: number;
  title: string;
  description?: string | null;
  based: string[];
  targets: string[];
  abilities: string[];
  skills: SkillDto[];
  levelId?: number | null;
  language: string;
}

export interface SkillDto {
  id: number;
  title: string;
}

export interface CourseGiftByAdminResponse {
  items: CourseGiftByAdminItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPage: number;
}

export interface CourseGiftByAdminItem {
  id: number;
  orderId: number;
  status: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
  message?: string | null;
  code: string;
  createdAt: string;
  canceledAt?: string | null;
  approvedAt?: string | null;
  senderUser: SenderUserInfo;
  receiverUser: ReceiverUserInfo;
  createdAtUTC: string;
  canceledAtUTC?: string | null;
  approvedAtUTC?: string | null;
}

export interface ReceiverUserInfo {
  id?: number | null;
  name: string;
}

export interface SenderUserInfo {
  id: number;
  name: string;
}

export interface CourseDiscountResponse {
  discountTitle?: string | null;
  coursePrice: CoursePriceResponse;
  usdCoursePrice: CoursePriceResponse;
  discountStartedAt?: string | null;
  discountEndedAt?: string | null;
  discountStartedAtUTC?: string | null;
  discountEndedAtUTC?: string | null;
}

export interface CoursePriceResponse {
  price: number;
  discountedPrice: number;
  regularPrice: number;
  sellingPrice: number;
  discountRate: number;
}

export interface GetCourseCurriculumAiGenerationRequestHistoryResponse {
  id: number;
  courseId: number;
  userId: number;
  snapshot: SnapshotView;
  mode: 'APPEND' | 'OVERWRITE';
  language: 'KO' | 'EN' | 'JA' | 'VI';
  status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  requestedAt: string;
  completedAt?: string | null;
  checkedAt?: string | null;
  sectionCount?: number | null;
  lectureCount?: number | null;
}

export interface SnapshotView {
  title: string;
  description?: string | null;
  based: string[];
  targets: string[];
  abilities: string[];
  categories: string[];
  skillTags: string[];
  level?: string | null;
}

export interface GetCourseCoverAiGenerationTemplateInputResponse {
  prompts: GetCourseCoverAiGenerationTemplateInputResponsePromptItem[];
  /**
   * Use referenceImageItems instead
   */
  referenceImages: string[];
  referenceImageItems: ReferenceImageItem[];
}

export interface GetCourseCoverAiGenerationTemplateInputResponsePromptItem {
  id: number;
  prompt: string;
  model: string;
}

export interface ReferenceImageItem {
  id: number;
  url: string;
}

export interface GetCourseContentTextBodyResponse {
  textBody: string;
}

export interface GetCourseContentAiGenerationInputsResponse {
  course: GetCourseContentAiGenerationInputsResponseCourseResponse;
  representativeInstructor: RepresentativeInstructorResponse;
  cover: CourseCoverResponse;
  based: string[];
  abilities: string[];
  targets: string[];
  description?: string | null;
}

export interface CourseCoverResponse {
  vodId?: string | null;
}

export interface GetCourseContentAiGenerationInputsResponseCourseResponse {
  id: number;
  title: string;
  skillTags: string[];
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
}

export interface RepresentativeInstructorResponse {
  name: string;
  introduction?: string | null;
}

export interface GetGroupUserCertificateDetailResponse {
  userId: number;
  courseId: number;
  voucherId: number;
  userRealName?: string | null;
  groupUserName?: string | null;
  courseTitle: string;
  courseRuntime: number;
  instructorName: string;
  startedAtUTC: string;
  certificatedAtUTC: string;
}

export interface GetCertificateSummaryByUserResponse {
  items: CertificateSummaryDto[];
}

export interface CertificateSummaryDto {
  voucherId: number;
  courseId: number;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  certificatedAt?: string | null;
  title: string;
  startedAtUTC?: string | null;
  completedAtUTC?: string | null;
  createdAtUTC: string;
  certificatedAtUTC?: string | null;
}

export interface GetCourseAiContentCheckResponse {
  isAiContent: boolean;
}

export interface FindCoteVoucherResponse {
  id?: number | null;
}

export interface SearchChallengeResponse {
  items: SearchedChallenge[];
  totalCount: number;
}

export interface SearchedChallenge {
  id: number;
  title: string;
  slug: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  price?: number | null;
  publishedAt?: string | null;
  contractedAt?: string | null;
  instructor: SearchChallengeInstructorInfo;
  categories: ChallengeCategory[];
  originalCourse?: OriginalCourse;
  isOnDiscount: boolean;
  publishedAtUTC?: string | null;
  contractedAtUTC?: string | null;
}

export interface ChallengeCategory {
  isMainCategory: boolean;
  name: string;
}

export interface OriginalCourse {
  id: number;
  name: string;
  slug: string;
}

export interface SearchChallengeInstructorInfo {
  userId: number;
  name: string;
}

export interface AdminFeaturedChallengeResponse {
  items: AdminFeaturedChallengeItemResponse[];
}

export interface AdminFeaturedChallengeItemResponse {
  id: number;
  thumbnailUrl?: string | null;
  title: string;
  description?: string | null;
  instructor: AdminFeaturedChallengeInstructorInfo;
  representativeCategory: AdminFeaturedChallengeItemResponseCategoryInfo;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  exposureStartedAtUTC?: string | null;
  exposureEndedAtUTC?: string | null;
  isExposed: boolean;
  price: number;
  regularPrice: number;
}

export interface AdminFeaturedChallengeInstructorInfo {
  id: number;
  slug?: string | null;
  name: string;
}

export interface AdminFeaturedChallengeItemResponseCategoryInfo {
  id: number;
  slug: string;
  title: string;
}

export interface FindCartResponse {
  items: CartDto[];
}

export interface CartDto {
  id: number;
  createdAt: string;
  updatedAt: string;
  type: string;
  status?: string | null;
  quantity: number;
  title?: string | null;
  price?: number | null;
  payPrice?: number | null;
  deletedAt?: string | null;
  paidAt?: string | null;
  regPrice?: number | null;
  revenue?: number | null;
  byInstAt?: string | null;
  courseDc: boolean;
  courseDcTitle?: string | null;
  userId: number;
  courseId?: number | null;
  orderId?: number | null;
  instUserId?: number | null;
  course?: object;
  legacy?: object;
  couponUserId?: number | null;
  revenueInfo?: object;
  couponId?: number | null;
  discountId?: number | null;
  userDcId?: number | null;
  createdAtUTC: string;
  updatedAtUTC: string;
  deletedAtUTC?: string | null;
  paidAtUTC?: string | null;
  byInstAtUTC?: string | null;
}

export interface RemoveCourseRequest {
  listId: number;
  courseId: number;
}

export interface RemoveCartsRequest {
  courseIds: number[];
}

/** CourseType */
export const enum CourseType {
  ON = 'ON',
  OFF = 'OFF',
  CHALLENGE = 'CHALLENGE',
}

/** MyVoucherStudyingType */
export const enum MyVoucherStudyingType {
  STUDYING = 'STUDYING',
  COMPLETED = 'COMPLETED',
}

export const enum MyVouchersSortType {
  COURSE_TITLE = 'COURSE_TITLE',
  RECENT_STUDYING = 'RECENT_STUDYING',
  RECENT_APPLY = 'RECENT_APPLY',
  EARLIEST_EXPIRATION = 'EARLIEST_EXPIRATION',
}

export const enum Schema {
  COURSE_TITLE = 'COURSE_TITLE',
  RECENT_STUDYING = 'RECENT_STUDYING',
  RECENT_APPLY = 'RECENT_APPLY',
  EARLIEST_EXPIRATION = 'EARLIEST_EXPIRATION',
}

/** Language */
export const enum Language {
  KO = 'KO',
  EN = 'EN',
  JA = 'JA',
  VI = 'VI',
}

/** MyVoucherChallengeStudyingType */
export const enum MyVoucherChallengeStudyingType {
  STUDYING = 'STUDYING',
}

/** MyChallengeVouchersOffsetSortType */
export const enum MyChallengeVouchersOffsetSortType {
  COURSE_TITLE = 'COURSE_TITLE',
  RECENT_STUDYING = 'RECENT_STUDYING',
  RECENT_APPLY = 'RECENT_APPLY',
}

/** SearchPartnerCourseSort */
export const enum SearchPartnerCourseSort {
  POPULAR = 'POPULAR',
  RECENT = 'RECENT',
  PRICE = 'PRICE',
  REVENUE = 'REVENUE',
  MARGIN = 'MARGIN',
  REVIEW = 'REVIEW',
  STUDENT = 'STUDENT',
  RATING = 'RATING',
}

/** GetUserProfileCourseSort */
export const enum GetUserProfileCourseSort {
  RECENT = 'RECENT',
  POPULAR = 'POPULAR',
  RECOMMEND = 'RECOMMEND',
}

/** LikedCourseSort */
export const enum LikedCourseSort {
  RECENT = 'RECENT',
  TITLE = 'TITLE',
  STUDENT = 'STUDENT',
  RATING = 'RATING',
}

/** LikedCourseChargeType */
export const enum LikedCourseChargeType {
  FREE = 'FREE',
  PAID = 'PAID',
  ALL = 'ALL',
}

export const enum CourseCertificateSortType {
  CERTIFICATED = 'CERTIFICATED',
  CONFIRMED = 'CONFIRMED',
}

/** RoadmapItemType */
export const enum RoadmapItemType {
  COURSE = 'COURSE',
  BOOK = 'BOOK',
  LINK = 'LINK',
}

/** MissionAttachmentRequestType */
export const enum MissionAttachmentRequestType {
  LINK = 'LINK',
  IMAGE = 'IMAGE',
  FILE = 'FILE',
}

/** BankCode */
export const enum BankCode {
  NH = 'NH',
  KB = 'KB',
  WOORI = 'WOORI',
  SHINHAN = 'SHINHAN',
  IBK = 'IBK',
  HANA = 'HANA',
  GYEONGNAM = 'GYEONGNAM',
  DAEGU = 'DAEGU',
  BUSAN = 'BUSAN',
  SUHYUP = 'SUHYUP',
  POST = 'POST',
  KDB = 'KDB',
  SC = 'SC',
  CITI = 'CITI',
  DGB = 'DGB',
  GWANGJU = 'GWANGJU',
  JEJU = 'JEJU',
  JEONBUK = 'JEONBUK',
  SAE_MAUL = 'SAE_MAUL',
  SHINHYEOP = 'SHINHYEOP',
  K_BANK = 'K_BANK',
  KAKAO_BANK = 'KAKAO_BANK',
}

export const enum Type {
  MAIN = 'MAIN',
  SUB = 'SUB',
}

/** SurveyQuestionType */
export const enum SurveyQuestionType {
  SUBJECTIVE = 'SUBJECTIVE',
  OBJECTIVE = 'OBJECTIVE',
  MULTIPLE_OBJECTIVE = 'MULTIPLE_OBJECTIVE',
  FILE = 'FILE',
}

export const enum StatusCode {
  OK = 'OK',
  CREATED = 'CREATED',
  BAD_REQUEST = 'BAD_REQUEST',
  NOT_FOUND = 'NOT_FOUND',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  FORBIDDEN = 'FORBIDDEN',
  CONFLICT = 'CONFLICT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

export const enum DiscountType {
  COURSE = 'COURSE',
  USER = 'USER',
  PLATFORM = 'PLATFORM',
  NONE = 'NONE',
  COUPON = 'COUPON',
}

/** DomesticPgType */
export const enum DomesticPgType {
  NAVERPAY = 'NAVERPAY',
  TOSS = 'TOSS',
  TOSS_PAY = 'TOSS_PAY',
}

/** OrderPaymentMethodType */
export const enum OrderPaymentMethodType {
  CARD = 'CARD',
  NAVERPAY = 'NAVERPAY',
  NAVERPAY_MONEY = 'NAVERPAY_MONEY',
  KAKAOPAY = 'KAKAOPAY',
  PAYCO = 'PAYCO',
  TOSSPAY = 'TOSSPAY',
  TOSS_PAY_MONEY = 'TOSS_PAY_MONEY',
  APPLEPAY = 'APPLEPAY',
  VIRTUAL_ACCOUNT = 'VIRTUAL_ACCOUNT',
  LIFE_EDUCATION = 'LIFE_EDUCATION',
  OVERSEAS = 'OVERSEAS',
  INFPAY = 'INFPAY',
  BANK_TRANSFER = 'BANK_TRANSFER',
  LINKPAY = 'LINKPAY',
  UNKNOWN = 'UNKNOWN',
  NONE = 'NONE',
}

/** GlobalPgType */
export const enum GlobalPgType {
  PORTONE = 'PORTONE',
  TOSS = 'TOSS',
}

/** GlobalPayMethod */
export const enum GlobalPayMethod {
  NONE = 'NONE',
  PAYPAL = 'PAYPAL',
}

/** CurrencyCode */
export const enum CurrencyCode {
  KRW = 'KRW',
  USD = 'USD',
  VND = 'VND',
  JPY = 'JPY',
}

export const enum Status {
  READY = 'READY',
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING_FOR_DEPOSIT = 'WAITING_FOR_DEPOSIT',
  DONE = 'DONE',
  CANCELED = 'CANCELED',
  PARTIAL_CANCELED = 'PARTIAL_CANCELED',
  ABORTED = 'ABORTED',
  EXPIRED = 'EXPIRED',
}

/** MarketingPartnerType */
export const enum MarketingPartnerType {
  BUSINESS = 'BUSINESS',
  PERSONAL = 'PERSONAL',
}

/** LiveSessionLectureRole */
export const enum LiveSessionLectureRole {
  ADMIN = 'ADMIN',
  INSTRUCTOR = 'INSTRUCTOR',
  STUDENT = 'STUDENT',
  STUDENT_VIEWER = 'STUDENT_VIEWER',
  RECORDER = 'RECORDER',
}

/** CourseGiftReceiveType */
export const enum CourseGiftReceiveType {
  PHONE = 'PHONE',
  LINK = 'LINK',
}

/** CreateCourseGiftPgType */
export const enum CreateCourseGiftPgType {
  TOSS = 'TOSS',
}

/** CreateCourseGiftPayMethod */
export const enum CreateCourseGiftPayMethod {
  CARD = 'CARD',
  NAVERPAY = 'NAVERPAY',
  KAKAOPAY = 'KAKAOPAY',
  TOSSPAY = 'TOSSPAY',
  INFPAY = 'INFPAY',
  BANK_TRANSFER = 'BANK_TRANSFER',
  APPLEPAY = 'APPLEPAY',
  PAYCO = 'PAYCO',
}

/** CourseContractHistoryType */
export const enum CourseContractHistoryType {
  PLATFORM = 'PLATFORM',
  INFLUENCER = 'INFLUENCER',
  EXCLUSIVE = 'EXCLUSIVE',
  NONEXCLUSIVE = 'NONEXCLUSIVE',
  CHALLENGE = 'CHALLENGE',
  OFFLINE = 'OFFLINE',
  E = 'E',
}

/** EarlyBirdSaleTerm */
export const enum EarlyBirdSaleTerm {
  AGREE = 'AGREE',
  DISAGREE = 'DISAGREE',
  DELEGATE = 'DELEGATE',
}

export const enum TargetType {
  ALL = 'ALL',
  SELECTED = 'SELECTED',
  CATEGORY = 'CATEGORY',
  SKILL = 'SKILL',
  ROADMAP = 'ROADMAP',
  COMMON_TAG = 'COMMON_TAG',
}

export const enum CancelType {
  B2B = 'B2B',
  B2C = 'B2C',
}

export const enum DiscountStrategy {
  PERCENT = 'PERCENT',
  PRICE = 'PRICE',
}

/** CourseStatus */
export const enum CourseStatus {
  DRAFT = 'DRAFT',
  PRIVATE = 'PRIVATE',
  SUBMIT = 'SUBMIT',
  PUBLISH = 'PUBLISH',
  PAUSE = 'PAUSE',
  PUBLISH_READY = 'PUBLISH_READY',
}

/** OrderStatus */
export const enum OrderStatus {
  CANCEL_DELETE = 'CANCEL_DELETE',
  MENTEE_CANCEL = 'MENTEE_CANCEL',
  PARTIAL_CANCEL = 'PARTIAL_CANCEL',
  CANCEL_NOT_ENOUGH_PROGRESS = 'CANCEL_NOT_ENOUGH_PROGRESS',
  GROUP_PAYMENT_CANCEL = 'GROUP_PAYMENT_CANCEL',
  PAYMENT_CANCEL = 'PAYMENT_CANCEL',
  ORDER_FORM = 'ORDER_FORM',
  CONFLICT_CANCEL = 'CONFLICT_CANCEL',
  AUTO_CANCEL = 'AUTO_CANCEL',
  PAYMENT_READY = 'PAYMENT_READY',
  TOTAL_CANCEL = 'TOTAL_CANCEL',
  PAYMENT_FAIL = 'PAYMENT_FAIL',
  PAYMENT_STOP = 'PAYMENT_STOP',
  CODE_ORDER = 'CODE_ORDER',
  ORDER_CANCEL = 'ORDER_CANCEL',
  PAYMENT_COMPLETE = 'PAYMENT_COMPLETE',
  PAYMENT_COMPLETE_PROCESSING = 'PAYMENT_COMPLETE_PROCESSING',
  ADMIN_CANCEL = 'ADMIN_CANCEL',
  MENTOR_CANCEL = 'MENTOR_CANCEL',
  DEPOSIT_WAIT = 'DEPOSIT_WAIT',
  WC_COMPLETED = 'WC_COMPLETED',
  MANUAL_DELETE = 'MANUAL_DELETE',
  REFUND_REQUEST = 'REFUND_REQUEST',
}

export const enum PaymentMethodType {
  CARD = 'CARD',
  NAVERPAY = 'NAVERPAY',
  NAVERPAY_MONEY = 'NAVERPAY_MONEY',
  KAKAOPAY = 'KAKAOPAY',
  TOSSPAY = 'TOSSPAY',
  TOSS_PAY_MONEY = 'TOSS_PAY_MONEY',
  APPLEPAY = 'APPLEPAY',
  VIRTUAL_ACCOUNT = 'VIRTUAL_ACCOUNT',
  LIFE_EDUCATION = 'LIFE_EDUCATION',
  OVERSEAS = 'OVERSEAS',
  INFPAY = 'INFPAY',
  BANK_TRANSFER = 'BANK_TRANSFER',
  PAYCO = 'PAYCO',
  UNKNOWN = 'UNKNOWN',
  NONE = 'NONE',
  LINKPAY = 'LINKPAY',
  LINKPAY_NAVERPAY = 'LINKPAY_NAVERPAY',
  LINKPAY_KAKAOPAY = 'LINKPAY_KAKAOPAY',
  LINKPAY_TOSSPAY = 'LINKPAY_TOSSPAY',
  LINKPAY_PAYCO = 'LINKPAY_PAYCO',
  LINKPAY_APPLEPAY = 'LINKPAY_APPLEPAY',
  LINKPAY_CARD = 'LINKPAY_CARD',
  LINKPAY_BANK_TRANSFER = 'LINKPAY_BANK_TRANSFER',
  FPX = 'FPX',
  SOFORT = 'SOFORT',
  PAYNOW = 'PAYNOW',
  GRABPAYPH = 'GRABPAYPH',
  GRABPAYMY = 'GRABPAYMY',
  BOOST = 'BOOST',
  TOUCHNGO = 'TOUCHNGO',
  KONBINI = 'KONBINI',
  PAYSHOP = 'PAYSHOP',
  BANCONTACT = 'BANCONTACT',
  EPS = 'EPS',
  MULTIBANCO = 'MULTIBANCO',
  PRZELEWY24 = 'PRZELEWY24',
  BLIK = 'BLIK',
  IDEAL = 'IDEAL',
  PLATFORM = 'PLATFORM',
  POLI = 'POLI',
  INSTANT_BANK_TRANSFER = 'INSTANT_BANK_TRANSFER',
  UPI = 'UPI',
}

export const enum PaymentChannel {
  TOSS = 'TOSS',
  PAYMENTWALL = 'PAYMENTWALL',
  TAZAPAY = 'TAZAPAY',
  PAYPAL = 'PAYPAL',
  PAYLETTER = 'PAYLETTER',
  IAMPORT = 'IAMPORT',
  NAVERPAY = 'NAVERPAY',
  NONE = 'NONE',
  TOSS_PAY = 'TOSS_PAY',
}

export const enum RefundStatus {
  IDLE = 'IDLE',
  PENDING = 'PENDING',
  COMPLETE = 'COMPLETE',
  REJECT = 'REJECT',
}

/** CartStatus */
export const enum CartStatus {
  AUTO_CANCEL = 'AUTO_CANCEL',
  REFUND = 'REFUND',
  CANCEL = 'CANCEL',
  PAID = 'PAID',
  ADMIN_CANCEL = 'ADMIN_CANCEL',
  CANCEL_MENTOR = 'CANCEL_MENTOR',
  WC_COMPLETED = 'WC_COMPLETED',
  WAIT = 'WAIT',
  MANUAL_DELETE = 'MANUAL_DELETE',
  CANCEL_DELETE = 'CANCEL_DELETE',
  CANCEL_PROGRESS = 'CANCEL_PROGRESS',
  CANCEL_MENTEE = 'CANCEL_MENTEE',
  GROUP_PAYMENT_CANCEL = 'GROUP_PAYMENT_CANCEL',
  ORDER = 'ORDER',
  ORDER_CANCEL = 'ORDER_CANCEL',
}

/** RoadmapStatus */
export const enum RoadmapStatus {
  DRAFT = 'DRAFT',
  SUBMIT = 'SUBMIT',
  PUBLISH = 'PUBLISH',
  PAUSE = 'PAUSE',
  PRIVATE = 'PRIVATE',
}

/** RoadmapHistoryType */
export const enum RoadmapHistoryType {
  RESTORE = 'RESTORE',
  PUBLISH = 'PUBLISH',
  DRAFT = 'DRAFT',
}

export const enum VodOriginalLanguage {
  KO = 'KO',
  EN = 'EN',
  JA = 'JA',
  VI = 'VI',
  CN = 'CN',
  TW = 'TW',
  HI = 'HI',
  FR = 'FR',
  CA = 'CA',
  ES = 'ES',
  MX = 'MX',
  PT = 'PT',
}

/** CourseSubType */
export const enum CourseSubType {
  ONLINE = 'ONLINE',
  CLASS = 'CLASS',
  CONFERENCE = 'CONFERENCE',
  CHALLENGE = 'CHALLENGE',
}

export const enum RejectCode {
  AWKWARD_MATCHING = 'AWKWARD_MATCHING',
  AI_CONTENT_CHECK_REQUIRED = 'AI_CONTENT_CHECK_REQUIRED',
}

export const enum ChallengeCourseType {
  NORMAL = 'NORMAL',
  LIGHTENING_LIVE = 'LIGHTENING_LIVE',
}

/** SubmissionStatusResponse */
export const enum SubmissionStatusResponse {
  NONE = 'NONE',
  DRAFT = 'DRAFT',
  REJECTED = 'REJECTED',
  SUBMITTED = 'SUBMITTED',
  ACCEPTED = 'ACCEPTED',
}

/** LiveSessionAttendanceStatus */
export const enum LiveSessionAttendanceStatus {
  NONE = 'NONE',
  ATTENDED = 'ATTENDED',
  ABSENT = 'ABSENT',
}

export const enum RecordingOption {
  PROVIDED = 'PROVIDED',
  NOT_PROVIDED = 'NOT_PROVIDED',
}

/** CourseContentType */
export const enum CourseContentType {
  EDITOR = 'EDITOR',
  BUILDER = 'BUILDER',
}

/** CoursePaymentInfoType */
export const enum CoursePaymentInfoType {
  ENROLLED = 'ENROLLED',
  PURCHASE_UNAVAILABLE = 'PURCHASE_UNAVAILABLE',
  B2B_SUBSCRIPTION = 'B2B_SUBSCRIPTION',
  B2B_ENROLMENT_PENDING = 'B2B_ENROLMENT_PENDING',
  B2B_PURCHASE = 'B2B_PURCHASE',
  B2B_PURCHASE_RESTRICTED = 'B2B_PURCHASE_RESTRICTED',
  FREE = 'FREE',
  DISCOUNTED_PURCHASE = 'DISCOUNTED_PURCHASE',
  REGULAR_PRICE_PURCHASE = 'REGULAR_PRICE_PURCHASE',
}

export const enum ChallengeStatus {
  BEFORE_APPLY = 'BEFORE_APPLY',
  APPLYING = 'APPLYING',
  AFTER_APPLY = 'AFTER_APPLY',
  PROCESSING = 'PROCESSING',
  PARTICIPATING = 'PARTICIPATING',
  COMPLETED = 'COMPLETED',
}

export const enum Tag {
  IS_ACTIVE_IN_QUESTION = 'IS_ACTIVE_IN_QUESTION',
  IS_B2B_SUBSCRIPTION = 'IS_B2B_SUBSCRIPTION',
  HAS_MISSION = 'HAS_MISSION',
  IS_CHALLENGE_APPLYING = 'IS_CHALLENGE_APPLYING',
  IS_CHALLENGE_BEFORE_APPLY = 'IS_CHALLENGE_BEFORE_APPLY',
  IS_CHALLENGE_AFTER_APPLY = 'IS_CHALLENGE_AFTER_APPLY',
  IS_CHALLENGE_PROCESSING = 'IS_CHALLENGE_PROCESSING',
  IS_CHALLENGE_COMPLETED = 'IS_CHALLENGE_COMPLETED',
  IS_CHALLENGE_PARTICIPATING = 'IS_CHALLENGE_PARTICIPATING',
}

/** OfflineCourseType */
export const enum OfflineCourseType {
  CONFERENCE = 'CONFERENCE',
  CLASS = 'CLASS',
}

export const enum UnitType {
  SECTION = 'SECTION',
  LECTURE = 'LECTURE',
  MISSION = 'MISSION',
  DOCUMENT = 'DOCUMENT',
  QUIZ = 'QUIZ',
  LIVE_SESSION = 'LIVE_SESSION',
  LABLUP = 'LABLUP',
}

export const enum SubmissionStatus {
  DRAFT = 'DRAFT',
  REJECTED = 'REJECTED',
  SUBMITTED = 'SUBMITTED',
  ACCEPTED = 'ACCEPTED',
}

/** UnitContentSummaryType */
export const enum UnitContentSummaryType {
  VOD_SUBTITLE = 'VOD_SUBTITLE',
  UNIT_BODY = 'UNIT_BODY',
}

/** QuizQuestionReactionType */
export const enum QuizQuestionReactionType {
  LIKE = 'LIKE',
  DISLIKE = 'DISLIKE',
}

/** RoadmapContentTemplateAuthorType */
export const enum RoadmapContentTemplateAuthorType {
  INFLAB = 'INFLAB',
  USER = 'USER',
}

export const enum Rank {
  MASTER = 'MASTER',
  SCHOLAR = 'SCHOLAR',
  SKILL_BUILDER = 'SKILL_BUILDER',
  LEARNER = 'LEARNER',
  STARTER = 'STARTER',
}

export const enum MaxStudyingTimeDayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

export const enum MaxStudyingTimeTimeOfDay {
  EARLY_MORNING = 'EARLY_MORNING',
  MORNING = 'MORNING',
  AFTERNOON = 'AFTERNOON',
  EVENING = 'EVENING',
  NIGHT = 'NIGHT',
  DAWN = 'DAWN',
}

export const enum QuizUserStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  RETRYING = 'RETRYING',
  COMPLETED = 'COMPLETED',
}

export const enum QuizQuestionUserStatus {
  PASS = 'PASS',
  FAIL = 'FAIL',
  NOT_ATTEMPTED = 'NOT_ATTEMPTED',
}

/** QuizUserStatusResponse */
export const enum QuizUserStatusResponse {
  NOT_STARTED = 'NOT_STARTED',
  NO_PERMISSION = 'NO_PERMISSION',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

/** PromotionCardContentType */
export const enum PromotionCardContentType {
  COURSE = 'COURSE',
  URL = 'URL',
}

export const enum OrderMode {
  NORMAL = 'NORMAL',
  B2B_CODE = 'B2B_CODE',
  B2C_CODE = 'B2C_CODE',
  CODE = 'CODE',
  MENTOR = 'MENTOR',
  MENTORING = 'MENTORING',
  COURSE_GIFT = 'COURSE_GIFT',
  CLIP = 'CLIP',
  COURSE_PACKAGE = 'COURSE_PACKAGE',
}

/** LiveSessionType */
export const enum LiveSessionType {
  STANDARD = 'STANDARD',
  LARGE_SCALE = 'LARGE_SCALE',
}

/** SelectableLecturerRegisterChannelType */
export const enum SelectableLecturerRegisterChannelType {
  INTERNET_SEARCH = 'INTERNET_SEARCH',
  SNS = 'SNS',
  LINKEDIN_MESSAGE = 'LINKEDIN_MESSAGE',
  ONLINE_ADS = 'ONLINE_ADS',
  REFERRAL = 'REFERRAL',
  ETC = 'ETC',
}

/** CourseContractStatus */
export const enum CourseContractStatus {
  DRAFT = 'DRAFT',
  NOT_SIGNED = 'NOT_SIGNED',
  CANCELED = 'CANCELED',
  SIGNED = 'SIGNED',
  TERMINATED = 'TERMINATED',
  NO_REQUIRED = 'NO_REQUIRED',
}

export const enum DiscountStatus {
  CAN_DISCOUNT = 'CAN_DISCOUNT',
  ON_DISCOUNT = 'ON_DISCOUNT',
  COOL_TIME = 'COOL_TIME',
  UNAVAILABLE = 'UNAVAILABLE',
}

/** InstitutionCurationType */
export const enum InstitutionCurationType {
  VERIFIED = 'VERIFIED',
  INTERESTED_MATCHING = 'INTERESTED_MATCHING',
  INTERESTED = 'INTERESTED',
}

/** MissionAttachmentType */
export const enum MissionAttachmentType {
  NONE = 'NONE',
  LINK = 'LINK',
  IMAGE_FILE = 'IMAGE_FILE',
  ATTACHMENT_FILE = 'ATTACHMENT_FILE',
}

export const enum LocationType {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
}

/** CourseGiftStatus */
export const enum CourseGiftStatus {
  READY_TO_RECEIVE = 'READY_TO_RECEIVE',
  RECEIVED = 'RECEIVED',
  EXPIRED = 'EXPIRED',
  CANCELED = 'CANCELED',
}

export const enum PayMethod {
  CARD = 'CARD',
  NAVERPAY = 'NAVERPAY',
  NAVERPAY_MONEY = 'NAVERPAY_MONEY',
  KAKAOPAY = 'KAKAOPAY',
  TOSSPAY = 'TOSSPAY',
  TOSS_PAY_MONEY = 'TOSS_PAY_MONEY',
  SAMSUNGPAY = 'SAMSUNGPAY',
  APPLEPAY = 'APPLEPAY',
  PAYCO = 'PAYCO',
  VIRTUAL_ACCOUNT = 'VIRTUAL_ACCOUNT',
  BANK_TRANSFER = 'BANK_TRANSFER',
  LIFE_EDUCATION = 'LIFE_EDUCATION',
  OVERSEAS = 'OVERSEAS',
  INFPAY = 'INFPAY',
  VBANK = 'VBANK',
  CARD_LEGACY = 'CARD_LEGACY',
  NAVERPAY_LEGACY = 'NAVERPAY_LEGACY',
  POINT = 'POINT',
  PHONE = 'PHONE',
  EMPTY = 'EMPTY',
  NONE = 'NONE',
  FPX = 'FPX',
  SOFORT = 'SOFORT',
  PAYNOW = 'PAYNOW',
  GRABPAYPH = 'GRABPAYPH',
  GRABPAYMY = 'GRABPAYMY',
  BOOST = 'BOOST',
  TOUCHNGO = 'TOUCHNGO',
  KONBINI = 'KONBINI',
  PAYSHOP = 'PAYSHOP',
  BANCONTACT = 'BANCONTACT',
  EPS = 'EPS',
  MULTIBANCO = 'MULTIBANCO',
  PRZELEWY24 = 'PRZELEWY24',
  BLIK = 'BLIK',
  IDEAL = 'IDEAL',
  PLATFORM = 'PLATFORM',
  POLI = 'POLI',
  INSTANT_BANK_TRANSFER = 'INSTANT_BANK_TRANSFER',
  UPI = 'UPI',
}

export const enum Pg {
  KAKAOPAY = 'KAKAOPAY',
  NAVERPAY = 'NAVERPAY',
  TOSS_WIDGET = 'TOSS_WIDGET',
  TOSS = 'TOSS',
  TOSS_V2 = 'TOSS_V2',
  TOSS_GLOBAL = 'TOSS_GLOBAL',
  TOSS_PAY_LEGACY = 'TOSS_PAY_LEGACY',
  TOSS_PAY = 'TOSS_PAY',
  IMP_KAKAOPAY = 'IMP_KAKAOPAY',
  IMP_NAVERPAY = 'IMP_NAVERPAY',
  IMP_NICE = 'IMP_NICE',
  IMP_JTNET = 'IMP_JTNET',
  PORTONE = 'PORTONE',
}

export const enum Mode {
  APPEND = 'APPEND',
  OVERWRITE = 'OVERWRITE',
}

export const enum RequiredImageType {
  NONE = 'NONE',
  PERSON = 'PERSON',
}

/** SettlementSubjectType */
export const enum SettlementSubjectType {
  WITHHOLDING_TAX = 'WITHHOLDING_TAX',
  NON_WITHHOLDING_TAX = 'NON_WITHHOLDING_TAX',
  BUSINESS = 'BUSINESS',
}

export const enum TermType {
  NEW_CONTRACT = 'NEW_CONTRACT',
  MODIFICATION_CONTRACT = 'MODIFICATION_CONTRACT',
}

/** CourseContentTemplateAuthorType */
export const enum CourseContentTemplateAuthorType {
  INFLAB = 'INFLAB',
  INSTRUCTOR = 'INSTRUCTOR',
}

export const enum RequiredInfoType {
  NONE = 'NONE',
  INSTRUCTOR_PROFILE = 'INSTRUCTOR_PROFILE',
}

export const enum TemplateKey {
  PROBLEM = 'PROBLEM',
  RESULT = 'RESULT',
  STORY = 'STORY',
}

/** ChallengeNotificationSubscriptionType */
export const enum ChallengeNotificationSubscriptionType {
  CHALLENGE_APPLY_STARTED = 'CHALLENGE_APPLY_STARTED',
  NEW_CHALLENGE_OPENED = 'NEW_CHALLENGE_OPENED',
}

/** PromotionCardActiveStatus */
export const enum PromotionCardActiveStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const enum AttachmentType {
  LINK = 'LINK',
  IMAGE = 'IMAGE',
  FILE = 'FILE',
  NONE = 'NONE',
}

export const enum PaymentMethod {
  CARD = 'CARD',
  NAVERPAY = 'NAVERPAY',
  NAVERPAY_MONEY = 'NAVERPAY_MONEY',
  KAKAOPAY = 'KAKAOPAY',
  TOSSPAY = 'TOSSPAY',
  TOSS_PAY_MONEY = 'TOSS_PAY_MONEY',
  SAMSUNGPAY = 'SAMSUNGPAY',
  APPLEPAY = 'APPLEPAY',
  PAYCO = 'PAYCO',
  VIRTUAL_ACCOUNT = 'VIRTUAL_ACCOUNT',
  BANK_TRANSFER = 'BANK_TRANSFER',
  LIFE_EDUCATION = 'LIFE_EDUCATION',
  OVERSEAS = 'OVERSEAS',
  INFPAY = 'INFPAY',
  VBANK = 'VBANK',
  CARD_LEGACY = 'CARD_LEGACY',
  NAVERPAY_LEGACY = 'NAVERPAY_LEGACY',
  POINT = 'POINT',
  PHONE = 'PHONE',
  EMPTY = 'EMPTY',
  NONE = 'NONE',
  FPX = 'FPX',
  SOFORT = 'SOFORT',
  PAYNOW = 'PAYNOW',
  GRABPAYPH = 'GRABPAYPH',
  GRABPAYMY = 'GRABPAYMY',
  BOOST = 'BOOST',
  TOUCHNGO = 'TOUCHNGO',
  KONBINI = 'KONBINI',
  PAYSHOP = 'PAYSHOP',
  BANCONTACT = 'BANCONTACT',
  EPS = 'EPS',
  MULTIBANCO = 'MULTIBANCO',
  PRZELEWY24 = 'PRZELEWY24',
  BLIK = 'BLIK',
  IDEAL = 'IDEAL',
  PLATFORM = 'PLATFORM',
  POLI = 'POLI',
  INSTANT_BANK_TRANSFER = 'INSTANT_BANK_TRANSFER',
  UPI = 'UPI',
}

/** MarketingPartnerStatus */
export const enum MarketingPartnerStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
}

export const enum InstructorType {
  MAIN = 'MAIN',
  SUB = 'SUB',
}
